import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Briefcase,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CheckCircle2,
  Circle,
  Clock,
  Database,
  ExternalLink,
  FileCode2,
  FileSearch,
  FileText,
  GitBranch,
  Globe,
  KeyRound,
  Layers,
  LayoutDashboard,
  Link2,
  ListChecks,
  LoaderCircle,
  LogIn,
  MonitorPlay,
  MousePointerClick,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Route,
  Save,
  Search,
  Settings2,
  Share2,
  ShieldCheck,
  SkipForward,
  Sparkles,
  Trash2,
  Archive,
  ArchiveRestore,
  Upload,
  UserMinus,
  Users,
  X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import {
  consumeWorkspaceBackRoute,
  wasCurrentDocumentReloaded,
} from '../../utils/workspaceNavigationHistory';
import { WorkspaceMembersModal } from '../modals/WorkspaceMembersModal';
import { AutomationEnvironmentModal } from '../modals/AutomationEnvironmentModal';
import { EditTestCasesStep } from '../pdf/EditTestCasesStep';
import { ExecutionSummaryStep } from '../pdf/ExecutionSummaryStep';
import { GeneratePOMStep } from '../pdf/GeneratePOMStep';
import { GenerateScriptsStep } from '../pdf/GenerateScriptsStep';
import { SaveTestsStep } from '../pdf/SaveTestsStep';
import { buildDefaultTestCaseFieldConfig } from '../pdf/TestCasePlanningStep';
import { HistoryDashboardShell } from '../shared/HistoryDashboardShell';
import {
  WorkspaceBackButton,
  WorkspaceChecklist,
  WorkspacePage,
  WorkspaceRouteSkeleton,
  WorkspaceSidebar,
  WorkspaceStageCard,
} from '../shared/WorkspaceChrome';
import { WORKSPACE_VARIANTS } from '../../config/workspaceVariants';
import {
  capturedFlowTabForRestore,
  CONSUMABLE_STEP_ACTIONS,
  formatCountLabel,
  journeyVariableNamesById,
  normalizeAction,
  stepSelectionId,
  stepValueBinding,
} from './capturedFlowHelpers';
import {
  buildPageGroups,
  CaptureTabsPanel,
  CapturedFlowSummary,
  CapturedFlowTabs,
  CapturedStepsPanel,
  JourneyVariablesPanel,
  StepDetailDrawer,
} from './CapturedFlowPanels';
import FlowModuleComposer from './FlowModuleComposer';

const BRIDGE_SOURCE = 'testsuite-ai-app';
const EXTENSION_SOURCE = 'testsuite-ai-extension';
const MAX_CONTEXT_FILE_BYTES = 5 * 1024 * 1024;
const CONTEXT_FILE_ACCEPT = '.pdf,.doc,.docx,.txt,.md';
const CAPTURE_WORKSPACE_VARIANT = WORKSPACE_VARIANTS['feature-capture'];
const CAPTURE_DASHBOARD_CHECKLISTS = {
  applications: {
    title: 'Application Workflow',
    items: [
      {
        title: 'Choose the product',
        description: 'Select an existing application or create the product area you want to test.',
      },
      {
        title: 'Capture a real journey',
        description: 'Record the browser flow once so actions, fields, and locators stay grounded in reality.',
      },
      {
        title: 'Continue to delivery',
        description: 'Turn the saved flow into coverage, page objects, scripts, and execution artifacts.',
      },
    ],
  },
  application: {
    title: 'Feature Workflow',
    items: [
      {
        title: 'Open the right feature',
        description: 'Choose a captured feature flow from the active application.',
      },
      {
        title: 'Review the recording',
        description: 'Check steps and locators before generating coverage.',
      },
      {
        title: 'Build reusable outputs',
        description: 'Move through cases, POM, scripts, and execution in one connected flow.',
      },
    ],
  },
};

const workflowStages = [
  {
    id: 'capture',
    title: 'Capture',
    copy: 'Record the real browser journey and collect locators.',
    checklistTitle: 'Capture Checklist',
    checklist: [
      { title: 'Target ready', copy: 'Feature name and URL are available.' },
      { title: 'Journey recorded', copy: 'The flow is performed once in Chrome.' },
      { title: 'Steps captured', copy: 'Actions and locators are ready to review.' },
    ],
  },
  {
    id: 'captured-flow',
    title: 'Captured Flow',
    copy: 'Review the recorded browser journey and locator quality.',
    checklistTitle: 'Flow Checklist',
    checklist: [
      { title: 'Steps available', copy: 'The captured journey is loaded.' },
      { title: 'Locators visible', copy: 'Each action shows the locator used for automation.' },
      { title: 'Ready to continue', copy: 'Move to cases, POM, or scripts when needed.' },
    ],
  },
  {
    id: 'cases',
    title: 'Test Cases',
    copy: 'Generate positive, negative, validation, and edge coverage.',
    checklistTitle: 'Test Case Checklist',
    checklist: [
      { title: 'Happy path', copy: 'The captured journey becomes the baseline case.' },
      { title: 'Negative paths', copy: 'Required and invalid input coverage is added.' },
      { title: 'Review ready', copy: 'Cases can be checked before automation.' },
    ],
  },
  {
    id: 'save',
    title: 'Save',
    copy: 'Save reviewed cases and create page objects from the captured flow.',
    checklistTitle: 'Save Checklist',
    checklist: [
      { title: 'Cases reviewed', copy: 'Positive and negative coverage is ready.' },
      { title: 'Artifact saved', copy: 'The reviewed set is stored with history.' },
      { title: 'Automation gate clear', copy: 'Saved cases can move into POM and script generation.' },
    ],
  },
  {
    id: 'pom',
    title: 'POM',
    copy: 'Review auto-generated page objects from captured pages and locators.',
    checklistTitle: 'POM Checklist',
    checklist: [
      { title: 'Pages detected', copy: 'Each interacted page gets a page object.' },
      { title: 'Locators mapped', copy: 'Captured fields become editable POM locators.' },
      { title: 'POMs selected', copy: 'Generated POMs are attached to test cases.' },
    ],
  },
  {
    id: 'scripts',
    title: 'Scripts',
    copy: 'Generate Playwright scripts from the cases and page object.',
    checklistTitle: 'Script Checklist',
    checklist: [
      { title: 'Cases selected', copy: 'Generated cases drive the script flow.' },
      { title: 'POM attached', copy: 'Script generation can reuse page object locators.' },
      { title: 'Automation ready', copy: 'Spec, POM, and data files are prepared.' },
    ],
  },
  {
    id: 'execute',
    title: 'Execute',
    copy: 'Prepare the generated script package for the runner.',
    checklistTitle: 'Execution Checklist',
    checklist: [
      { title: 'Script available', copy: 'A Playwright spec has been generated.' },
      { title: 'Run package ready', copy: 'POM and test data are attached.' },
      { title: 'Execution tracked', copy: 'The run package is saved with the flow.' },
    ],
  },
];

const CAPTURE_RECORDING_MODES = [
  {
    id: 'full',
    label: 'Full journey',
    icon: Route,
    copy: 'Use this when the entire feature should be recorded from the configured start page.',
  },
  {
    id: 'continuation',
    label: 'Next part only',
    icon: SkipForward,
    copy: 'Use this after a reusable login or navigation flow. Record only what follows the branch point.',
  },
  {
    id: 'shared-login',
    label: 'Shared login & navigation',
    icon: LogIn,
    copy: 'Use this to record sign-in and common navigation once, then reuse it in other flows.',
  },
];

const NON_ACTION_CAPTURE_STEPS = new Set(['navigate', 'tab_switch']);

const FEATURE_CAPTURE_ROUTE_ROOT = '/feature-capture';
const FEATURE_CAPTURE_LAST_ROUTE_KEY = 'testsuite_feature_capture_last_route';
const FEATURE_CAPTURE_STAGE_IDS = new Set(workflowStages.map((stage) => stage.id));
const FEATURE_CAPTURE_LEGACY_STAGE_ALIASES = {
  analyze: 'captured-flow',
};
const FEATURE_CAPTURE_BACK_STAGE_BY_CURRENT = {
  cases: 'captured-flow',
  save: 'cases',
  pom: 'save',
  scripts: 'pom',
  execute: 'scripts',
};

const getFeatureSessionStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const decodeRoutePart = (value = '') => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch {
    return String(value || '');
  }
};

const normalizeFeatureCaptureStageId = (value, fallback = 'capture') => {
  const normalized = String(value || '').trim().toLowerCase();
  const currentStageId = FEATURE_CAPTURE_LEGACY_STAGE_ALIASES[normalized] || normalized;
  return FEATURE_CAPTURE_STAGE_IDS.has(currentStageId) ? currentStageId : fallback;
};

const parseFeatureCaptureRoute = (pathname = '') => {
  const parts = String(pathname || '').split('/').filter(Boolean).map(decodeRoutePart);
  if (parts[0] === 'applications' && parts[1] && parts[2] === 'features') {
    const rootApplicationId = String(parts[1] || '').trim();
    const resource = String(parts[3] || '').trim();
    if (!resource || resource === 'new') {
      return {
        isFeatureCaptureRoute: true,
        isApplicationRoute: true,
        routeKind: resource === 'new' ? 'new' : 'dashboard',
        rootApplicationId,
        applicationId: '',
        sessionId: '',
        stageId: 'capture',
      };
    }
    return {
      isFeatureCaptureRoute: true,
      isApplicationRoute: true,
      routeKind: 'workflow',
      rootApplicationId,
      applicationId: '',
      sessionId: resource,
      stageId: normalizeFeatureCaptureStageId(parts[4], 'capture'),
    };
  }

  if (parts[0] !== 'feature-capture') {
    return {
      isFeatureCaptureRoute: false,
      isApplicationRoute: false,
      rootApplicationId: '',
      routeKind: '',
      applicationId: '',
      sessionId: '',
      stageId: '',
    };
  }

  if (parts[1] === 'applications' && parts[2]) {
    return {
      isFeatureCaptureRoute: true,
      isApplicationRoute: false,
      rootApplicationId: '',
      routeKind: 'application',
      applicationId: String(parts[2] || '').trim(),
      sessionId: '',
      stageId: '',
    };
  }

  if (parts[1] === 'flows' && parts[2]) {
    return {
      isFeatureCaptureRoute: true,
      isApplicationRoute: false,
      rootApplicationId: '',
      routeKind: 'workflow',
      applicationId: '',
      sessionId: String(parts[2] || '').trim(),
      stageId: normalizeFeatureCaptureStageId(parts[3], 'capture'),
    };
  }

  return {
    isFeatureCaptureRoute: true,
    isApplicationRoute: false,
    rootApplicationId: '',
    routeKind: 'dashboard',
    applicationId: '',
    sessionId: '',
    stageId: '',
  };
};

const buildFeatureCaptureRoute = ({
  applicationId = '',
  rootApplicationId = '',
  sessionId = '',
  stageId = '',
  isNew = false,
} = {}) => {
  const normalizedRootApplicationId = String(rootApplicationId || '').trim();
  const applicationRoutePrefix = normalizedRootApplicationId
    ? `/applications/${encodeURIComponent(normalizedRootApplicationId)}/features`
    : '';
  const normalizedSessionId = String(sessionId || '').trim();
  if (normalizedSessionId) {
    return applicationRoutePrefix
      ? `${applicationRoutePrefix}/${encodeURIComponent(normalizedSessionId)}/${normalizeFeatureCaptureStageId(stageId)}`
      : `${FEATURE_CAPTURE_ROUTE_ROOT}/flows/${encodeURIComponent(normalizedSessionId)}/${normalizeFeatureCaptureStageId(stageId)}`;
  }

  if (applicationRoutePrefix) {
    return isNew ? `${applicationRoutePrefix}/new` : applicationRoutePrefix;
  }

  const normalizedApplicationId = String(applicationId || '').trim();
  if (normalizedApplicationId) {
    return `${FEATURE_CAPTURE_ROUTE_ROOT}/applications/${encodeURIComponent(normalizedApplicationId)}`;
  }

  return FEATURE_CAPTURE_ROUTE_ROOT;
};

const formatWorkspaceRole = (workspace) => {
  return String(workspace?.current_user_role || 'member').trim().toLowerCase();
};

const compactValue = (value, fallback = 'Not selected') => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.length > 30 ? `${text.slice(0, 27)}...` : text;
};

const hasPermission = (permissions, permission) => {
  const target = String(permission || '').trim().toLowerCase();
  return Array.isArray(permissions)
    && permissions.some((item) => String(item || '').trim().toLowerCase() === target);
};

const chunkText = (chunk) => {
  if (!chunk || typeof chunk !== 'object') return '';
  return String(
    chunk.text
      || chunk.content
      || chunk.page_content
      || chunk.chunk_text
      || chunk.raw_text
      || ''
  ).trim();
};

const buildBrdSectionSourceText = (section) => {
  const chunks = Array.isArray(section?.source_text_chunks) ? section.source_text_chunks : [];
  const parts = chunks
    .map(chunkText)
    .filter(Boolean)
    .slice(0, 12);
  return parts.join('\n\n').slice(0, 12000);
};

const brdSectionLabel = (section) => {
  const number = section?.section_number || section?.number || '';
  const title = section?.title || section?.section_title || section?.section_name || section?.name || '';
  return [number, title].filter(Boolean).join(' - ') || 'Untitled section';
};

const storedBrdSectionsFromPayload = (payload) => {
  if (Array.isArray(payload?.sections)) return payload.sections;
  if (Array.isArray(payload?.sections?.sections)) return payload.sections.sections;
  return [];
};

const isMissingStoredBrdSectionsError = (error) => (
  /sections?\s+not\s+yet\s+extracted/i.test(String(error?.message || error || ''))
);

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    const [, base64 = ''] = result.split(',');
    resolve(base64);
  };
  reader.onerror = () => reject(new Error('Could not read the selected feature BRD.'));
  reader.readAsDataURL(file);
});

const statusLabel = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'created') return 'Ready';
  if (normalized === 'capturing') return 'Capturing';
  if (normalized === 'captured') return 'Captured';
  if (normalized === 'analyzed') return 'Analyzed';
  if (normalized === 'cases_generated') return 'Cases ready';
  if (normalized === 'test_cases_reviewed') return 'Cases reviewed';
  if (normalized === 'pom_generated') return 'POM ready';
  if (normalized === 'scripts_generated') return 'Scripts ready';
  if (normalized === 'execution_prepared') return 'Execution ready';
  return normalized ? normalizeAction(normalized.replace(/_/g, ' ')) : 'Ready';
};

const formatHistoryDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Unknown time';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const applicationNameFromUrl = (value, fallback = 'Captured Feature Area') => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  try {
    const url = new URL(text);
    return url.hostname.replace(/^www\./, '') || fallback;
  } catch {
    return fallback;
  }
};

const uniqueNameKey = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

const flowApplicationId = (item) => (
  String(item?.feature_area_id || item?.feature_area?.id || '').trim() || 'unassigned'
);

const flowApplicationName = (item) => (
  String(item?.feature_area_name || item?.feature_area?.name || '').trim()
  || applicationNameFromUrl(item?.feature_area_base_url || item?.start_url, 'Unassigned feature area')
);

const flowDisplayName = (item, allItems = []) => {
  const baseName = String(item?.feature_name || '').trim() || 'Untitled feature';
  const duplicates = (Array.isArray(allItems) ? allItems : []).filter((row) => (
    String(row?.feature_name || '').trim().toLowerCase() === baseName.toLowerCase()
  ));
  if (duplicates.length <= 1) return baseName;
  const timestamp = formatHistoryDate(item?.updated_at || item?.created_at);
  const shortId = String(item?.id || '').slice(-6);
  return `${baseName} · ${timestamp}${shortId ? ` · ${shortId}` : ''}`;
};

const applicationWorkspaceName = (application) => (
  String(
    application?.application_name
    || application?.application?.name
    || application?.workspace_name
    || application?.workspace?.name
    || application?.workspace?.workspace_name
    || ''
  ).trim()
);

const applicationBaseLabel = (application) => (
  String(application?.base_url || application?.feature_area_base_url || '').trim()
);

const applicationEnvironmentLabel = (application) => {
  const label = String(
    application?.environment_name
    || application?.feature_area_environment_name
    || ''
  ).trim();
  return label && label.toLowerCase() !== 'default' ? label : '';
};

const journeyVariablesFromSession = (session) => (
  Array.isArray(session?.journey_variables) ? session.journey_variables : []
);

const hasObjectContent = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0
);

const featureAutomationSourceId = (session) => {
  const sessionId = String(session?.id || '').trim();
  const explicit = String(session?.automation_source_id || '').trim();
  if (explicit && !explicit.startsWith('feature_capture_')) return explicit;
  return sessionId;
};

const UUID_TEXT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const applicationAutomationSourceId = (application) => {
  const explicit = String(application?.automation_source_id || '').trim();
  if (explicit) return explicit;
  const applicationId = String(application?.id || '').trim();
  return UUID_TEXT_PATTERN.test(applicationId) ? applicationId : '';
};

const hasCategorizedTestCases = (value) => (
  value
  && typeof value === 'object'
  && !Array.isArray(value)
  && (Array.isArray(value.positive) || Array.isArray(value.negative))
);

const countTestCases = (value) => {
  if (Array.isArray(value)) return value.length;
  if (hasCategorizedTestCases(value)) {
    return (Array.isArray(value.positive) ? value.positive.length : 0)
      + (Array.isArray(value.negative) ? value.negative.length : 0);
  }
  return 0;
};

const stageIdFromSession = (session) => {
  if (!session?.id) return 'capture';
  const status = String(session.status || '').toLowerCase();
  if (status === 'execution_prepared' || hasObjectContent(session.execution)) return 'execute';
  if (status === 'scripts_generated' || session.scripts?.spec) return 'scripts';
  if (status === 'pom_generated' || hasObjectContent(session.pom)) return 'pom';
  if (status === 'test_cases_reviewed') return 'save';
  if (status === 'cases_generated' || (Array.isArray(session.test_cases) && session.test_cases.length > 0)) return 'cases';
  if (status === 'analyzed' || hasObjectContent(session.analysis)) return 'cases';
  if (status === 'captured') return 'captured-flow';
  return 'capture';
};

const categorizeFeatureTestCases = (items = []) => {
  const positive = [];
  const negative = [];
  const rows = Array.isArray(items) ? items : [];
  rows.forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const category = String(item.type || item.category || item.test_type || '').toLowerCase();
    if (category.includes('negative')) {
      negative.push(item);
    } else {
      positive.push(item);
    }
  });
  return {
    positive,
    negative,
    positive_count: positive.length,
    negative_count: negative.length,
    total_count: positive.length + negative.length,
  };
};

const buildFeatureProcessingResult = ({ session, analysis, testCases }) => {
  const generationConfig = session?.approved_generation_config || {};
  const plan = analysis?.generation_plan || {};
  const sourceId = featureAutomationSourceId(session);
  return {
    id: session?.id,
    pdf_id: sourceId,
    brd_id: sourceId,
    automation_source_id: sourceId,
    source_type: 'feature_capture',
    start_url: session?.start_url || '',
    application_url: session?.start_url || '',
    application_id: session?.application_id || session?.application?.id || session?.workspace?.id || '',
    section_title: session?.feature_name || 'Captured Feature',
    section_number: 1,
    test_cases: categorizeFeatureTestCases(testCases),
    coverage_gaps: Array.isArray(analysis?.risks) ? analysis.risks : [],
    approved_focus: Array.isArray(generationConfig.additional_focus)
      ? generationConfig.additional_focus
      : Array.isArray(plan.coverage_focus)
        ? plan.coverage_focus
        : [],
    approved_global_constraints: Array.isArray(generationConfig.global_constraints)
      ? generationConfig.global_constraints
      : [],
    approved_counts: {
      positive_count: generationConfig.positive_count,
      negative_count: generationConfig.negative_count,
    },
    generated_at: session?.updated_at,
    generated_by_name: session?.owner_email || '',
  };
};

const isManualFeatureTestCase = (testCase = {}) => {
  const sourceType = String(testCase?.source_type || testCase?.source || '').trim().toLowerCase();
  return sourceType === 'manual_upload' || testCase?._source === 'manual';
};

const nonNegativeCount = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
};

const resolveFeatureLocatorReadiness = (session, testCases = []) => {
  const pom = session?.pom && typeof session.pom === 'object' ? session.pom : {};
  const raw = (
    session?.locator_readiness
    || pom.locator_readiness
    || null
  );
  const summary = raw?.summary && typeof raw.summary === 'object' ? raw.summary : {};
  const casesById = raw?.cases && typeof raw.cases === 'object' && !Array.isArray(raw.cases)
    ? raw.cases
    : {};
  const caseRows = Object.values(casesById).filter((item) => item && typeof item === 'object');
  const manualCases = (Array.isArray(testCases) ? testCases : []).filter(isManualFeatureTestCase);
  const manualCaseCount = nonNegativeCount(summary.manual_case_count, manualCases.length);
  const readyCaseCount = nonNegativeCount(
    summary.ready_case_count,
    caseRows.filter((item) => String(item.status || '').toLowerCase() === 'ready').length
  );
  const blockedCaseCount = nonNegativeCount(
    summary.blocked_case_count,
    Math.max(manualCaseCount - readyCaseCount, 0)
  );
  const actionableStepCount = nonNegativeCount(summary.actionable_step_count);
  const mappedStepCount = nonNegativeCount(summary.mapped_action_step_count);
  const unmappedStepCount = nonNegativeCount(summary.unmapped_action_step_count);
  const weakLocatorStepCount = nonNegativeCount(summary.weak_locator_step_count);
  const provided = Boolean(raw && typeof raw === 'object');
  const hasManualCases = manualCaseCount > 0 || manualCases.length > 0;
  const blockedCaseIds = caseRows
    .filter((item) => String(item.status || '').toLowerCase() !== 'ready')
    .map((item) => String(item.test_case_id || '').trim())
    .filter(Boolean);
  const blockedStepLabels = Array.from(new Set(
    caseRows.flatMap((item) => {
      const caseId = String(item.test_case_id || '').trim();
      const caseSteps = Array.isArray(item.steps) ? item.steps : [];
      return caseSteps
        .filter((step) => ['unmapped', 'weak_locator'].includes(String(step?.status || '').toLowerCase()))
        .map((step) => {
          const text = String(step?.text || step?.reason || '').trim();
          return text ? `${caseId ? `${caseId}: ` : ''}${text}` : '';
        })
        .filter(Boolean);
    })
  ));
  const ready = !hasManualCases || Boolean(
    provided
    && manualCaseCount > 0
    && readyCaseCount >= manualCaseCount
    && blockedCaseCount === 0
    && unmappedStepCount === 0
    && weakLocatorStepCount === 0
  );

  return {
    provided,
    hasManualCases,
    ready,
    manualCaseCount: Math.max(manualCaseCount, manualCases.length),
    readyCaseCount,
    blockedCaseCount,
    actionableStepCount,
    mappedStepCount,
    unmappedStepCount,
    weakLocatorStepCount,
    blockedCaseIds,
    blockedStepLabels,
  };
};

const LocatorReadinessSummary = ({
  readiness,
  onRecapture = null,
  recaptureDisabled = false,
}) => {
  if (!readiness?.hasManualCases) return null;
  const blockedSteps = readiness.unmappedStepCount + readiness.weakLocatorStepCount;
  return (
    <section className={`rounded-2xl border p-4 ${
      readiness.ready
        ? 'border-emerald-200 bg-emerald-50'
        : 'border-amber-200 bg-amber-50'
    }`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {readiness.ready ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          ) : (
            <AlertCircle className="h-5 w-5 text-amber-700" />
          )}
          <h3 className="text-sm font-bold text-slate-900">Locator readiness</h3>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${
          readiness.ready
            ? 'border-emerald-300 bg-white text-emerald-700'
            : 'border-amber-300 bg-white text-amber-800'
        }`}>
          {readiness.ready ? 'Ready' : readiness.provided ? 'Review needed' : 'Pending'}
        </span>
      </div>

      {readiness.provided && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-700">
            Cases {readiness.readyCaseCount}/{readiness.manualCaseCount}
          </span>
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-700">
            Steps {readiness.mappedStepCount}/{readiness.actionableStepCount}
          </span>
          {blockedSteps > 0 && (
            <span className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-amber-800">
              Review {blockedSteps}
            </span>
          )}
        </div>
      )}

      {!readiness.ready && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0 text-sm text-amber-900">
            <p>
              {readiness.provided
                ? onRecapture
                  ? 'Review weak or unmatched steps, or recapture the missing actions.'
                  : 'Review weak or unmatched steps. Ask the capture owner to recapture missing actions.'
                : 'Generate POMs to check locator mapping before scripts.'}
            </p>
            {readiness.blockedStepLabels.length > 0 && (
              <p className="mt-1 truncate text-xs" title={readiness.blockedStepLabels.join(' • ')}>
                Review: {readiness.blockedStepLabels.slice(0, 3).join(' • ')}
                {readiness.blockedStepLabels.length > 3 ? '…' : ''}
              </p>
            )}
            {readiness.blockedStepLabels.length === 0 && readiness.blockedCaseIds.length > 0 && (
              <p className="mt-1 truncate text-xs">
                Review: {readiness.blockedCaseIds.slice(0, 3).join(', ')}
                {readiness.blockedCaseIds.length > 3 ? '…' : ''}
              </p>
            )}
          </div>
          {onRecapture && readiness.provided && (
            <button
              type="button"
              onClick={onRecapture}
              disabled={recaptureDisabled}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MonitorPlay className="h-3.5 w-3.5" />
              Re-record flow
            </button>
          )}
        </div>
      )}
    </section>
  );
};

const STREAM_CASE_CATEGORIES = ['positive', 'negative'];

const resolveStreamCategory = (value, fallback = null) => {
  const normalized = String(value || '').trim().toLowerCase();
  return STREAM_CASE_CATEGORIES.includes(normalized) ? normalized : fallback;
};

const countStreamCases = (testCases = {}) =>
  STREAM_CASE_CATEGORIES.reduce(
    (total, category) => total + (Array.isArray(testCases?.[category]) ? testCases[category].length : 0),
    0
  );

const countStreamCasesWithInputSets = (testCases = {}) =>
  STREAM_CASE_CATEGORIES.reduce((total, category) => {
    const rows = Array.isArray(testCases?.[category]) ? testCases[category] : [];
    return total + rows.filter((testCase) => (
      Array.isArray(testCase?.user_input_sets) && testCase.user_input_sets.length > 0
    )).length;
  }, 0);

const buildInitialStreamTestCases = (sectionTitle, fieldConfig = null) => ({
  module: sectionTitle || 'Captured Feature',
  field_config: fieldConfig || null,
  positive: [],
  negative: [],
  total_count: 0,
  positive_count: 0,
  negative_count: 0,
  enrichment_status: 'pending',
  input_sets_status: 'pending',
  gap_analysis_status: 'pending',
});

const updateStreamCaseCounts = (testCases = {}) => {
  const positive = Array.isArray(testCases.positive) ? testCases.positive : [];
  const negative = Array.isArray(testCases.negative) ? testCases.negative : [];
  return {
    ...testCases,
    positive,
    negative,
    positive_count: positive.length,
    negative_count: negative.length,
    total_count: positive.length + negative.length,
  };
};

const mergeStreamCaseIntoResult = (currentResult, payload = {}) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  const category = resolveStreamCategory(payload.category || payload.prompt_type, 'positive');
  const incomingCase = payload.test_case && typeof payload.test_case === 'object'
    ? payload.test_case
    : payload;
  if (!incomingCase || typeof incomingCase !== 'object') return currentResult;

  const testCases = currentResult.test_cases && typeof currentResult.test_cases === 'object'
    ? currentResult.test_cases
    : buildInitialStreamTestCases(currentResult.section_title, currentResult.test_case_field_config);
  const currentCases = Array.isArray(testCases[category]) ? testCases[category] : [];
  const caseId = String(incomingCase.test_case_no || incomingCase.id || '').trim();
  const nextCase = {
    ...incomingCase,
    type: category,
    _streaming: true,
  };
  const existingIndex = caseId
    ? currentCases.findIndex((testCase) => String(testCase?.test_case_no || testCase?.id || '').trim() === caseId)
    : -1;
  const nextCases = [...currentCases];
  if (existingIndex >= 0) {
    const existingCase = nextCases[existingIndex] || {};
    nextCases[existingIndex] = {
      ...existingCase,
      ...nextCase,
      user_inputs: Array.isArray(nextCase.user_inputs) && nextCase.user_inputs.length > 0
        ? nextCase.user_inputs
        : existingCase.user_inputs,
      user_input_sets: Array.isArray(nextCase.user_input_sets) && nextCase.user_input_sets.length > 0
        ? nextCase.user_input_sets
        : existingCase.user_input_sets,
      _streaming: true,
      _streaming_input_sets: existingCase._streaming_input_sets,
    };
  } else {
    nextCases.push(nextCase);
  }

  const nextTestCases = updateStreamCaseCounts({
    ...testCases,
    [category]: nextCases,
  });
  const visibleCaseCount = countStreamCases(nextTestCases);

  return {
    ...currentResult,
    status: 'running',
    stream_status: 'running',
    test_cases: nextTestCases,
    streamed_case_count: Math.max(Number(currentResult.streamed_case_count || 0), visibleCaseCount),
  };
};

const mergeStreamInputSetsIntoResult = (currentResult, payload = {}) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  const caseId = String(payload.test_case_no || '').trim();
  if (!caseId) return currentResult;

  const testCases = currentResult.test_cases && typeof currentResult.test_cases === 'object'
    ? currentResult.test_cases
    : buildInitialStreamTestCases(currentResult.section_title, currentResult.test_case_field_config);
  const preferredCategory = resolveStreamCategory(payload.category || payload.prompt_type);
  const categories = preferredCategory
    ? [preferredCategory, ...STREAM_CASE_CATEGORIES.filter((category) => category !== preferredCategory)]
    : STREAM_CASE_CATEGORIES;
  const incomingSets = Array.isArray(payload.user_input_sets) ? payload.user_input_sets : [];
  const firstInputSet = incomingSets.find((item) => Array.isArray(item?.user_inputs));
  let didMerge = false;
  let nextTestCases = { ...testCases };

  categories.forEach((category) => {
    if (didMerge) return;
    const currentCases = Array.isArray(testCases[category]) ? testCases[category] : [];
    const existingIndex = currentCases.findIndex(
      (testCase) => String(testCase?.test_case_no || '').trim() === caseId
    );
    if (existingIndex < 0) return;
    const nextCases = [...currentCases];
    const existingCase = nextCases[existingIndex] || {};
    nextCases[existingIndex] = {
      ...existingCase,
      user_inputs: Array.isArray(payload.user_inputs) && payload.user_inputs.length > 0
        ? payload.user_inputs
        : (Array.isArray(firstInputSet?.user_inputs) ? firstInputSet.user_inputs : existingCase.user_inputs),
      user_input_sets: incomingSets,
      _streaming: true,
      _streaming_input_sets: true,
    };
    nextTestCases = {
      ...nextTestCases,
      [category]: nextCases,
    };
    didMerge = true;
  });

  const countedInputSets = didMerge
    ? countStreamCasesWithInputSets(nextTestCases)
    : Number(currentResult.streamed_input_sets_count || 0);

  return {
    ...currentResult,
    stream_status: 'running',
    test_cases: updateStreamCaseCounts(nextTestCases),
    streamed_input_sets_count: Math.max(
      Number(currentResult.streamed_input_sets_count || 0),
      countedInputSets,
      didMerge ? 1 : 0
    ),
  };
};

const updateStreamEnrichmentStatus = (currentResult, status, payload = {}) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  const testCases = currentResult.test_cases && typeof currentResult.test_cases === 'object'
    ? currentResult.test_cases
    : buildInitialStreamTestCases(currentResult.section_title, currentResult.test_case_field_config);

  return {
    ...currentResult,
    stream_status: status === 'failed' ? 'failed' : 'running',
    enrichment_status: status,
    input_sets_status: status,
    gap_analysis_status: status,
    coverage_gaps: Array.isArray(payload.coverage_gaps) ? payload.coverage_gaps : currentResult.coverage_gaps,
    suggested_additional: payload.suggested_additional || currentResult.suggested_additional,
    enriched_at: payload.enriched_at || currentResult.enriched_at,
    test_cases: {
      ...testCases,
      enrichment_status: status,
      input_sets_status: status,
      gap_analysis_status: status,
      coverage_gaps: Array.isArray(payload.coverage_gaps) ? payload.coverage_gaps : testCases.coverage_gaps,
      suggested_additional: payload.suggested_additional || testCases.suggested_additional,
      enriched_at: payload.enriched_at || testCases.enriched_at,
    },
  };
};

const markStreamResultFailed = (currentResult, message) => {
  if (!currentResult || typeof currentResult !== 'object') return currentResult;
  return {
    ...updateStreamEnrichmentStatus(currentResult, 'failed'),
    status: 'failed',
    stream_status: 'failed',
    stream_error: message || 'Generation stopped before completion.',
  };
};

const requestJson = async (endpoint, { method = 'GET', body = null } = {}) => {
  const options = {
    method,
    headers: { Accept: 'application/json' },
  };
  if (body !== null) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  const response = await apiFetch(endpoint, options);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Blocking errors carry a structured detail (readiness gates, unresolved runtime
    // values); the message inside it is what a tester can act on.
    const detail = payload?.detail;
    const message = (detail && typeof detail === 'object' ? detail.message : detail)
      || payload?.error
      || `Request failed with status ${response.status}`;
    const error = new Error(message);
    if (detail && typeof detail === 'object') error.detail = detail;
    throw error;
  }
  return payload;
};

const featureCaseJobStorageKey = (sessionId) => `feature-case-job:${sessionId}`;

const applyFeatureCaseProgressEvent = (row, setResult, initialResult) => {
  const data = row.data || {};
  if (row.type === 'case') {
    setResult((current) => mergeStreamCaseIntoResult(current || initialResult, data));
  } else if (row.type === 'input_sets') {
    setResult((current) => mergeStreamInputSetsIntoResult(current || initialResult, data));
  } else if (row.type === 'enrichment_started') {
    setResult((current) => updateStreamEnrichmentStatus(current || initialResult, 'running', data));
  } else if (row.type === 'enrichment_complete') {
    setResult((current) => updateStreamEnrichmentStatus(current || initialResult, 'completed', data));
  } else if (row.type === 'enrichment_failed') {
    setResult((current) => updateStreamEnrichmentStatus(current || initialResult, 'failed', data));
  }
};

const waitForFeatureCaseJob = async (jobId, sessionId, onEvent, shouldStop = () => false) => {
  const seenEvents = new Set();
  const deadline = Date.now() + 35 * 60 * 1000;
  while (Date.now() < deadline && !shouldStop()) {
    const [statusPayload, eventsPayload] = await Promise.all([
      requestJson(API_ENDPOINTS.JOB_BY_ID(jobId)),
      requestJson(`${API_ENDPOINTS.JOB_EVENTS(jobId)}?limit=500`),
    ]);
    if (shouldStop()) return null;
    for (const row of eventsPayload?.events || []) {
      if (seenEvents.has(row.id)) continue;
      seenEvents.add(row.id);
      onEvent(row);
    }
    const job = statusPayload?.job;
    if (job?.status === 'completed' || job?.status === 'partial_completed') {
      return requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId));
    }
    if (job?.status === 'failed' || job?.status === 'cancelled') {
      const error = new Error(job?.error?.message || (job.status === 'cancelled'
        ? 'Test case generation was stopped.' : 'Test case generation failed.'));
      error.terminalJob = true;
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
  if (shouldStop()) return null;
  throw new Error('Test case generation timed out before results were ready.');
};

const sendExtensionBridgeMessage = (message, timeoutMs = 12000) => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Open TestSuite AI in Chrome to start capture.'));
  }

  return new Promise((resolve, reject) => {
    const requestId = `feature-capture-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const cleanup = () => {
      window.clearTimeout(timerId);
      window.removeEventListener('message', handleMessage);
    };

    const timerId = window.setTimeout(() => {
      cleanup();
      reject(new Error('Chrome extension is not available on this page. Reload the extension, refresh TestSuite AI, and try again.'));
    }, timeoutMs);

    const handleMessage = (event) => {
      if (event.source !== window) return;
      const data = event.data || {};
      if (data.source !== EXTENSION_SOURCE || data.requestId !== requestId) return;
      cleanup();
      if (!data.ok) {
        reject(new Error(data.error || 'Chrome extension could not start capture.'));
        return;
      }
      resolve(data.response || data);
    };

    window.addEventListener('message', handleMessage);
    window.postMessage(
      {
        source: BRIDGE_SOURCE,
        requestId,
        ...message,
      },
      window.location.origin
    );
  });
};

const captureTabsFromSession = (session) => (
  Array.isArray(session?.tabs) ? session.tabs.filter((tab) => tab?.tab_key) : []
);

export const FeatureCaptureWorkspace = ({
  currentUser = null,
  applicationId = '',
  applicationRouteMode = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const lockedApplicationId = String(applicationId || '').trim();
  const isApplicationRouteMode = Boolean(applicationRouteMode && lockedApplicationId);
  const parsedFeatureRoute = useMemo(
    () => parseFeatureCaptureRoute(location.pathname),
    [location.pathname]
  );
  const [featureName, setFeatureName] = useState('');
  const [applicationUrl, setApplicationUrl] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(lockedApplicationId);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [applications, setApplications] = useState([]);
  const [, setApplicationsLoading] = useState(false);
  const [applicationsError, setApplicationsError] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState('');
  const [showCreateApplicationPanel, setShowCreateApplicationPanel] = useState(false);
  const [dashboardCreateApplication, setDashboardCreateApplication] = useState({
    name: '',
    application_id: '',
  });
  const [creatingDashboardApplication, setCreatingDashboardApplication] = useState(false);
  const [dashboardCreateApplicationError, setDashboardCreateApplicationError] = useState('');
  const [captureDashboardView, setCaptureDashboardView] = useState(
    isApplicationRouteMode ? 'workflow' : 'applications'
  );
  const [dashboardApplications, setDashboardApplications] = useState([]);
  const [dashboardApplicationsLoading, setDashboardApplicationsLoading] = useState(false);
  const [dashboardApplicationsError, setDashboardApplicationsError] = useState('');
  const [dashboardApplicationSearch, setDashboardApplicationSearch] = useState('');
  const [dashboardWorkspaceFilter, setDashboardWorkspaceFilter] = useState('');
  const [selectedDashboardApplicationId, setSelectedDashboardApplicationId] = useState('');
  const [applicationFeatureItems, setApplicationFeatureItems] = useState([]);
  const [applicationFeatureLoading, setApplicationFeatureLoading] = useState(false);
  const [applicationFeatureError, setApplicationFeatureError] = useState('');
  const [applicationFeatureSearch, setApplicationFeatureSearch] = useState('');
  const [applicationAuthState, setApplicationAuthState] = useState(null);
  const [applicationAuthLoading, setApplicationAuthLoading] = useState(false);
  const [applicationEnvironment, setApplicationEnvironment] = useState(null);
  const [captureEnvironmentId, setCaptureEnvironmentId] = useState('');
  const [showApplicationEnvironmentModal, setShowApplicationEnvironmentModal] = useState(false);
  const [renamingApplicationId, setRenamingApplicationId] = useState('');
  const [applicationRenameValue, setApplicationRenameValue] = useState('');
  const [applicationRenameError, setApplicationRenameError] = useState('');
  const [applicationRenameSaving, setApplicationRenameSaving] = useState(false);
  const [renamingFeatureId, setRenamingFeatureId] = useState('');
  const [featureRenameValue, setFeatureRenameValue] = useState('');
  const [featureRenameError, setFeatureRenameError] = useState('');
  const [featureRenameSavingId, setFeatureRenameSavingId] = useState('');
  const [deletingApplicationId, setDeletingApplicationId] = useState('');
  const [deletingFeatureCaptureId, setDeletingFeatureCaptureId] = useState('');
  const [archivingFeatureCaptureId, setArchivingFeatureCaptureId] = useState('');
  const [showArchivedFeatures, setShowArchivedFeatures] = useState(false);
  const [recapturingFeatureCaptureId, setRecapturingFeatureCaptureId] = useState('');
  const [showWorkspaceTools, setShowWorkspaceTools] = useState(false);
  const [showWorkspaceMembersModal, setShowWorkspaceMembersModal] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [workspaceMembersLoading, setWorkspaceMembersLoading] = useState(false);
  const [workspaceMembersError, setWorkspaceMembersError] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [activeUsersError, setActiveUsersError] = useState('');
  const [processedBrds, setProcessedBrds] = useState([]);
  const [brdListLoading, setBrdListLoading] = useState(false);
  const [brdListError, setBrdListError] = useState('');
  const [selectedBrdId, setSelectedBrdId] = useState('');
  const [brdSections, setBrdSections] = useState([]);
  const [brdSectionsLoading, setBrdSectionsLoading] = useState(false);
  const [brdSectionsError, setBrdSectionsError] = useState('');
  const [brdSectionsReloadToken, setBrdSectionsReloadToken] = useState(0);
  const [selectedBrdSectionNumber, setSelectedBrdSectionNumber] = useState('');
  const [showBrdPicker, setShowBrdPicker] = useState(false);
  const [brdSearchQuery, setBrdSearchQuery] = useState('');
  const [sectionSearchQuery, setSectionSearchQuery] = useState('');
  const [brdContextText, setBrdContextText] = useState('');
  const [brdContextFile, setBrdContextFile] = useState(null);
  const [brdFileError, setBrdFileError] = useState('');
  const [brdIndexing, setBrdIndexing] = useState(false);
  const [brdIndexingError, setBrdIndexingError] = useState('');
  const [brdIndexedFileName, setBrdIndexedFileName] = useState('');
  const [notes, setNotes] = useState('');
  const [scriptNotes, setScriptNotes] = useState('');
  const [recordContinuation, setRecordContinuation] = useState(false);
  const [recordSharedLogin, setRecordSharedLogin] = useState(false);
  const [session, setSession] = useState(null);
  const [showFeatureHistory, setShowFeatureHistory] = useState(false);
  const [featureHistoryItems, setFeatureHistoryItems] = useState([]);
  const [featureHistoryLoading, setFeatureHistoryLoading] = useState(false);
  const [featureHistoryError, setFeatureHistoryError] = useState('');
  const [featureHistorySearch, setFeatureHistorySearch] = useState('');
  const [selectedFeatureHistoryId, setSelectedFeatureHistoryId] = useState('');
  const [selectedHistoryApplicationId, setSelectedHistoryApplicationId] = useState('');
  const [featureHistoryRestoreId, setFeatureHistoryRestoreId] = useState('');
  const [featureHistoryDeleteTarget, setFeatureHistoryDeleteTarget] = useState(null);
  const [featureHistoryDeleteConfirmText, setFeatureHistoryDeleteConfirmText] = useState('');
  const [featureHistoryDeleteSaving, setFeatureHistoryDeleteSaving] = useState(false);
  const [featureHistoryDeleteError, setFeatureHistoryDeleteError] = useState('');
  const [featureSharingPanelOpen, setFeatureSharingPanelOpen] = useState(false);
  const [featureSharingStatus, setFeatureSharingStatus] = useState(null);
  const [featureSharingRequests, setFeatureSharingRequests] = useState([]);
  const [featureSharingLoading, setFeatureSharingLoading] = useState(false);
  const [featureSharingError, setFeatureSharingError] = useState('');
  const [featureShareUserId, setFeatureShareUserId] = useState('');
  const [featureShareActionError, setFeatureShareActionError] = useState('');
  const [featureRevokeTargetUser, setFeatureRevokeTargetUser] = useState(null);
  const [workflowStageOverride, setWorkflowStageOverride] = useState('');
  const [featureRunResults, setFeatureRunResults] = useState({});
  const [featureNameError, setFeatureNameError] = useState('');
  const [editingRecaptureTarget, setEditingRecaptureTarget] = useState(false);
  const [recaptureEditFocus, setRecaptureEditFocus] = useState('feature');
  const [recaptureFeatureName, setRecaptureFeatureName] = useState('');
  const [recaptureTargetSaving, setRecaptureTargetSaving] = useState(false);
  const [recaptureConfirmation, setRecaptureConfirmation] = useState(null);
  const [regenerateCasesConfirmation, setRegenerateCasesConfirmation] = useState(false);
  const [preparingRecapture, setPreparingRecapture] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedStepIds, setSelectedStepIds] = useState([]);
  const [capturedFlowTab, setCapturedFlowTab] = useState('steps');
  const [activeStepId, setActiveStepId] = useState('');
  const [contextEditing, setContextEditing] = useState(false);
  const [contextSaving, setContextSaving] = useState(false);
  const [contextError, setContextError] = useState('');
  const [streamProcessingResult, setStreamProcessingResult] = useState(null);
  const [activeCaseJobId, setActiveCaseJobId] = useState('');
  const activeCaseMonitorRef = useRef('');
  const [featureSaveResponse, setFeatureSaveResponse] = useState(null);
  const [featureRouteHydration, setFeatureRouteHydration] = useState({
    signature: '',
    loading: false,
  });
  const pendingRestoreStageRef = useRef('');
  const activeSessionIdRef = useRef('');
  const featureRouteHydratingRef = useRef(false);
  const featureRouteHandledRef = useRef('');
  const pendingCapturedFlowTabRef = useRef('');
  const selectedBrdIdRef = useRef('');
  const selectedWorkspaceIdRef = useRef('');
  const pendingRestoredBrdSectionRef = useRef({ pdfId: '', sectionNumber: '' });
  const handleRefreshFlowRef = useRef(null);
  const automaticMappingReviewRef = useRef({ signature: '', inFlight: false });
  const brdIndexingRequestRef = useRef({
    generation: 0,
    workspaceId: '',
  });

  const updateFeatureRouteHydration = useCallback((signature, loading) => {
    const normalizedSignature = String(signature || '').trim();
    featureRouteHydratingRef.current = Boolean(loading);
    setFeatureRouteHydration((current) => (
      current.signature === normalizedSignature && current.loading === Boolean(loading)
        ? current
        : { signature: normalizedSignature, loading: Boolean(loading) }
    ));
  }, []);

  const updateSelectedBrdId = useCallback((value) => {
    const normalized = String(value || '').trim();
    selectedBrdIdRef.current = normalized;
    setSelectedBrdId(normalized);
    return normalized;
  }, []);

  const updateSelectedWorkspaceId = useCallback((value) => {
    const normalized = String(value || '').trim();
    const resolved = isApplicationRouteMode ? lockedApplicationId : normalized;
    selectedWorkspaceIdRef.current = resolved;
    setSelectedWorkspaceId(resolved);
    return resolved;
  }, [isApplicationRouteMode, lockedApplicationId]);

  useEffect(() => {
    if (isApplicationRouteMode) {
      updateSelectedWorkspaceId(lockedApplicationId);
      setCaptureDashboardView('workflow');
    }
  }, [isApplicationRouteMode, lockedApplicationId, updateSelectedWorkspaceId]);

  const invalidateBrdIndexingRequest = useCallback(() => {
    const nextGeneration = Number(brdIndexingRequestRef.current?.generation || 0) + 1;
    brdIndexingRequestRef.current = {
      generation: nextGeneration,
      workspaceId: '',
    };
  }, []);

  const steps = useMemo(() => (
    Array.isArray(session?.normalized_steps) ? session.normalized_steps : []
  ), [session]);
  const selectableStepIds = useMemo(
    () => steps.map((step, index) => stepSelectionId(step, index)),
    [steps]
  );
  const actionableCaptureStepCount = useMemo(() => (
    steps.filter((step) => {
      const action = String(step?.action || '').trim().toLowerCase();
      return action && !NON_ACTION_CAPTURE_STEPS.has(action);
    }).length
  ), [steps]);
  const journeyVariables = useMemo(
    () => journeyVariablesFromSession(session),
    [session]
  );
  const captureTabs = useMemo(
    () => captureTabsFromSession(session),
    [session]
  );
  const journeyVariablesValidation = useMemo(
    () => (hasObjectContent(session?.journey_variables_validation)
      ? session.journey_variables_validation
      : {}),
    [session?.journey_variables_validation]
  );
  // Tab badge: how many captured values a reviewer still has to act on.
  const journeyVariableIssueCount = useMemo(() => {
    const issues = Array.isArray(journeyVariablesValidation?.issues)
      ? journeyVariablesValidation.issues
      : [];
    return new Set(issues.map((issue) => String(issue?.variable_id || ''))).size;
  }, [journeyVariablesValidation]);
  const testCases = useMemo(() => (
    Array.isArray(session?.test_cases) ? session.test_cases : []
  ), [session]);

  useEffect(() => {
    setCapturedFlowTab(
      pendingCapturedFlowTabRef.current === 'components' ? 'components' : 'steps'
    );
    pendingCapturedFlowTabRef.current = '';
    setActiveStepId('');
  }, [session?.id]);

  const analysis = useMemo(
    () => (hasObjectContent(session?.analysis) ? session.analysis : {}),
    [session?.analysis]
  );
  const featureProcessingResult = useMemo(
    () => buildFeatureProcessingResult({ session, analysis, testCases }),
    [analysis, session, testCases]
  );
  const activeFeatureProcessingResult = streamProcessingResult || featureProcessingResult;
  const featureInitialTestCases = useMemo(() => {
    const savedBaseline = session?.test_case_review?.baseline_test_cases;
    return countTestCases(savedBaseline) > 0
      ? savedBaseline
      : activeFeatureProcessingResult.test_cases;
  }, [activeFeatureProcessingResult.test_cases, session?.test_case_review?.baseline_test_cases]);
  const featureLocatorReadiness = useMemo(
    () => resolveFeatureLocatorReadiness(session, testCases),
    [session, testCases]
  );
  const hasLiveCaseGeneration = ['running', 'failed', 'completed'].includes(
    String(streamProcessingResult?.stream_status || '').trim().toLowerCase()
  );
  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => String(workspace.id) === String(selectedWorkspaceId)) || null,
    [selectedWorkspaceId, workspaces]
  );
  const selectedWorkspaceName = selectedWorkspace?.name || session?.application?.name || session?.workspace?.name || '';
  const selectedApplication = useMemo(
    () => (
      applications.find((application) => String(application.id) === String(selectedApplicationId))
      || dashboardApplications.find((application) => String(application.id) === String(selectedApplicationId))
      || null
    ),
    [applications, dashboardApplications, selectedApplicationId]
  );
  const selectedApplicationName = (
    selectedApplication?.name
    || session?.feature_area?.name
    || applicationNameFromUrl(applicationUrl, '')
  );
  const selectedDashboardApplication = useMemo(
    () => (
      dashboardApplications.find((application) => (
        String(application?.id || '') === String(selectedDashboardApplicationId || '')
      ))
      || null
    ),
    [dashboardApplications, selectedDashboardApplicationId]
  );
  const activeApplication = selectedDashboardApplication || selectedApplication || session?.feature_area || null;
  const activeApplicationAuthSourceId = applicationAutomationSourceId(activeApplication);
  const effectiveFeatureAuthSourceId = String(
    applicationEnvironment?.auth_source_id
    || activeApplicationAuthSourceId
    || featureAutomationSourceId(session)
    || ''
  ).trim();
  const applicationAuthMetadata = applicationAuthState?.metadata || {};
  const applicationSessionAvailable = Boolean(applicationAuthState?.available);
  const applicationLoginScriptAvailable = Boolean(applicationAuthMetadata?.login_replay_available);
  const applicationAuthCapturedLabel = applicationAuthMetadata?.captured_at
    ? formatHistoryDate(applicationAuthMetadata.captured_at)
    : 'saved';
  const applicationAuthAvailable = applicationSessionAvailable || applicationLoginScriptAvailable;
  // Recording always uses the application's default environment.
  // A capture always opens in its own incognito window. When the application environment
  // has a saved login, the extension puts it into that window before recording starts, so
  // the sign-in never becomes part of the captured flow.
  const captureEnvironmentName = String(applicationEnvironment?.name || '').trim();
  const captureStartUrl = String(
    applicationEnvironment?.base_url
    || activeApplication?.base_url
    || applicationUrl
    || ''
  ).trim();
  const captureLaunchMessage = recordContinuation
    ? 'Opening the capture window. Complete the shared steps first, then click Start Capture when the new part begins.'
    : recordSharedLogin
      ? 'Opening a clean capture window. Record the login and navigation once as a reusable shared flow.'
    : applicationSessionAvailable
      ? 'Opening the Application URL with the saved session. If it has expired, automatic login runs before recording starts.'
      : applicationLoginScriptAvailable
        ? 'Opening the Application URL and signing in automatically. Recording starts on the page reached after login.'
      : 'Opening an incognito capture window. Recording starts automatically when the page loads.';
  const dashboardWorkspaceName = useMemo(() => {
    if (!dashboardWorkspaceFilter) return 'All applications';
    return workspaces.find((workspace) => String(workspace?.id || '') === String(dashboardWorkspaceFilter || ''))?.name
      || 'Selected application';
  }, [dashboardWorkspaceFilter, workspaces]);
  const effectiveDashboardWorkspaceId = useMemo(() => String(
    dashboardWorkspaceFilter
    || selectedWorkspaceId
    || workspaces[0]?.id
    || ''
  ).trim(), [dashboardWorkspaceFilter, selectedWorkspaceId, workspaces]);
  const effectiveDashboardWorkspaceName = useMemo(() => (
    workspaces.find((workspace) => String(workspace?.id || '').trim() === effectiveDashboardWorkspaceId)?.name
    || ''
  ), [effectiveDashboardWorkspaceId, workspaces]);
  const visibleDashboardApplications = useMemo(() => {
    const query = String(dashboardApplicationSearch || '').trim().toLowerCase();
    return dashboardApplications.filter((application) => {
      const workspaceId = String(application?.application_id || application?.application?.id || application?.workspace?.id || '').trim();
      if (dashboardWorkspaceFilter && workspaceId !== String(dashboardWorkspaceFilter || '').trim()) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        application?.name,
        application?.base_url,
        application?.environment_name,
        applicationWorkspaceName(application),
        Array.isArray(application?.flow_feature_names) ? application.flow_feature_names.join(' ') : '',
        Array.isArray(application?.tags) ? application.tags.join(' ') : '',
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [dashboardApplicationSearch, dashboardApplications, dashboardWorkspaceFilter]);
  const applicationFeatureGroups = useMemo(() => {
    const groups = new Map();
    applicationFeatureItems.forEach((item) => {
      const featureNameText = String(item?.feature_name || '').trim() || 'Untitled feature';
      const groupKey = featureNameText ? featureNameText.toLowerCase() : String(item?.id || '').trim();
      const existing = groups.get(groupKey) || {
        id: groupKey,
        feature_name: featureNameText,
        flows: [],
        latest: null,
        latest_at: '',
      };
      const itemUpdatedAt = String(item?.updated_at || item?.created_at || '');
      existing.flows.push(item);
      if (!existing.latest || itemUpdatedAt > existing.latest_at) {
        existing.latest = item;
        existing.latest_at = itemUpdatedAt;
        existing.feature_name = featureNameText;
      }
      groups.set(groupKey, existing);
    });
    return Array.from(groups.values()).sort((left, right) => (
      String(right.latest_at || '').localeCompare(String(left.latest_at || ''))
    ));
  }, [applicationFeatureItems]);
  const filteredApplicationFeatureGroups = useMemo(() => {
    const query = String(applicationFeatureSearch || '').trim().toLowerCase();
    if (!query) return applicationFeatureGroups;
    return applicationFeatureGroups.filter((group) => {
      const latest = group.latest || {};
      const haystack = [
        group.feature_name,
        latest.start_url,
        latest.status,
        latest.owner_email,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [applicationFeatureGroups, applicationFeatureSearch]);
  const workspaceProcessedBrds = useMemo(() => {
    const workspaceId = String(selectedWorkspaceId || '').trim();
    if (!workspaceId) return [];
    return processedBrds.filter((brd) => {
      const brdWorkspaceId = String(brd?.application_id || '').trim();
      return brdWorkspaceId === workspaceId;
    });
  }, [processedBrds, selectedWorkspaceId]);
  const selectedBrd = useMemo(
    () => workspaceProcessedBrds.find((brd) => String(brd.pdf_id) === String(selectedBrdId)) || null,
    [selectedBrdId, workspaceProcessedBrds]
  );
  const rawSelectedBrdStoredSectionCount = Number(selectedBrd?.sections_count || 0);
  const selectedBrdStoredSectionCount = Number.isFinite(rawSelectedBrdStoredSectionCount)
    ? Math.max(0, rawSelectedBrdStoredSectionCount)
    : 0;
  const selectedBrdSection = useMemo(
    () => brdSections.find((section) => (
      String(section?.number ?? section?.section_number ?? '') === String(selectedBrdSectionNumber)
      || String(section?.section_number ?? section?.number ?? '') === String(selectedBrdSectionNumber)
    )) || null,
    [brdSections, selectedBrdSectionNumber]
  );
  const selectedBrdSectionReference = useMemo(() => {
    if (!selectedBrd || !selectedBrdSection) return null;
    return {
      pdf_id: selectedBrd.pdf_id,
      pdf_file: selectedBrd.pdf_file || selectedBrd.pdf_id,
      brd_version: selectedBrd.brd_version || null,
      application_id: selectedBrd.application_id || '',
      section_number: String(selectedBrdSection.section_number || selectedBrdSection.number || ''),
      section_title: (
        selectedBrdSection.title
        || selectedBrdSection.section_title
        || selectedBrdSection.section_name
        || selectedBrdSection.name
        || ''
      ),
      indexed_pages: selectedBrdSection.indexed_pages || '',
      source_text: buildBrdSectionSourceText(selectedBrdSection),
    };
  }, [selectedBrd, selectedBrdSection]);
  const hasSelectedBrdSectionContext = Boolean(selectedBrdSectionReference);
  const hasIncompleteBrdSectionSelection = Boolean(selectedBrdId && !selectedBrdSectionReference);
  const hasAttachedBrdContext = Boolean(brdContextFile || String(brdContextText || '').trim());
  const filteredBrds = useMemo(() => {
    const query = String(brdSearchQuery || '').trim().toLowerCase();
    if (!query) return workspaceProcessedBrds;
    return workspaceProcessedBrds.filter((brd) => {
      const haystack = [
        brd?.pdf_file,
        brd?.pdf_id,
        brd?.application_id,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [brdSearchQuery, workspaceProcessedBrds]);
  const filteredBrdSections = useMemo(() => {
    const query = String(sectionSearchQuery || '').trim().toLowerCase();
    if (!query) return brdSections;
    return brdSections.filter((section) => (
      brdSectionLabel(section).toLowerCase().includes(query)
      || String(section?.indexed_pages || '').toLowerCase().includes(query)
    ));
  }, [brdSections, sectionSearchQuery]);
  const featureHistoryApplicationGroups = useMemo(() => {
    const groups = new Map();
    featureHistoryItems.forEach((item) => {
      const applicationId = flowApplicationId(item);
      const existing = groups.get(applicationId) || {
        id: applicationId,
        name: flowApplicationName(item),
        base_url: item?.feature_area_base_url || item?.start_url || '',
        workspace_name: item?.application_name || item?.workspace_name || '',
        flows: [],
        latest_at: '',
        steps_count: 0,
        test_cases_count: 0,
        scripts_count: 0,
      };
      existing.flows.push(item);
      existing.steps_count += Number(item?.steps_count || 0);
      existing.test_cases_count += Number(item?.test_cases_count || 0);
      existing.scripts_count += Number(item?.scripts_count || 0);
      const itemUpdatedAt = String(item?.updated_at || item?.created_at || '');
      if (!existing.latest_at || itemUpdatedAt > existing.latest_at) {
        existing.latest_at = itemUpdatedAt;
      }
      groups.set(applicationId, existing);
    });
    return Array.from(groups.values()).sort((left, right) => (
      String(right.latest_at || '').localeCompare(String(left.latest_at || ''))
    ));
  }, [featureHistoryItems]);

  const filteredFeatureHistory = useMemo(() => {
    const query = String(featureHistorySearch || '').trim().toLowerCase();
    const applicationFiltered = selectedHistoryApplicationId
      ? featureHistoryItems.filter((item) => flowApplicationId(item) === selectedHistoryApplicationId)
      : featureHistoryItems;
    if (!query) return applicationFiltered;
    return applicationFiltered.filter((item) => {
      const haystack = [
        item?.feature_name,
        item?.start_url,
        item?.application_name,
        item?.workspace_name,
        item?.feature_area_name,
        item?.feature_area_base_url,
        item?.feature_area_environment_name,
        item?.status,
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [featureHistoryItems, featureHistorySearch, selectedHistoryApplicationId]);
  const selectedFeatureHistoryItem = useMemo(() => {
    if (!filteredFeatureHistory.length) return null;
    const explicitSelection = filteredFeatureHistory.find(
      (item) => String(item?.id || '') === String(selectedFeatureHistoryId || '')
    );
    if (explicitSelection) return explicitSelection;
    const activeSelection = filteredFeatureHistory.find(
      (item) => String(item?.id || '') === String(session?.id || '')
    );
    return activeSelection || filteredFeatureHistory[0];
  }, [filteredFeatureHistory, selectedFeatureHistoryId, session?.id]);
  const selectedHistoryApplication = useMemo(
    () => featureHistoryApplicationGroups.find((group) => group.id === selectedHistoryApplicationId) || null,
    [featureHistoryApplicationGroups, selectedHistoryApplicationId]
  );
  const applicationNameExistsInWorkspace = useCallback((name, workspaceId, excludeApplicationId = '') => {
    const nameKey = uniqueNameKey(name);
    const workspaceKey = String(workspaceId || '').trim();
    const excludeKey = String(excludeApplicationId || '').trim();
    if (!nameKey || !workspaceKey) return false;
    const rows = [...dashboardApplications, ...applications];
    const seen = new Set();
    return rows.some((application) => {
      const applicationId = String(application?.id || '').trim();
      if (applicationId && seen.has(applicationId)) return false;
      if (applicationId) seen.add(applicationId);
      if (excludeKey && applicationId === excludeKey) return false;
      const applicationWorkspaceId = String(application?.application_id || application?.application?.id || application?.workspace?.id || '').trim();
      return applicationWorkspaceId === workspaceKey && uniqueNameKey(application?.name) === nameKey;
    });
  }, [applications, dashboardApplications]);
  const featureNameExistsInApplication = useCallback((name, applicationId, excludeSessionId = '') => {
    const nameKey = uniqueNameKey(name);
    const applicationKey = String(applicationId || '').trim();
    const excludeKey = String(excludeSessionId || '').trim();
    if (!nameKey || !applicationKey) return false;
    const rows = [...applicationFeatureItems, ...featureHistoryItems];
    const seen = new Set();
    return rows.some((item) => {
      const sessionId = String(item?.id || '').trim();
      if (sessionId && seen.has(sessionId)) return false;
      if (sessionId) seen.add(sessionId);
      if (excludeKey && sessionId === excludeKey) return false;
      return flowApplicationId(item) === applicationKey && uniqueNameKey(item?.feature_name) === nameKey;
    });
  }, [applicationFeatureItems, featureHistoryItems]);

  useEffect(() => {
    const sourceId = String(effectiveFeatureAuthSourceId || '').trim();
    let cancelled = false;
    if (!sourceId) {
      setApplicationAuthState(null);
      setApplicationAuthLoading(false);
      return undefined;
    }

    setApplicationAuthLoading(true);
    setApplicationAuthState(null);
    apiFetch(API_ENDPOINTS.PLAYWRIGHT_AUTH_STATE(sourceId))
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!cancelled) {
          setApplicationAuthState(response.ok ? payload : null);
        }
      })
      .catch(() => {
        if (!cancelled) setApplicationAuthState(null);
      })
      .finally(() => {
        if (!cancelled) setApplicationAuthLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [effectiveFeatureAuthSourceId]);

  useEffect(() => {
    if (!showFeatureHistory || !selectedHistoryApplicationId) return;
    const stillExists = featureHistoryApplicationGroups.some((group) => group.id === selectedHistoryApplicationId);
    if (!stillExists) {
      setSelectedHistoryApplicationId(featureHistoryApplicationGroups[0]?.id || '');
    }
  }, [featureHistoryApplicationGroups, selectedHistoryApplicationId, showFeatureHistory]);

  const currentFeatureSharing = useMemo(() => {
    const selectedId = String(selectedFeatureHistoryItem?.id || '').trim();
    return selectedId && String(featureSharingStatus?.pdf_id || '').trim() === selectedId
      ? featureSharingStatus
      : null;
  }, [featureSharingStatus, selectedFeatureHistoryItem?.id]);
  const featureSharedUsers = Array.isArray(currentFeatureSharing?.shared_users)
    ? currentFeatureSharing.shared_users
    : [];
  const featureEligibleShareUsers = Array.isArray(currentFeatureSharing?.eligible_users)
    ? currentFeatureSharing.eligible_users
    : [];
  const featurePendingShareRequests = (Array.isArray(featureSharingRequests) ? featureSharingRequests : [])
    .filter((requestRow) => String(requestRow?.status || '').toLowerCase() === 'pending');
  const featureCanDirectShare = Boolean(currentFeatureSharing?.can_direct_share);
  const featureCanRequestShare = Boolean(currentFeatureSharing?.can_request_share);
  const featureCanShare = Boolean(currentFeatureSharing) && (featureCanDirectShare || featureCanRequestShare);
  const featureShareActionLabel = featureCanDirectShare ? 'Share Flow' : 'Request Share';
  const eventsCount = Array.isArray(session?.events) ? session.events.length : 0;
  const capturedCount = steps.length || eventsCount;
  const hasCapturedFlow = capturedCount > 0;
  const captureIsComplete = !preparingRecapture && (hasCapturedFlow || [
    'captured',
    'analyzed',
    'cases_generated',
    'test_cases_reviewed',
    'pom_generated',
    'scripts_generated',
    'execution_prepared',
  ].includes(String(session?.status || '').toLowerCase()));
  const currentStageId = workflowStageOverride || stageIdFromSession(session);
  const currentStageIndex = Math.max(0, workflowStages.findIndex((stage) => stage.id === currentStageId));
  const currentStage = workflowStages[currentStageIndex] || workflowStages[0];

  useEffect(() => {
    if (currentStage.id !== 'captured-flow' || !session?.id || !journeyVariables.length) return;
    const unresolved = steps.filter((step) => (
      ['fill', 'select', 'check', 'uncheck', 'assert'].includes(String(step?.action || '').toLowerCase())
      && step?.value_source?.kind !== 'variable'
      && !String(step?.suggested_variable_id || '').trim()
    ));
    if (!unresolved.length) return;
    const signature = JSON.stringify({
      sessionId: session.id,
      variables: journeyVariables.map((variable) => [variable.id, variable.name]),
      steps: unresolved.map((step) => [
        step.id,
        step.action,
        step.target,
        step.field?.label,
        step.field?.name,
        step.field?.placeholder,
        step.page_name || step.url_path,
      ]),
    });
    const review = automaticMappingReviewRef.current;
    if (review.signature === signature) return;
    automaticMappingReviewRef.current = { signature, inFlight: true };
    setStatusMessage(`Checking ${unresolved.length} unmapped input${unresolved.length === 1 ? '' : 's'}…`);

    let cancelled = false;
    requestJson(
      API_ENDPOINTS.FEATURE_CAPTURE_VARIABLE_BINDING_SUGGESTIONS(session.id),
      { method: 'POST', body: {} },
    ).then((payload) => {
      if (cancelled || automaticMappingReviewRef.current.signature !== signature) return;
      if (payload?.session) setSession(payload.session);
      const count = Array.isArray(payload?.suggestions) ? payload.suggestions.length : 0;
      setStatusMessage(count
        ? `${count} mapping suggestion${count === 1 ? '' : 's'} ready for your review.`
        : 'No additional confident mappings were found. Existing mappings were preserved.');
    }).catch((error) => {
      if (cancelled || automaticMappingReviewRef.current.signature !== signature) return;
      setStatusMessage(error.message || 'Automatic mapping review is unavailable right now.');
    }).finally(() => {
      if (automaticMappingReviewRef.current.signature === signature) {
        automaticMappingReviewRef.current = { signature, inFlight: false };
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentStage.id, journeyVariables, session?.id, steps]);

  useEffect(() => {
    setContextEditing(false);
    setContextSaving(false);
    setContextError('');
  }, [session?.id]);
  const showStageChecklist = !(
    currentStage.id === 'cases'
    || currentStage.id === 'capture'
    || currentStage.id === 'captured-flow'
    || currentStage.id === 'save'
    || currentStage.id === 'pom'
  );
  const canPrepareCapture = Boolean(
    featureName.trim()
    && captureStartUrl
    && selectedWorkspaceId
    && (isApplicationRouteMode || selectedApplicationId)
    && !hasIncompleteBrdSectionSelection
    && !brdFileError
  );
  const isBusy = Boolean(busyAction);
  const backendEndpoint = API_BASE_URL || '';
  const canManageCaptureBrd = useMemo(() => {
    if (!session?.id) return true;

    const explicitOwnerFlag = [
      session?.access?.is_owner,
      session?.access?.isOwner,
      session?.access_metadata?.is_owner,
      session?.capture_access?.is_owner,
      session?.is_owner,
    ].find((value) => typeof value === 'boolean');
    if (typeof explicitOwnerFlag === 'boolean') return explicitOwnerFlag;

    const ownerUserId = String(session?.owner_user_id || '').trim();
    const currentUserId = String(currentUser?.id || currentUser?.user_id || '').trim();
    if (ownerUserId && currentUserId) return ownerUserId === currentUserId;
    return true;
  }, [currentUser?.id, currentUser?.user_id, session]);
  useEffect(() => {
    if (!canManageCaptureBrd) setShowBrdPicker(false);
  }, [canManageCaptureBrd]);
  const currentGlobalRole = String(currentUser?.role || '').trim().toLowerCase();
  const canGloballyManageWorkspaceMembers = currentGlobalRole === 'lead';
  const canUploadCaptureBrd = canManageCaptureBrd
    && hasPermission(selectedWorkspace?.current_user_permissions, 'brd:upload');
  const canCreateWorkspace = hasPermission(currentUser?.application_permissions, 'application:create');
  const canViewWorkspaceMembers = hasPermission(selectedWorkspace?.current_user_permissions, 'application:view');
  const canManageWorkspaceMembers =
    canGloballyManageWorkspaceMembers
    && hasPermission(selectedWorkspace?.current_user_permissions, 'application:manage_members');
  const canUseWorkspaceTools = canCreateWorkspace || canViewWorkspaceMembers;
  const opensMembersDirectly = canViewWorkspaceMembers && !canCreateWorkspace;
  const canOpenCreateApplicationPanel = !workspaceLoading && (workspaces.length > 0 || canCreateWorkspace);
  const createApplicationButtonTitle = canOpenCreateApplicationPanel
    ? undefined
    : workspaceLoading
      ? 'Loading applications...'
      : 'Ask a Lead or Admin to add you to an application before creating a feature area.';

  const navigateFeatureRoute = useCallback((targetPath, navigationOptions = {}) => {
    if (!parsedFeatureRoute.isFeatureCaptureRoute || location.search) return;
    let normalizedPath = String(targetPath || FEATURE_CAPTURE_ROUTE_ROOT).trim() || FEATURE_CAPTURE_ROUTE_ROOT;
    if (isApplicationRouteMode && normalizedPath.startsWith(FEATURE_CAPTURE_ROUTE_ROOT)) {
      const legacyTarget = parseFeatureCaptureRoute(normalizedPath);
      normalizedPath = legacyTarget.routeKind === 'workflow'
        ? buildFeatureCaptureRoute({
            rootApplicationId: lockedApplicationId,
            sessionId: legacyTarget.sessionId,
            stageId: legacyTarget.stageId,
          })
        : buildFeatureCaptureRoute({ rootApplicationId: lockedApplicationId });
    }
    const currentPath = `${location.pathname}${location.search || ''}`;
    if (currentPath !== normalizedPath) {
      navigate(normalizedPath, navigationOptions);
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    isApplicationRouteMode,
    lockedApplicationId,
    parsedFeatureRoute.isFeatureCaptureRoute,
  ]);

  const goToFeatureStage = useCallback((stageId, options = {}) => {
    const nextStage = normalizeFeatureCaptureStageId(stageId, 'capture');
    const targetSessionId = String(options?.sessionId || session?.id || '').trim();
    const navigationOptions = { ...(options || {}) };
    delete navigationOptions.sessionId;
    setWorkflowStageOverride(nextStage);
    if (!targetSessionId) return;
    navigateFeatureRoute(buildFeatureCaptureRoute({
      rootApplicationId: isApplicationRouteMode ? lockedApplicationId : '',
      sessionId: targetSessionId,
      stageId: nextStage,
    }), navigationOptions);
  }, [
    isApplicationRouteMode,
    lockedApplicationId,
    navigateFeatureRoute,
    session?.id,
  ]);

  const loadWorkspaces = useCallback(async () => {
    setWorkspaceLoading(true);
    setWorkspaceError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.APPLICATIONS);
      const rows = Array.isArray(payload?.applications)
        ? payload.applications
        : (Array.isArray(payload?.workspaces) ? payload.workspaces : []);
      setWorkspaces(rows);
      if (isApplicationRouteMode) {
        updateSelectedWorkspaceId(lockedApplicationId);
        return rows;
      }
      if (!selectedWorkspaceId && rows.length > 0) {
        if (rows[0]?.id) updateSelectedWorkspaceId(rows[0].id);
      }
      return rows;
    } catch (error) {
      setWorkspaceError(error.message || 'Failed to load applications');
      return [];
    } finally {
      setWorkspaceLoading(false);
    }
  }, [
    isApplicationRouteMode,
    lockedApplicationId,
    selectedWorkspaceId,
    updateSelectedWorkspaceId,
  ]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  // The environment is a property of the application, so it is resolved the same way in
  // both routes. The backend picks the capture's login from the application's default
  // environment, and this screen has to be looking at the same one to describe it.
  const environmentApplicationId = String(
    isApplicationRouteMode
      ? lockedApplicationId
      : (session?.application_id || session?.application?.id || selectedWorkspaceId || '')
  ).trim();

  useEffect(() => {
    if (!environmentApplicationId) {
      setApplicationEnvironment(null);
      setCaptureEnvironmentId('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const payload = await requestJson(
          API_ENDPOINTS.APPLICATION_ENVIRONMENTS(environmentApplicationId)
        );
        const environments = Array.isArray(payload?.environments) ? payload.environments : [];
        const environment = environments.find((item) => item?.is_default) || environments[0] || null;
        if (!cancelled) {
          setApplicationEnvironment(environment);
          setCaptureEnvironmentId(String(environment?.id || ''));
        }
      } catch (_err) {
        if (!cancelled) {
          setApplicationEnvironment(null);
          setCaptureEnvironmentId('');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [environmentApplicationId]);

  const loadApplications = useCallback(async (workspaceId = selectedWorkspaceId) => {
    if (isApplicationRouteMode) {
      setApplications([]);
      return [];
    }
    const normalizedWorkspaceId = String(workspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setApplications([]);
      setSelectedApplicationId('');
      return [];
    }

    setApplicationsLoading(true);
    setApplicationsError('');
    try {
      const payload = await requestJson(
        `${API_ENDPOINTS.FEATURE_AREAS}?application_id=${encodeURIComponent(normalizedWorkspaceId)}`
      );
      const rows = Array.isArray(payload?.feature_areas)
        ? payload.feature_areas
        : (Array.isArray(payload?.applications) ? payload.applications : []);
      setApplications(rows);
      setSelectedApplicationId((current) => {
        const currentId = String(current || '').trim();
        if (currentId && rows.some((application) => String(application?.id || '') === currentId)) {
          return currentId;
        }
        const sessionApplicationId = String(session?.feature_area_id || session?.feature_area?.id || '').trim();
        if (sessionApplicationId && rows.some((application) => String(application?.id || '') === sessionApplicationId)) {
          return sessionApplicationId;
        }
        return '';
      });
      return rows;
    } catch (error) {
      setApplications([]);
      setApplicationsError(error.message || 'Failed to load feature areas.');
      return [];
    } finally {
      setApplicationsLoading(false);
    }
  }, [
    isApplicationRouteMode,
    selectedWorkspaceId,
    session?.feature_area_id,
    session?.feature_area?.id,
  ]);

  useEffect(() => {
    loadApplications(selectedWorkspaceId);
  }, [loadApplications, selectedWorkspaceId]);

  const loadApplicationDashboard = useCallback(async () => {
    if (isApplicationRouteMode) {
      setDashboardApplications([]);
      return [];
    }
    setDashboardApplicationsLoading(true);
    setDashboardApplicationsError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.FEATURE_AREAS);
      const rows = Array.isArray(payload?.feature_areas)
        ? payload.feature_areas
        : (Array.isArray(payload?.applications) ? payload.applications : []);
      setDashboardApplications(rows);
      setSelectedDashboardApplicationId((current) => {
        const currentId = String(current || '').trim();
        return currentId && rows.some((application) => String(application?.id || '') === currentId)
          ? currentId
          : '';
      });
      return rows;
    } catch (error) {
      setDashboardApplications([]);
      setDashboardApplicationsError(error.message || 'Failed to load feature areas.');
      return [];
    } finally {
      setDashboardApplicationsLoading(false);
    }
  }, [isApplicationRouteMode]);

  useEffect(() => {
    loadApplicationDashboard();
  }, [loadApplicationDashboard]);

  const loadApplicationFeatures = useCallback(async (applicationId = selectedDashboardApplicationId) => {
    const normalizedApplicationId = String(applicationId || '').trim();
    if (!normalizedApplicationId) {
      setApplicationFeatureItems([]);
      return [];
    }

    setApplicationFeatureLoading(true);
    setApplicationFeatureError('');
    try {
      const payload = await requestJson(
        `${API_ENDPOINTS.FEATURE_CAPTURES}?feature_area_id=${encodeURIComponent(normalizedApplicationId)}`
        + `&include_archived=${showArchivedFeatures ? 'true' : 'false'}`
      );
      const rows = Array.isArray(payload?.captures) ? payload.captures : [];
      setApplicationFeatureItems(rows);
      return rows;
    } catch (error) {
      setApplicationFeatureItems([]);
      setApplicationFeatureError(error.message || 'Failed to load features for this feature area.');
      return [];
    } finally {
      setApplicationFeatureLoading(false);
    }
  }, [selectedDashboardApplicationId, showArchivedFeatures]);

  useEffect(() => {
    if (captureDashboardView !== 'application' || !selectedDashboardApplicationId) return;
    loadApplicationFeatures(selectedDashboardApplicationId);
  }, [captureDashboardView, loadApplicationFeatures, selectedDashboardApplicationId]);

  const loadProcessedBrds = useCallback(async () => {
    setBrdListLoading(true);
    setBrdListError('');
    try {
      const payload = await requestJson(`${API_ENDPOINTS.PROCESSED_PDFS}?source_category=brd`);
      const rows = Array.isArray(payload?.pdfs) ? payload.pdfs : [];
      setProcessedBrds(rows.filter((row) => Number(row?.sections_count || 0) > 0));
      return rows;
    } catch (error) {
      setProcessedBrds([]);
      setBrdListError(error.message || 'Failed to load uploaded BRDs.');
      return [];
    } finally {
      setBrdListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProcessedBrds();
  }, [loadProcessedBrds]);

  useEffect(() => {
    setStreamProcessingResult(null);
    const pendingStage = pendingRestoreStageRef.current;
    pendingRestoreStageRef.current = '';
    if (pendingStage) {
      setWorkflowStageOverride(pendingStage);
    } else {
      setWorkflowStageOverride('');
    }
    setFeatureRunResults({});
    setFeatureSaveResponse(null);
  }, [session?.id]);

  useEffect(() => {
    setSelectedStepIds((current) => {
      const next = current.filter((stepId) => selectableStepIds.includes(stepId));
      const unchanged = next.length === current.length && next.every((stepId, index) => stepId === current[index]);
      return unchanged ? current : next;
    });
  }, [selectableStepIds]);

  useEffect(() => {
    activeSessionIdRef.current = String(session?.id || '').trim();
  }, [session?.id]);

  useEffect(() => {
    selectedBrdIdRef.current = String(selectedBrdId || '').trim();
  }, [selectedBrdId]);

  useEffect(() => {
    selectedWorkspaceIdRef.current = String(selectedWorkspaceId || '').trim();
  }, [selectedWorkspaceId]);

  useEffect(() => {
    const normalizedBrdId = String(selectedBrdId || '').trim();
    const pendingRestoredSection = pendingRestoredBrdSectionRef.current;
    const pendingSectionNumber = (
      String(pendingRestoredSection?.pdfId || '').trim() === normalizedBrdId
        ? String(pendingRestoredSection?.sectionNumber || '').trim()
        : ''
    );
    if (
      pendingRestoredSection?.pdfId
      && String(pendingRestoredSection.pdfId).trim() !== normalizedBrdId
    ) {
      pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
    }
    setBrdSections([]);
    setSelectedBrdSectionNumber(pendingSectionNumber);
    setBrdSectionsError('');
    if (!normalizedBrdId) {
      pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
      setBrdSectionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const sectionLoadIsCurrent = () => (
      !cancelled
      && String(selectedBrdIdRef.current || '').trim() === normalizedBrdId
    );
    const loadSections = async () => {
      setBrdSectionsLoading(true);
      let recoveredMissingIndex = false;
      try {
        let payload;
        try {
          payload = await requestJson(
            `${API_ENDPOINTS.SECTIONS}/${encodeURIComponent(normalizedBrdId)}`
          );
        } catch (error) {
          if (!isMissingStoredBrdSectionsError(error)) throw error;
          if (!sectionLoadIsCurrent()) return;
          try {
            payload = await requestJson(
              `${API_ENDPOINTS.EXTRACT_SECTIONS}/${encodeURIComponent(normalizedBrdId)}?extraction_mode=auto`,
              { method: 'POST' }
            );
            recoveredMissingIndex = true;
          } catch (recoveryError) {
            throw new Error(
              `Stored sections were unavailable and standard BRD indexing could not recover them: ${
                recoveryError?.message || 'unknown error'
              }`
            );
          }
        }
        if (!sectionLoadIsCurrent()) return;

        const responsePdfId = String(
          payload?.pdf_id || payload?.brd_id || normalizedBrdId
        ).trim();
        if (responsePdfId !== normalizedBrdId) {
          throw new Error('The stored section response did not match the selected BRD.');
        }

        const loadedSections = storedBrdSectionsFromPayload(payload);
        if (!loadedSections.length && selectedBrdStoredSectionCount > 0) {
          throw new Error(
            `This BRD is marked as indexed with ${selectedBrdStoredSectionCount} section${
              selectedBrdStoredSectionCount === 1 ? '' : 's'
            }, but no stored sections were returned. Refresh the BRD list and retry.`
          );
        }

        setBrdSections(loadedSections);
        const currentPending = pendingRestoredBrdSectionRef.current;
        if (String(currentPending?.pdfId || '').trim() === normalizedBrdId) {
          const restoredSectionNumber = String(currentPending?.sectionNumber || '').trim();
          const restoredSectionExists = Boolean(restoredSectionNumber) && loadedSections.some((section) => (
            String(section?.number ?? section?.section_number ?? '').trim() === restoredSectionNumber
            || String(section?.section_number ?? section?.number ?? '').trim() === restoredSectionNumber
          ));
          setSelectedBrdSectionNumber(restoredSectionExists ? restoredSectionNumber : '');
          pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
        }
        if (recoveredMissingIndex) {
          setStatusMessage(
            `${loadedSections.length} stored BRD section${
              loadedSections.length === 1 ? '' : 's'
            } recovered using the standard indexing flow.`
          );
        }
      } catch (error) {
        if (!sectionLoadIsCurrent()) return;
        setBrdSections([]);
        if (
          String(pendingRestoredBrdSectionRef.current?.pdfId || '').trim()
          === normalizedBrdId
        ) {
          pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
          setSelectedBrdSectionNumber('');
        }
        setBrdSectionsError(
          error?.message
          || 'The stored sections for this indexed BRD could not be loaded.'
        );
      } finally {
        if (sectionLoadIsCurrent()) setBrdSectionsLoading(false);
      }
    };

    loadSections();
    return () => {
      cancelled = true;
    };
  }, [brdSectionsReloadToken, selectedBrdId, selectedBrdStoredSectionCount]);

  const runAction = async (actionName, task) => {
    setBusyAction(actionName);
    setErrorMessage('');
    try {
      return await task();
    } catch (error) {
      setErrorMessage(error.message || String(error));
    } finally {
      setBusyAction('');
    }
  };

  useEffect(() => {
    const sessionId = session?.id;
    if (!sessionId || typeof window === 'undefined') return undefined;
    const jobId = window.sessionStorage.getItem(featureCaseJobStorageKey(sessionId));
    if (!jobId || activeCaseMonitorRef.current === jobId) return undefined;
    let stopped = false;
    activeCaseMonitorRef.current = jobId;
    setActiveCaseJobId(jobId);
    setBusyAction('cases');
    const initialResult = {
      id: sessionId,
      source_type: 'feature_capture',
      status: 'running',
      stream_status: 'running',
      test_cases: buildInitialStreamTestCases('Captured Feature', null),
    };
    setStreamProcessingResult(initialResult);
    waitForFeatureCaseJob(jobId, sessionId,
      (row) => applyFeatureCaseProgressEvent(row, setStreamProcessingResult, initialResult),
      () => stopped)
      .then((result) => {
        if (stopped || !result) return;
        setSession(result);
        setStreamProcessingResult(null);
        setStatusMessage(`${result.test_cases?.length || 0} test case(s) generated.`);
        window.sessionStorage.removeItem(featureCaseJobStorageKey(sessionId));
      })
      .catch((error) => {
        if (stopped) return;
        if (error.terminalJob) window.sessionStorage.removeItem(featureCaseJobStorageKey(sessionId));
        setStreamProcessingResult((current) => markStreamResultFailed(current || initialResult, error.message));
        setErrorMessage(error.message);
      })
      .finally(() => {
        if (stopped) return;
        activeCaseMonitorRef.current = '';
        setActiveCaseJobId('');
        setBusyAction('');
      });
    return () => { stopped = true; };
  }, [session?.id]);

  const ensureFeatureCaptureExtensionReady = async () => {
    const bridgeCheck = await sendExtensionBridgeMessage({
      type: 'lsq-feature-capture-ping',
    }, 5000);
    if (bridgeCheck?.ok === false) {
      throw new Error(bridgeCheck.error || 'Feature capture extension is not available. Reload the extension, refresh TestSuite AI, and try again.');
    }
    return bridgeCheck;
  };

  const handleWorkspaceSelectionChange = (nextWorkspaceId) => {
    if (session?.id || brdIndexing) return;
    const normalizedWorkspaceId = String(nextWorkspaceId || '').trim();
    if (normalizedWorkspaceId === String(selectedWorkspaceId || '').trim()) return;

    invalidateBrdIndexingRequest();
    updateSelectedWorkspaceId(normalizedWorkspaceId);
    setSelectedApplicationId('');
    pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
    updateSelectedBrdId('');
    setSelectedBrdSectionNumber('');
    setBrdSections([]);
    setBrdSectionsLoading(false);
    setBrdSectionsError('');
    setBrdIndexing(false);
    setBrdIndexingError('');
    setBrdIndexedFileName('');
    setShowBrdPicker(false);
  };

  const openApplicationDashboard = (application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId) return;
    const workspaceId = String(application?.application_id || application?.application?.id || application?.workspace?.id || '').trim();
    setSelectedDashboardApplicationId(applicationId);
    setSelectedApplicationId(applicationId);
    setApplicationFeatureSearch('');
    setFeatureShareActionError('');
    setFeatureSharingStatus(null);
    setFeatureSharingRequests([]);
    setRenamingApplicationId('');
    setApplicationRenameValue('');
    setApplicationRenameError('');
    setRenamingFeatureId('');
    setFeatureRenameValue('');
    setFeatureRenameError('');
    if (workspaceId) updateSelectedWorkspaceId(workspaceId);
    if (!session?.id && application?.base_url) setApplicationUrl(application.base_url);
    setCaptureDashboardView('application');
    navigateFeatureRoute(buildFeatureCaptureRoute({ applicationId }));
  };

  const returnToApplicationsDashboard = () => {
    setCaptureDashboardView('applications');
    setSelectedDashboardApplicationId('');
    setApplicationFeatureSearch('');
    setApplicationFeatureError('');
    setApplicationFeatureItems([]);
    setRenamingApplicationId('');
    setApplicationRenameValue('');
    setApplicationRenameError('');
    setRenamingFeatureId('');
    setFeatureRenameValue('');
    setFeatureRenameError('');
    getFeatureSessionStorage()?.removeItem(FEATURE_CAPTURE_LAST_ROUTE_KEY);
    navigateFeatureRoute(FEATURE_CAPTURE_ROUTE_ROOT);
  };

  const returnToActiveApplicationDashboard = () => {
    const applicationId = String(
      selectedDashboardApplicationId
      || selectedApplicationId
      || session?.feature_area_id
      || session?.feature_area?.id
      || ''
    ).trim();

    setWorkflowStageOverride('');
    setSelectedStepIds([]);

    if (!applicationId) {
      returnToApplicationsDashboard();
      return;
    }

    const applicationLabel = (
      selectedDashboardApplication?.name
      || selectedApplication?.name
      || session?.feature_area?.name
      || 'application'
    );
    setSelectedDashboardApplicationId(applicationId);
    setSelectedApplicationId(applicationId);
    setApplicationFeatureSearch('');
    setFeatureShareActionError('');
    setFeatureSharingStatus(null);
    setFeatureSharingRequests([]);
    setRenamingFeatureId('');
    setFeatureRenameValue('');
    setFeatureRenameError('');
    setCaptureDashboardView('application');
    navigateFeatureRoute(buildFeatureCaptureRoute({ applicationId }));
    loadApplicationFeatures(applicationId);
    loadApplicationDashboard();
    setStatusMessage(`Back to ${applicationLabel} features.`);
  };

  const startFeatureCaptureForApplication = (application = selectedDashboardApplication || selectedApplication) => {
    if (isBusy || brdIndexing) return;
    const applicationId = String(application?.id || '').trim();
    const workspaceId = String(application?.application_id || application?.application?.id || application?.workspace?.id || '').trim();
    if (!applicationId || !workspaceId) return;

    invalidateBrdIndexingRequest();
    activeSessionIdRef.current = '';
    pendingRestoreStageRef.current = '';
    pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
    setSession(null);
    setFeatureName('');
    setApplicationUrl(String(application?.base_url || '').trim());
    setNotes('');
    updateSelectedWorkspaceId(workspaceId);
    setSelectedApplicationId(applicationId);
    setSelectedDashboardApplicationId(applicationId);
    updateSelectedBrdId('');
    setSelectedBrdSectionNumber('');
    setBrdSections([]);
    setBrdSectionsError('');
    setBrdContextFile(null);
    setBrdContextText('');
    setBrdFileError('');
    setBrdIndexingError('');
    setBrdIndexedFileName('');
    setWorkflowStageOverride('capture');
    setFeatureRunResults({});
    setFeatureSaveResponse(null);
    setStreamProcessingResult(null);
    setSelectedStepIds([]);
    setFeatureNameError('');
    setRenamingApplicationId('');
    setApplicationRenameValue('');
    setApplicationRenameError('');
    setRenamingFeatureId('');
    setFeatureRenameValue('');
    setFeatureRenameError('');
    setShowBrdPicker(false);
    setErrorMessage('');
    setCaptureDashboardView('workflow');
    getFeatureSessionStorage()?.removeItem(FEATURE_CAPTURE_LAST_ROUTE_KEY);
    navigateFeatureRoute(FEATURE_CAPTURE_ROUTE_ROOT);
    setStatusMessage(`${application?.name || 'Application'} selected. Add the feature target and start capture.`);
  };

  const handleToggleStepSelection = (stepId) => {
    setSelectedStepIds((current) => (
      current.includes(stepId)
        ? current.filter((selectedId) => selectedId !== stepId)
        : [...current, stepId]
    ));
  };

  // The steps table can be filtered or paged, so it passes the ids it is actually
  // showing; without them the toggle covers every captured step.
  const handleToggleAllStepSelection = (stepIds) => {
    const scope = Array.isArray(stepIds) && stepIds.length ? stepIds : selectableStepIds;
    setSelectedStepIds((current) => {
      const allSelected = scope.length > 0 && scope.every((stepId) => current.includes(stepId));
      if (allSelected) return current.filter((stepId) => !scope.includes(stepId));
      return Array.from(new Set([...current, ...scope]));
    });
  };

  const handleRemoveSelectedSteps = (requestedStepIds = selectedStepIds) => runAction('removeSteps', async () => {
    const candidateStepIds = Array.isArray(requestedStepIds) ? requestedStepIds : selectedStepIds;
    if (!session?.id || !candidateStepIds.length) return;
    const stepIdsToRemove = candidateStepIds.filter((stepId) => selectableStepIds.includes(stepId));
    if (!stepIdsToRemove.length) {
      setSelectedStepIds([]);
      return;
    }
    if (!window.confirm(`Remove ${stepIdsToRemove.length} selected step(s) from this captured flow?`)) {
      return;
    }
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_REMOVE_STEPS(session.id), {
      method: 'POST',
      body: { step_ids: stepIdsToRemove },
    });
    setSession(payload);
    setSelectedStepIds([]);
    setStatusMessage(`${stepIdsToRemove.length} step(s) removed. Review the updated flow before generating again.`);
  });

  const handleUpdateCapturedStep = (stepId, updates) => runAction('updateStep', async () => {
    if (!session?.id || !stepId) return;
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_STEP(session.id, stepId), {
      method: 'PATCH',
      body: updates,
    });
    setSession(payload);
    setStatusMessage('Captured step updated. Generated outputs will use the revised step.');
    return true;
  });

  const handleUseJourneyVariable = (stepId, variableId) => runAction('valueSource', async () => {
    if (!session?.id || !stepId) return;
    const payload = await requestJson(
      API_ENDPOINTS.FEATURE_CAPTURE_STEP_VALUE_SOURCE(session.id, stepId),
      {
        method: 'PUT',
        body: variableId
          ? { kind: 'variable', variable_id: variableId }
          : { kind: 'literal' },
      },
    );
    setSession(payload);
    const variableName = journeyVariableNamesById(journeyVariablesFromSession(payload)).get(variableId);
    setStatusMessage(variableId
      ? `Step now uses ${variableName || 'the captured value'} read at run time.`
      : 'Step uses test data again.');
  });

  const handleApplySuggestedMappings = (mappings) => runAction('valueSource', async () => {
    if (!session?.id || !Array.isArray(mappings) || !mappings.length) return;
    let payload = null;
    for (const mapping of mappings) {
      payload = await requestJson(
        API_ENDPOINTS.FEATURE_CAPTURE_STEP_VALUE_SOURCE(session.id, mapping.stepId),
        {
          method: 'PUT',
          body: { kind: 'variable', variable_id: mapping.variableId },
        },
      );
    }
    if (payload) setSession(payload);
    setStatusMessage(`${mappings.length} suggested ${mappings.length === 1 ? 'mapping' : 'mappings'} applied on this page.`);
  });

  const handleRenameJourneyVariable = (variable) => runAction('renameVariable', async () => {
    if (!session?.id || !variable?.id) return;
    const nextName = window.prompt(`Rename ${variable.name} to:`, variable.name);
    if (!nextName || nextName.trim() === variable.name) return;
    const payload = await requestJson(
      API_ENDPOINTS.FEATURE_CAPTURE_VARIABLE(session.id, variable.id),
      { method: 'PATCH', body: { name: nextName.trim() } },
    );
    setSession(payload);
    setStatusMessage(`Captured value renamed. Regenerate scripts so they use the new name.`);
  });

  const handleChangeJourneyVariableRowRule = (variable, rowRuleType) => runAction('variableRowRule', async () => {
    if (!session?.id || !variable?.id) return;
    const capturedIndex = Number(variable?.source?.row_rule?.index || 0);
    const payload = await requestJson(
      API_ENDPOINTS.FEATURE_CAPTURE_VARIABLE(session.id, variable.id),
      {
        method: 'PATCH',
        body: {
          row_rule: rowRuleType === 'captured'
            ? { type: 'captured', index: capturedIndex }
            : { type: rowRuleType },
        },
      },
    );
    setSession(payload);
    setStatusMessage(`${variable.name} now reads the ${rowRuleType === 'captured' ? 'captured' : rowRuleType} row.`);
  });

  const handleDeleteJourneyVariable = (variable) => runAction('deleteVariable', async () => {
    if (!session?.id || !variable?.id) return;
    if (!window.confirm(
      `Delete ${variable.name}? The step that captured it is removed and any step using it goes back to test data.`
    )) {
      return;
    }
    const payload = await requestJson(
      API_ENDPOINTS.FEATURE_CAPTURE_VARIABLE(session.id, variable.id),
      { method: 'DELETE' },
    );
    setSession(payload);
    setStatusMessage('Captured value deleted.');
  });

  const handleCreateWorkspace = async () => {
    const normalizedName = String(newWorkspaceName || '').trim();
    if (!canCreateWorkspace || !normalizedName) return;
    setCreatingWorkspace(true);
    setWorkspaceError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.APPLICATIONS, {
        method: 'POST',
        body: { name: normalizedName, source_type: 'application' },
      });
      const workspace = payload?.application || payload?.workspace || null;
      const latest = await loadWorkspaces();
      const nextWorkspaceId = workspace?.id || latest?.[0]?.id || '';
      if (nextWorkspaceId && !session?.id) handleWorkspaceSelectionChange(nextWorkspaceId);
      if (nextWorkspaceId) {
        setDashboardCreateApplication((current) => (
          current.application_id ? current : { ...current, application_id: nextWorkspaceId }
        ));
        setDashboardCreateApplicationError('');
      }
      setNewWorkspaceName('');
      return nextWorkspaceId;
    } catch (error) {
      setWorkspaceError(error.message || 'Failed to create application');
      return '';
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const openCreateApplicationPanel = () => {
    setDashboardCreateApplication({
      name: '',
      application_id: effectiveDashboardWorkspaceId,
    });
    setDashboardCreateApplicationError('');
    setShowCreateApplicationPanel(true);
  };

  useEffect(() => {
    if (!showCreateApplicationPanel || !effectiveDashboardWorkspaceId) return;
    setDashboardCreateApplication((current) => {
      const currentWorkspaceId = String(current.application_id || '').trim();
      if (currentWorkspaceId === effectiveDashboardWorkspaceId) return current;
      const currentStillExists = workspaces.some((workspace) => (
        String(workspace?.id || '').trim() === currentWorkspaceId
      ));
      if (currentStillExists && !dashboardWorkspaceFilter) return current;
      return { ...current, application_id: effectiveDashboardWorkspaceId };
    });
  }, [
    dashboardWorkspaceFilter,
    effectiveDashboardWorkspaceId,
    showCreateApplicationPanel,
    workspaces,
  ]);

  const closeCreateApplicationPanel = () => {
    if (creatingDashboardApplication) return;
    setShowCreateApplicationPanel(false);
    setDashboardCreateApplicationError('');
  };

  const handleCreateDashboardApplication = async () => {
    const normalizedName = String(dashboardCreateApplication.name || '').trim();
    const normalizedWorkspaceId = String(
      dashboardCreateApplication.application_id
      || effectiveDashboardWorkspaceId
      || ''
    ).trim();

    if (!normalizedName) {
      setDashboardCreateApplicationError('Feature area name is required.');
      return;
    }
    if (!normalizedWorkspaceId) {
      setDashboardCreateApplicationError('Select an application for this feature area.');
      return;
    }
    if (applicationNameExistsInWorkspace(normalizedName, normalizedWorkspaceId)) {
      setDashboardCreateApplicationError('Feature area name already exists in this application.');
      return;
    }

    setCreatingDashboardApplication(true);
    setDashboardCreateApplicationError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.FEATURE_AREAS, {
        method: 'POST',
        body: {
          name: normalizedName,
          application_id: normalizedWorkspaceId,
          base_url: '',
          environment_name: 'Default',
        },
      });
      const application = payload?.feature_area || payload?.application || null;
      const rows = await loadApplicationDashboard();
      if (selectedWorkspaceId) {
        await loadApplications(selectedWorkspaceId);
      }
      const createdApplication = application || rows.find((row) => (
        String(row?.name || '').trim() === normalizedName
        && String(row?.application_id || row?.application?.id || row?.workspace?.id || '').trim() === normalizedWorkspaceId
      ));
      setShowCreateApplicationPanel(false);
      setDashboardCreateApplication({ name: '', application_id: '' });
      if (createdApplication?.id) {
        openApplicationDashboard(createdApplication);
      }
      setStatusMessage(`Application ${createdApplication?.name || normalizedName} created.`);
    } catch (error) {
      setDashboardCreateApplicationError(error.message || 'Failed to create application.');
    } finally {
      setCreatingDashboardApplication(false);
    }
  };

  const loadWorkspaceMembers = useCallback(async (workspaceId = selectedWorkspaceId) => {
    const normalizedWorkspaceId = String(workspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      setWorkspaceMembers([]);
      return [];
    }

    setWorkspaceMembersLoading(true);
    setWorkspaceMembersError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.APPLICATION_MEMBERS(normalizedWorkspaceId));
      const members = Array.isArray(payload?.members) ? payload.members : [];
      setWorkspaceMembers(members);
      return members;
    } catch (error) {
      setWorkspaceMembersError(error.message || 'Failed to load application members');
      return [];
    } finally {
      setWorkspaceMembersLoading(false);
    }
  }, [selectedWorkspaceId]);

  const loadActiveUsers = useCallback(async () => {
    setActiveUsersLoading(true);
    setActiveUsersError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.USERS);
      const users = Array.isArray(payload?.users) ? payload.users : [];
      setActiveUsers(users);
      return users;
    } catch (error) {
      setActiveUsers([]);
      setActiveUsersError(error.message || 'Failed to load active users');
      return [];
    } finally {
      setActiveUsersLoading(false);
    }
  }, []);

  const openWorkspaceMembersManager = async () => {
    if (!selectedWorkspaceId) return;
    setShowWorkspaceMembersModal(true);
    await loadWorkspaceMembers(selectedWorkspaceId);
    if (canManageWorkspaceMembers) {
      await loadActiveUsers();
    }
  };

  const addWorkspaceMember = async ({ email, user_id = null, role = null }) => {
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      throw new Error('Select an application first');
    }
    await requestJson(API_ENDPOINTS.APPLICATION_MEMBERS(normalizedWorkspaceId), {
      method: 'POST',
      body: {
        ...(email ? { email } : {}),
        ...(user_id ? { user_id } : {}),
        ...(role ? { role } : {}),
      },
    });
    await loadWorkspaceMembers(normalizedWorkspaceId);
    await loadWorkspaces();
  };

  const removeWorkspaceMember = async (memberUserId, displayLabel = '') => {
    const normalizedWorkspaceId = String(selectedWorkspaceId || '').trim();
    const normalizedMemberId = String(memberUserId || '').trim();
    if (!normalizedWorkspaceId || !normalizedMemberId) return;

    await requestJson(API_ENDPOINTS.APPLICATION_MEMBER_BY_ID(normalizedWorkspaceId, normalizedMemberId), {
      method: 'DELETE',
    });
    await loadWorkspaceMembers(normalizedWorkspaceId);
    await loadWorkspaces();
  };

  const handleOpenBrdPicker = () => {
    if (!canManageCaptureBrd) {
      setStatusMessage('Only the capture owner can change BRD context or linked sections.');
      return;
    }
    setShowBrdPicker(true);
    setBrdSearchQuery('');
    setSectionSearchQuery('');
    if (!processedBrds.length && !brdListLoading) {
      loadProcessedBrds();
    }
  };

  const clearBrdContext = () => {
    if (!canManageCaptureBrd) {
      setStatusMessage('Only the capture owner can change BRD context or linked sections.');
      return;
    }
    invalidateBrdIndexingRequest();
    pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
    updateSelectedBrdId('');
    setSelectedBrdSectionNumber('');
    setBrdContextFile(null);
    setBrdContextText('');
    setBrdFileError('');
    setBrdIndexingError('');
    setBrdIndexedFileName('');
    setBrdIndexing(false);
  };

  const handleBrdScopeChange = (nextPdfId) => {
    if (!canManageCaptureBrd) {
      setStatusMessage('Only the capture owner can change BRD context or linked sections.');
      return;
    }
    const normalizedPdfId = String(nextPdfId || '').trim();
    invalidateBrdIndexingRequest();
    pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
    updateSelectedBrdId(normalizedPdfId);
    setSelectedBrdSectionNumber('');
    setBrdSectionsError('');
    if (normalizedPdfId) {
      // Re-selecting the same indexed BRD is an explicit retry of its stored
      // section read. It never starts extraction unless the server reports
      // the canonical "sections not yet extracted" condition.
      setBrdSectionsReloadToken((current) => current + 1);
    } else {
      setBrdSections([]);
      setBrdSectionsLoading(false);
    }
    setBrdIndexing(false);
    setBrdIndexingError('');
    setBrdIndexedFileName('');
  };

  const confirmSelectedBrdSection = (section = selectedBrdSection) => {
    if (!canManageCaptureBrd) {
      setStatusMessage('Only the capture owner can change BRD context or linked sections.');
      return;
    }
    if (!selectedBrd || !section) {
      setStatusMessage('Choose one BRD section for this feature flow.');
      return;
    }
    const sectionNumber = String(section?.number ?? section?.section_number ?? '').trim();
    if (sectionNumber) setSelectedBrdSectionNumber(sectionNumber);
    setShowBrdPicker(false);
    setStatusMessage(
      `${selectedBrd.pdf_file || selectedBrd.pdf_id} / ${brdSectionLabel(section)} selected as BRD context.`
    );
  };

  const applyFeatureSessionToWorkspace = useCallback((payload, { fromHistory = false, stageOverride = '' } = {}) => {
    if (!payload?.id) return null;

    const brdContext = payload.brd_context && typeof payload.brd_context === 'object'
      ? payload.brd_context
      : {};
    const selectedSection = brdContext.selected_section && typeof brdContext.selected_section === 'object'
      ? brdContext.selected_section
      : null;
    const selectedSections = Array.isArray(brdContext.selected_sections) && brdContext.selected_sections.length
      ? brdContext.selected_sections
      : selectedSection
        ? [selectedSection]
        : [];
    const primarySection = selectedSections[0] || selectedSection || null;
    const workspaceId = String(payload.application_id || payload.application?.id || payload.workspace?.id || '').trim();
    if (
      isApplicationRouteMode
      && workspaceId
      && workspaceId !== lockedApplicationId
    ) {
      throw new Error('This captured feature belongs to a different application.');
    }
    const applicationId = String(payload.feature_area_id || payload.feature_area?.id || '').trim();
    const nextBrdId = String(primarySection?.pdf_id || '').trim();
    const restoredSectionNumber = String(
      selectedSection?.section_number || selectedSections[0]?.section_number || ''
    ).trim();
    const restoredSectionPdfId = String(
      selectedSection?.pdf_id || selectedSections[0]?.pdf_id || ''
    ).trim();
    const canRestoreSection = Boolean(
      nextBrdId
      && restoredSectionNumber
      && (!restoredSectionPdfId || restoredSectionPdfId === nextBrdId)
    );

    invalidateBrdIndexingRequest();
    activeSessionIdRef.current = String(payload.id || '').trim();
    setSession(payload);
    setFeatureName(String(payload.feature_name || '').trim());
    setApplicationUrl(String(payload.start_url || '').trim());
    setNotes(String(payload.notes || '').trim());
    setScriptNotes(String(payload.script_generation_notes || ''));
    if (workspaceId) updateSelectedWorkspaceId(workspaceId);
    if (applicationId) setSelectedApplicationId(applicationId);
    pendingRestoredBrdSectionRef.current = canRestoreSection
      ? { pdfId: nextBrdId, sectionNumber: restoredSectionNumber }
      : { pdfId: '', sectionNumber: '' };
    updateSelectedBrdId(nextBrdId);
    setBrdSectionsReloadToken((current) => current + 1);
    setSelectedBrdSectionNumber(canRestoreSection ? restoredSectionNumber : '');
    setBrdContextText(String(brdContext.text || '').trim());
    setBrdContextFile(null);
    setBrdFileError('');
    setBrdIndexingError('');
    setBrdIndexedFileName('');
    setStreamProcessingResult(null);
    setWorkflowStageOverride(stageOverride);
    setFeatureRunResults({});
    setSelectedStepIds([]);
    setBrdIndexing(false);
    if (fromHistory) {
      setStatusMessage(`Loaded ${payload.feature_name || 'captured feature'} from history.`);
    }
    return payload;
  }, [
    invalidateBrdIndexingRequest,
    isApplicationRouteMode,
    lockedApplicationId,
    updateSelectedBrdId,
    updateSelectedWorkspaceId,
  ]);

  useEffect(() => {
    if (!parsedFeatureRoute.isFeatureCaptureRoute || location.search) return undefined;

    if (parsedFeatureRoute.routeKind === 'dashboard') {
      setWorkflowStageOverride('');
      setSelectedStepIds([]);
      setCaptureDashboardView(
        isApplicationRouteMode && selectedDashboardApplicationId ? 'application' : 'applications'
      );
      return undefined;
    }

    if (parsedFeatureRoute.routeKind === 'new') {
      setCaptureDashboardView('workflow');
      setWorkflowStageOverride('capture');
      updateFeatureRouteHydration(
        `new:${parsedFeatureRoute.rootApplicationId || lockedApplicationId}`,
        false
      );
      return undefined;
    }

    if (parsedFeatureRoute.routeKind === 'application') {
      const targetApplicationId = String(parsedFeatureRoute.applicationId || '').trim();
      if (!targetApplicationId) return undefined;

      const signature = `application:${targetApplicationId}`;
      if (featureRouteHandledRef.current === signature) {
        updateFeatureRouteHydration(signature, false);
        return undefined;
      }

      featureRouteHandledRef.current = signature;
      updateFeatureRouteHydration(signature, true);
      setSelectedDashboardApplicationId(targetApplicationId);
      setSelectedApplicationId(targetApplicationId);
      setApplicationFeatureSearch('');
      setApplicationFeatureError('');
      setFeatureShareActionError('');
      setFeatureSharingStatus(null);
      setFeatureSharingRequests([]);
      setRenamingFeatureId('');
      setFeatureRenameValue('');
      setFeatureRenameError('');
      setCaptureDashboardView('application');

      let cancelled = false;
      loadApplicationFeatures(targetApplicationId)
        .catch((error) => {
          if (!cancelled) {
            setApplicationFeatureError(error.message || 'Failed to load features for this feature area.');
          }
        })
        .finally(() => {
          if (!cancelled) {
            updateFeatureRouteHydration(signature, false);
          }
        });

      return () => {
        cancelled = true;
        updateFeatureRouteHydration(signature, false);
      };
    }

    if (parsedFeatureRoute.routeKind !== 'workflow') return undefined;

    const targetSessionId = String(parsedFeatureRoute.sessionId || '').trim();
    if (!targetSessionId) return undefined;
    const targetStageId = normalizeFeatureCaptureStageId(parsedFeatureRoute.stageId, 'capture');
    const signature = `workflow:${targetSessionId}:${targetStageId}`;
    const currentSessionId = String(session?.id || '').trim();

    if (currentSessionId === targetSessionId) {
      if (featureRouteHandledRef.current !== signature) {
        featureRouteHandledRef.current = signature;
        setCaptureDashboardView('workflow');
        const restoredNotes = String(session?.notes || '').trim();
        if (!String(notes || '').trim() && restoredNotes) {
          setNotes(restoredNotes);
        }
        if (currentStage.id !== targetStageId) {
          setWorkflowStageOverride(targetStageId);
        }
      }
      updateFeatureRouteHydration(signature, false);
      return undefined;
    }

    const alreadyOnTargetWorkflow =
      currentSessionId === targetSessionId
      && captureDashboardView === 'workflow'
      && currentStage.id === targetStageId;
    if (featureRouteHandledRef.current === signature && alreadyOnTargetWorkflow) {
      return undefined;
    }

    featureRouteHandledRef.current = signature;
    updateFeatureRouteHydration(signature, true);
    pendingRestoreStageRef.current = targetStageId;
    setFeatureHistoryRestoreId(targetSessionId);
    setErrorMessage('');
    setFeatureHistoryError('');
    setStatusMessage('Opening captured feature...');

    let cancelled = false;
    const run = async () => {
      try {
        const fastOpen = ['scripts', 'execution'].includes(targetStageId);
        const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(targetSessionId, { fast: fastOpen }));
        if (cancelled) return;
        setSelectedFeatureHistoryId(targetSessionId);
        applyFeatureSessionToWorkspace(payload, { fromHistory: true, stageOverride: targetStageId });
        setSelectedDashboardApplicationId(flowApplicationId(payload));
        setCaptureDashboardView('workflow');
        const stageTitle = workflowStages.find((stage) => stage.id === targetStageId)?.title || 'Capture';
        setStatusMessage(`Loaded ${payload.feature_name || 'captured feature'}. Viewing ${stageTitle}.`);
        setShowFeatureHistory(false);
      } catch (error) {
        if (cancelled) return;
        pendingRestoreStageRef.current = '';
        setFeatureHistoryError(error.message || 'Failed to load this captured feature.');
        setErrorMessage(error.message || 'Failed to load this captured feature.');
      } finally {
        if (!cancelled) {
          setFeatureHistoryRestoreId('');
          updateFeatureRouteHydration(signature, false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
      updateFeatureRouteHydration(signature, false);
    };
  }, [
    applyFeatureSessionToWorkspace,
    captureDashboardView,
    currentStage.id,
    isApplicationRouteMode,
    loadApplicationFeatures,
    lockedApplicationId,
    location.search,
    location.pathname,
    navigate,
    notes,
    parsedFeatureRoute.applicationId,
    parsedFeatureRoute.isFeatureCaptureRoute,
    parsedFeatureRoute.routeKind,
    parsedFeatureRoute.rootApplicationId,
    parsedFeatureRoute.sessionId,
    parsedFeatureRoute.stageId,
    selectedDashboardApplicationId,
    session?.id,
    session?.notes,
    updateFeatureRouteHydration,
  ]);

  useEffect(() => {
    if (!parsedFeatureRoute.isFeatureCaptureRoute || location.search || featureRouteHydratingRef.current) {
      return;
    }

    // Browser Back/Forward changes the route before React applies the matching stage.
    // Let the route-hydration effect above make that transition instead of rewriting
    // the URL from the one-render-old currentStage value.
    const routeSessionId = parsedFeatureRoute.routeKind === 'workflow'
      ? String(parsedFeatureRoute.sessionId || '').trim()
      : '';
    const routeStageId = routeSessionId
      ? normalizeFeatureCaptureStageId(parsedFeatureRoute.stageId, 'capture')
      : '';
    if (
      routeSessionId
      && routeSessionId === String(session?.id || '').trim()
      && (
        captureDashboardView !== 'workflow'
        || routeStageId !== currentStage.id
      )
    ) {
      return;
    }
    if (parsedFeatureRoute.routeKind === 'dashboard' && captureDashboardView === 'workflow') {
      return;
    }

    let expectedPath = isApplicationRouteMode
      ? buildFeatureCaptureRoute({
          rootApplicationId: lockedApplicationId,
          isNew: true,
        })
      : FEATURE_CAPTURE_ROUTE_ROOT;
    if (captureDashboardView === 'workflow' && session?.id) {
      expectedPath = buildFeatureCaptureRoute({
        rootApplicationId: isApplicationRouteMode ? lockedApplicationId : '',
        sessionId: session.id,
        stageId: currentStage.id,
      });
    } else if (captureDashboardView === 'application' && selectedDashboardApplicationId) {
      expectedPath = isApplicationRouteMode
        ? buildFeatureCaptureRoute({ rootApplicationId: lockedApplicationId })
        : buildFeatureCaptureRoute({ applicationId: selectedDashboardApplicationId });
    }

    const storage = getFeatureSessionStorage();
    if (expectedPath === FEATURE_CAPTURE_ROUTE_ROOT || isApplicationRouteMode) {
      storage?.removeItem(FEATURE_CAPTURE_LAST_ROUTE_KEY);
    } else {
      storage?.setItem(FEATURE_CAPTURE_LAST_ROUTE_KEY, expectedPath);
    }

    const currentPath = `${location.pathname}${location.search || ''}`;
    if (currentPath !== expectedPath) {
      navigate(expectedPath);
    }
  }, [
    captureDashboardView,
    currentStage.id,
    location.pathname,
    location.search,
    navigate,
    isApplicationRouteMode,
    lockedApplicationId,
    parsedFeatureRoute.isFeatureCaptureRoute,
    parsedFeatureRoute.routeKind,
    parsedFeatureRoute.sessionId,
    parsedFeatureRoute.stageId,
    selectedDashboardApplicationId,
    session?.id,
  ]);

  const handleFeatureBrdFileChange = async (file) => {
    setBrdFileError('');
    setBrdIndexingError('');
    setBrdIndexedFileName('');
    if (!canManageCaptureBrd) {
      setBrdIndexingError('Only the capture owner can upload or change BRD context.');
      return;
    }
    if (!file) {
      setBrdContextFile(null);
      return;
    }

    const extension = String(file.name || '').split('.').pop()?.toLowerCase() || '';
    const shouldIndex = ['pdf', 'doc', 'docx'].includes(extension);
    if (shouldIndex) {
      if (!canUploadCaptureBrd) {
        setBrdIndexingError('You need BRD upload permission in this application to upload and index a document.');
        return;
      }
      const workspaceId = String(selectedWorkspaceId || '').trim();
      if (!workspaceId) {
        setBrdIndexingError('Select an application before uploading and indexing this BRD.');
        return;
      }

      const indexingGeneration = Number(brdIndexingRequestRef.current?.generation || 0) + 1;
      brdIndexingRequestRef.current = {
        generation: indexingGeneration,
        workspaceId,
      };
      const indexingRequestIsCurrent = () => (
        Number(brdIndexingRequestRef.current?.generation || 0) === indexingGeneration
        && String(brdIndexingRequestRef.current?.workspaceId || '').trim() === workspaceId
        && String(selectedWorkspaceIdRef.current || '').trim() === workspaceId
      );

      setBrdIndexing(true);
      setBrdIndexedFileName(file.name || 'BRD');
      setBrdContextFile(null);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await apiFetch(
          `${API_ENDPOINTS.UPLOAD_PDF}?application_id=${encodeURIComponent(workspaceId)}`,
          { method: 'POST', body: formData }
        );
        const uploadPayload = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          throw new Error(
            uploadPayload?.detail
            || uploadPayload?.error
            || `BRD upload failed with status ${uploadResponse.status}`
          );
        }
        if (!indexingRequestIsCurrent()) return;

        const pdfId = String(uploadPayload?.pdf_id || uploadPayload?.brd_id || '').trim();
        if (!pdfId) throw new Error('The uploaded BRD did not return a document id.');

        const extractionResponse = await apiFetch(
          `${API_ENDPOINTS.EXTRACT_SECTIONS}/${encodeURIComponent(pdfId)}?extraction_mode=auto`,
          { method: 'POST', headers: { Accept: 'application/json' } }
        );
        const extractionPayload = await extractionResponse.json().catch(() => ({}));
        if (!extractionResponse.ok) {
          throw new Error(
            extractionPayload?.detail
            || extractionPayload?.error
            || `BRD indexing failed with status ${extractionResponse.status}`
          );
        }
        if (!indexingRequestIsCurrent()) return;

        pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
        updateSelectedBrdId(pdfId);
        setSelectedBrdSectionNumber('');
        setBrdSections(Array.isArray(extractionPayload?.sections) ? extractionPayload.sections : []);
        await loadProcessedBrds();
        if (!indexingRequestIsCurrent()) return;
        setStatusMessage(`${file.name || 'BRD'} uploaded and indexed. Choose a section if you need BRD context.`);
      } catch (error) {
        if (indexingRequestIsCurrent()) {
          setBrdIndexingError(error.message || 'Failed to upload and index this BRD.');
        }
      } finally {
        if (indexingRequestIsCurrent()) setBrdIndexing(false);
      }
      return;
    }

    if (file.size > MAX_CONTEXT_FILE_BYTES) {
      setBrdContextFile(null);
      setBrdFileError('Feature BRD must be 5 MB or smaller.');
      return;
    }
    setBrdContextFile(file);
  };

  const buildContextFilePayload = async () => {
    if (!brdContextFile) return null;
    const contentBase64 = await fileToBase64(brdContextFile);
    return {
      filename: brdContextFile.name,
      content_type: brdContextFile.type || '',
      size: brdContextFile.size,
      content_base64: contentBase64,
    };
  };

  const refreshSession = async (sessionId = session?.id, { quiet = false } = {}) => {
    if (!sessionId) return null;
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId));
    applyFeatureSessionToWorkspace(payload);
    if (!quiet) {
      setStatusMessage(`Flow refreshed. ${payload.normalized_steps?.length || 0} step(s) available.`);
    }
    return payload;
  };

  const loadFeatureSharingDetails = async (sessionId) => {
    const normalizedSessionId = String(sessionId || '').trim();
    if (!normalizedSessionId) return null;
    setFeatureSharingLoading(true);
    setFeatureSharingError('');
    try {
      const [statusPayload, requestsPayload] = await Promise.all([
        requestJson(API_ENDPOINTS.FEATURE_CAPTURE_SHARING_STATUS(normalizedSessionId)),
        requestJson(`${API_ENDPOINTS.FEATURE_CAPTURE_SHARING_REQUESTS(normalizedSessionId)}?status=pending`),
      ]);
      const sharing = statusPayload?.sharing || null;
      const requests = Array.isArray(requestsPayload?.requests) ? requestsPayload.requests : [];
      setFeatureSharingStatus(sharing);
      setFeatureSharingRequests(requests);
      return sharing;
    } catch (error) {
      setFeatureSharingStatus(null);
      setFeatureSharingRequests([]);
      setFeatureSharingError(error.message || 'Failed to load sharing details.');
      return null;
    } finally {
      setFeatureSharingLoading(false);
    }
  };

  const loadFeatureCaptureHistory = async () => {
    setShowFeatureHistory(true);
    setFeatureHistorySearch('');
    setFeatureHistoryLoading(true);
    setFeatureHistoryError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURES);
      const rows = Array.isArray(payload?.captures) ? payload.captures : [];
      const currentSessionId = String(session?.id || '').trim();
      const defaultItem = rows.find((item) => String(item?.id || '') === currentSessionId)
        || rows[0]
        || null;
      const defaultSelection = defaultItem?.id || '';
      setFeatureHistoryItems(rows);
      setSelectedHistoryApplicationId((current) => {
        const currentId = String(current || '').trim();
        if (currentId && rows.some((item) => flowApplicationId(item) === currentId)) {
          return currentId;
        }
        return defaultItem ? flowApplicationId(defaultItem) : '';
      });
      setSelectedFeatureHistoryId((current) => (
        rows.some((item) => String(item?.id || '') === String(current || ''))
          ? current
          : defaultSelection
      ));
      if (defaultSelection) {
        loadFeatureSharingDetails(defaultSelection);
      }
      return rows;
    } catch (error) {
      setFeatureHistoryItems([]);
      setSelectedFeatureHistoryId('');
      setSelectedHistoryApplicationId('');
      setFeatureHistoryError(error.message || 'Failed to load feature history.');
      return [];
    } finally {
      setFeatureHistoryLoading(false);
    }
  };

  const refreshFeatureHistoryList = async ({ preferredSessionId = '' } = {}) => {
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURES);
    const rows = Array.isArray(payload?.captures) ? payload.captures : [];
    const preferredId = String(preferredSessionId || '').trim();
    const currentId = String(selectedFeatureHistoryId || '').trim();
    const nextItem = (
      rows.find((item) => String(item?.id || '') === preferredId)
      || rows.find((item) => String(item?.id || '') === currentId)
      || rows[0]
      || null
    );
    setFeatureHistoryItems(rows);
    setSelectedFeatureHistoryId(nextItem?.id || '');
    setSelectedHistoryApplicationId(nextItem ? flowApplicationId(nextItem) : '');
    if (nextItem?.id) {
      loadFeatureSharingDetails(nextItem.id);
    } else {
      setFeatureSharingStatus(null);
      setFeatureSharingRequests([]);
    }
    return rows;
  };

  const refreshFeatureCaptureCollections = async ({
    applicationId = selectedDashboardApplicationId,
    refreshHistory = showFeatureHistory,
    preferredSessionId = selectedFeatureHistoryId,
  } = {}) => {
    const normalizedApplicationId = String(applicationId || '').trim();
    const tasks = [loadApplicationDashboard()];
    if (normalizedApplicationId) {
      tasks.push(loadApplicationFeatures(normalizedApplicationId));
    }
    if (refreshHistory) {
      tasks.push(refreshFeatureHistoryList({ preferredSessionId }));
    }
    const results = await Promise.all(tasks);
    return results;
  };

  const openFeatureSharingPanel = async (item) => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    setSelectedFeatureHistoryId(sessionId);
    setFeatureSharingPanelOpen(true);
    setFeatureShareActionError('');
    setFeatureShareUserId('');
    setFeatureRevokeTargetUser(null);
    await loadFeatureSharingDetails(sessionId);
  };

  const closeFeatureSharingPanel = () => {
    if (featureSharingLoading) return;
    setFeatureSharingPanelOpen(false);
    setFeatureShareActionError('');
    setFeatureShareUserId('');
    setFeatureRevokeTargetUser(null);
  };

  const requestFeatureShare = async () => {
    const sessionId = String(selectedFeatureHistoryItem?.id || '').trim();
    const targetUserId = String(featureShareUserId || '').trim();
    if (!sessionId || !targetUserId) return;
    setFeatureShareActionError('');
    setFeatureSharingLoading(true);
    try {
      const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_SHARING_REQUEST(sessionId), {
        method: 'POST',
        body: { target_user_id: targetUserId },
      });
      setFeatureSharingStatus(payload?.sharing || null);
      setFeatureShareUserId('');
      await loadFeatureSharingDetails(sessionId);
    } catch (error) {
      setFeatureShareActionError(error.message || 'Failed to share this feature flow.');
    } finally {
      setFeatureSharingLoading(false);
    }
  };

  const reviewFeatureShareRequest = async (requestId, action) => {
    const sessionId = String(selectedFeatureHistoryItem?.id || '').trim();
    const normalizedRequestId = String(requestId || '').trim();
    if (!sessionId || !normalizedRequestId) return;
    setFeatureShareActionError('');
    setFeatureSharingLoading(true);
    try {
      const endpoint = action === 'approve'
        ? API_ENDPOINTS.FEATURE_CAPTURE_SHARING_APPROVE(sessionId, normalizedRequestId)
        : API_ENDPOINTS.FEATURE_CAPTURE_SHARING_REJECT(sessionId, normalizedRequestId);
      const payload = await requestJson(endpoint, {
        method: 'POST',
        body: {},
      });
      setFeatureSharingStatus(payload?.sharing || null);
      await loadFeatureSharingDetails(sessionId);
    } catch (error) {
      setFeatureShareActionError(error.message || `Failed to ${action} share request.`);
    } finally {
      setFeatureSharingLoading(false);
    }
  };

  const revokeFeatureShareAccess = async (targetUserId) => {
    const sessionId = String(selectedFeatureHistoryItem?.id || '').trim();
    const normalizedTargetUserId = String(targetUserId || '').trim();
    if (!sessionId || !normalizedTargetUserId) return;
    setFeatureShareActionError('');
    setFeatureSharingLoading(true);
    try {
      const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_SHARING_REVOKE(sessionId, normalizedTargetUserId), {
        method: 'DELETE',
      });
      setFeatureSharingStatus(payload?.sharing || null);
      setFeatureRevokeTargetUser(null);
      await loadFeatureSharingDetails(sessionId);
    } catch (error) {
      setFeatureShareActionError(error.message || 'Failed to remove feature flow access.');
    } finally {
      setFeatureSharingLoading(false);
    }
  };

  const openFeatureDeleteDialog = (item) => {
    if (!item?.id) return;
    setFeatureHistoryDeleteTarget(item);
    setFeatureHistoryDeleteConfirmText('');
    setFeatureHistoryDeleteError('');
  };

  const closeFeatureDeleteDialog = () => {
    if (featureHistoryDeleteSaving) return;
    setFeatureHistoryDeleteTarget(null);
    setFeatureHistoryDeleteConfirmText('');
    setFeatureHistoryDeleteError('');
  };

  const confirmDeleteFeatureCapture = async () => {
    const target = featureHistoryDeleteTarget;
    const sessionId = String(target?.id || '').trim();
    if (!sessionId) return;
    if (featureHistoryDeleteConfirmText.trim().toLowerCase() !== 'delete this') {
      setFeatureHistoryDeleteError('Type "delete this" to confirm deletion.');
      return;
    }
    setFeatureHistoryDeleteSaving(true);
    setFeatureHistoryDeleteError('');
    try {
      const targetApplicationId = flowApplicationId(target);
      const nextHistoryItem = featureHistoryItems.find((item) => String(item?.id || '') !== sessionId) || null;
      await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_DELETE(sessionId), { method: 'DELETE' });
      setFeatureHistoryItems((current) => current.filter((item) => String(item?.id || '') !== sessionId));
      setApplicationFeatureItems((current) => current.filter((item) => String(item?.id || '') !== sessionId));
      if (String(selectedFeatureHistoryId || '') === sessionId) {
        const nextItem = featureHistoryItems.find((item) => String(item?.id || '') !== sessionId);
        setSelectedFeatureHistoryId(nextItem?.id || '');
        if (nextItem?.id) {
          loadFeatureSharingDetails(nextItem.id);
        } else {
          setFeatureSharingStatus(null);
          setFeatureSharingRequests([]);
        }
      }
      if (String(session?.id || '') === sessionId) {
        invalidateBrdIndexingRequest();
        activeSessionIdRef.current = '';
        pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
        setSession(null);
        setWorkflowStageOverride('');
        setFeatureRunResults({});
        setFeatureSaveResponse(null);
        setStreamProcessingResult(null);
        const fallbackApplicationId = selectedDashboardApplicationId || targetApplicationId;
        if (fallbackApplicationId) {
          setSelectedDashboardApplicationId(fallbackApplicationId);
          setSelectedApplicationId(fallbackApplicationId);
          setCaptureDashboardView('application');
          navigateFeatureRoute(buildFeatureCaptureRoute({ applicationId: fallbackApplicationId }), { replace: true });
        } else {
          setCaptureDashboardView('applications');
          getFeatureSessionStorage()?.removeItem(FEATURE_CAPTURE_LAST_ROUTE_KEY);
          navigateFeatureRoute(FEATURE_CAPTURE_ROUTE_ROOT, { replace: true });
        }
      }
      setStatusMessage(`Deleted ${target.feature_name || 'captured feature'} from history.`);
      setFeatureHistoryDeleteTarget(null);
      setFeatureHistoryDeleteConfirmText('');
      setFeatureHistoryDeleteError('');
      await refreshFeatureCaptureCollections({
        applicationId: selectedDashboardApplicationId || targetApplicationId,
        refreshHistory: showFeatureHistory,
        preferredSessionId: nextHistoryItem?.id || '',
      });
    } catch (error) {
      setFeatureHistoryDeleteError(error.message || 'Failed to delete feature flow.');
    } finally {
      setFeatureHistoryDeleteSaving(false);
    }
  };

  const closeApplicationRename = () => {
    if (applicationRenameSaving) return;
    setRenamingApplicationId('');
    setApplicationRenameValue('');
    setApplicationRenameError('');
  };

  const beginRenameApplication = (application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId) return;
    setRenamingApplicationId(applicationId);
    setApplicationRenameValue(String(application?.name || '').trim());
    setApplicationRenameError('');
  };

  const mergeApplicationIntoFlowItem = (item, application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId || flowApplicationId(item) !== applicationId) return item;
    const currentApplication = item?.feature_area && typeof item.feature_area === 'object'
      ? item.feature_area
      : {};
    return {
      ...item,
      feature_area: { ...currentApplication, ...application },
      feature_area_name: application?.name ?? item?.feature_area_name,
      feature_area_base_url: application?.base_url ?? item?.feature_area_base_url,
      feature_area_environment_name: application?.environment_name ?? item?.feature_area_environment_name,
      application_id: application?.application_id ?? item?.application_id,
      application_name: applicationWorkspaceName(application) || item?.application_name,
      workspace_name: applicationWorkspaceName(application) || item?.workspace_name,
    };
  };

  const applyRenamedApplicationToState = (application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId) return;
    const updateApplication = (row) => (
      String(row?.id || '').trim() === applicationId ? { ...row, ...application } : row
    );
    setDashboardApplications((current) => current.map(updateApplication));
    setApplications((current) => current.map(updateApplication));
    setApplicationFeatureItems((current) => current.map((item) => mergeApplicationIntoFlowItem(item, application)));
    setFeatureHistoryItems((current) => current.map((item) => mergeApplicationIntoFlowItem(item, application)));
    setSession((current) => (current ? mergeApplicationIntoFlowItem(current, application) : current));
  };

  const handleRenameApplication = async (application) => {
    const applicationId = String(application?.id || '').trim();
    const workspaceId = String(application?.application_id || application?.application?.id || application?.workspace?.id || '').trim();
    const normalizedName = String(applicationRenameValue || '').trim();
    if (!applicationId) return;
    if (!normalizedName) {
      setApplicationRenameError('Feature area name is required.');
      return;
    }
    if (applicationNameExistsInWorkspace(normalizedName, workspaceId, applicationId)) {
      setApplicationRenameError('Feature area name already exists in this application.');
      return;
    }

    setApplicationRenameSaving(true);
    setApplicationRenameError('');
    try {
      const payload = await requestJson(API_ENDPOINTS.FEATURE_AREA_BY_ID(applicationId), {
        method: 'PATCH',
        body: { name: normalizedName },
      });
      const savedApplication = payload?.feature_area || payload?.application || { ...application, name: normalizedName };
      applyRenamedApplicationToState(savedApplication);
      setRenamingApplicationId('');
      setApplicationRenameValue('');
      setStatusMessage(`Feature area renamed to ${savedApplication?.name || normalizedName}.`);
    } catch (error) {
      setApplicationRenameError(error.message || 'Failed to rename feature area.');
    } finally {
      setApplicationRenameSaving(false);
    }
  };

  const closeFeatureRename = () => {
    if (featureRenameSavingId) return;
    setRenamingFeatureId('');
    setFeatureRenameValue('');
    setFeatureRenameError('');
  };

  const beginRenameApplicationFeature = (item) => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    setRenamingFeatureId(sessionId);
    setFeatureRenameValue(String(item?.feature_name || '').trim());
    setFeatureRenameError('');
  };

  const applyRenamedFeatureToState = (renamedCapture, fallbackName = '') => {
    const sessionId = String(renamedCapture?.id || '').trim();
    if (!sessionId) return;
    const nextFeatureName = String(renamedCapture?.feature_name || fallbackName || '').trim();
    const updateCapture = (item) => (
      String(item?.id || '').trim() === sessionId
        ? { ...item, ...renamedCapture, feature_name: nextFeatureName }
        : item
    );
    setApplicationFeatureItems((current) => current.map(updateCapture));
    setFeatureHistoryItems((current) => current.map(updateCapture));
    setSession((current) => (
      current && String(current?.id || '').trim() === sessionId
        ? { ...current, ...renamedCapture, feature_name: nextFeatureName }
        : current
    ));
    if (String(session?.id || '').trim() === sessionId) {
      setFeatureName(nextFeatureName);
    }
  };

  const handleRenameApplicationFeature = async (item) => {
    const sessionId = String(item?.id || '').trim();
    const applicationId = flowApplicationId(item) || selectedDashboardApplicationId;
    const normalizedName = String(featureRenameValue || '').trim();
    if (!sessionId) return;
    if (!normalizedName) {
      setFeatureRenameError('Feature name is required.');
      return;
    }
    if (featureNameExistsInApplication(normalizedName, applicationId, sessionId)) {
      setFeatureRenameError('Feature name already exists in this feature area.');
      return;
    }

    setFeatureRenameSavingId(sessionId);
    setFeatureRenameError('');
    try {
      const renamedCapture = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId), {
        method: 'PATCH',
        body: { feature_name: normalizedName },
      });
      applyRenamedFeatureToState(renamedCapture || { ...item, feature_name: normalizedName }, normalizedName);
      setRenamingFeatureId('');
      setFeatureRenameValue('');
      setStatusMessage(`Feature renamed to ${renamedCapture?.feature_name || normalizedName}.`);
      await loadApplicationDashboard();
    } catch (error) {
      setFeatureRenameError(error.message || 'Failed to rename feature.');
    } finally {
      setFeatureRenameSavingId('');
    }
  };

  const handleDeleteApplication = async (application) => {
    const applicationId = String(application?.id || '').trim();
    if (!applicationId) return;
    const appName = application?.name || 'this feature area';
    const flowsForApplication = Array.isArray(application?.flows)
      ? application.flows
      : featureHistoryItems.filter((item) => flowApplicationId(item) === applicationId);
    const deletedSessionIds = new Set(
      flowsForApplication
        .map((item) => String(item?.id || '').trim())
        .filter(Boolean)
    );
    const flowsCount = Number(application?.flows_count ?? flowsForApplication.length ?? 0);
    const message = flowsCount > 0
      ? `Delete ${appName} and ${flowsCount} feature flow(s)? This cannot be undone.`
      : `Delete ${appName}? This cannot be undone.`;
    if (!window.confirm(message)) return;

    setDeletingApplicationId(applicationId);
    setErrorMessage('');
    try {
      const remainingHistoryItems = featureHistoryItems.filter((item) => flowApplicationId(item) !== applicationId);
      const nextHistoryItem = remainingHistoryItems[0] || null;
      await requestJson(API_ENDPOINTS.FEATURE_AREA_BY_ID(applicationId), { method: 'DELETE' });
      setDashboardApplications((current) => current.filter((row) => String(row?.id || '') !== applicationId));
      setApplications((current) => current.filter((row) => String(row?.id || '') !== applicationId));
      setFeatureHistoryItems((current) => current.filter((row) => flowApplicationId(row) !== applicationId));
      setApplicationFeatureItems((current) => (
        String(selectedDashboardApplicationId || '') === applicationId
          ? []
          : current.filter((row) => !deletedSessionIds.has(String(row?.id || '').trim()))
      ));
      if (String(selectedHistoryApplicationId || '') === applicationId) {
        setSelectedHistoryApplicationId(nextHistoryItem ? flowApplicationId(nextHistoryItem) : '');
        setSelectedFeatureHistoryId(nextHistoryItem?.id || '');
      }
      const deletedActiveApplication = (
        String(session?.feature_area_id || session?.feature_area?.id || '') === applicationId
        || deletedSessionIds.has(String(session?.id || '').trim())
      );
      if (deletedActiveApplication) {
        invalidateBrdIndexingRequest();
        activeSessionIdRef.current = '';
        pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
        setSession(null);
        setWorkflowStageOverride('');
        setFeatureRunResults({});
        setFeatureSaveResponse(null);
        setStreamProcessingResult(null);
        setCaptureDashboardView('applications');
        getFeatureSessionStorage()?.removeItem(FEATURE_CAPTURE_LAST_ROUTE_KEY);
        navigateFeatureRoute(FEATURE_CAPTURE_ROUTE_ROOT, { replace: true });
      }
      if (String(selectedDashboardApplicationId || '') === applicationId) {
        returnToApplicationsDashboard();
      }
      if (String(selectedApplicationId || '') === applicationId) {
        setSelectedApplicationId('');
      }
      await loadApplicationDashboard();
      if (selectedWorkspaceId) await loadApplications(selectedWorkspaceId);
      if (showFeatureHistory) {
        await refreshFeatureHistoryList({ preferredSessionId: nextHistoryItem?.id || '' });
      }
      setStatusMessage(`${appName} deleted.`);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to delete application.');
      if (showFeatureHistory) {
        setFeatureHistoryError(error.message || 'Failed to delete application.');
      }
    } finally {
      setDeletingApplicationId('');
    }
  };

  const handleArchiveApplicationFeature = async (item) => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    const isArchived = Boolean(item?.archived);
    const featureLabel = item?.feature_name || 'this feature';
    if (!isArchived && !window.confirm(
      `Archive ${featureLabel}? It is hidden from the list but keeps its name and can be restored.`
    )) return;

    setArchivingFeatureCaptureId(sessionId);
    setErrorMessage('');
    try {
      await requestJson(
        isArchived
          ? API_ENDPOINTS.FEATURE_CAPTURE_UNARCHIVE(sessionId)
          : API_ENDPOINTS.FEATURE_CAPTURE_ARCHIVE(sessionId),
        { method: 'POST' },
      );
      await loadApplicationFeatures(flowApplicationId(item) || selectedDashboardApplicationId);
    } catch (error) {
      setErrorMessage(error.message || `Failed to ${isArchived ? 'restore' : 'archive'} the feature.`);
    } finally {
      setArchivingFeatureCaptureId('');
    }
  };

  const handleDeleteApplicationFeature = async (item) => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    const featureLabel = item?.feature_name || 'this feature';
    if (!window.confirm(
      `Permanently delete ${featureLabel}? This removes the captured flow and generated outputs `
      + 'and cannot be undone. The name becomes available again. Use Archive to keep the data.'
    )) return;

    setDeletingFeatureCaptureId(sessionId);
    setErrorMessage('');
    try {
      const targetApplicationId = flowApplicationId(item);
      const nextHistoryItem = featureHistoryItems.find((row) => String(row?.id || '') !== sessionId) || null;
      await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_DELETE(sessionId), { method: 'DELETE' });
      setApplicationFeatureItems((current) => current.filter((row) => String(row?.id || '') !== sessionId));
      setFeatureHistoryItems((current) => current.filter((row) => String(row?.id || '') !== sessionId));
      if (String(selectedFeatureHistoryId || '') === sessionId) {
        setSelectedFeatureHistoryId('');
        setFeatureSharingStatus(null);
        setFeatureSharingRequests([]);
      }
      if (String(session?.id || '') === sessionId) {
        invalidateBrdIndexingRequest();
        activeSessionIdRef.current = '';
        pendingRestoredBrdSectionRef.current = { pdfId: '', sectionNumber: '' };
        setSession(null);
        setWorkflowStageOverride('');
        setFeatureRunResults({});
        setFeatureSaveResponse(null);
        setStreamProcessingResult(null);
        if (targetApplicationId) {
          setSelectedDashboardApplicationId(targetApplicationId);
          setSelectedApplicationId(targetApplicationId);
          setCaptureDashboardView('application');
          navigateFeatureRoute(buildFeatureCaptureRoute({ applicationId: targetApplicationId }), { replace: true });
        } else {
          setCaptureDashboardView('applications');
          getFeatureSessionStorage()?.removeItem(FEATURE_CAPTURE_LAST_ROUTE_KEY);
          navigateFeatureRoute(FEATURE_CAPTURE_ROUTE_ROOT, { replace: true });
        }
      }
      await refreshFeatureCaptureCollections({
        applicationId: selectedDashboardApplicationId || targetApplicationId,
        refreshHistory: showFeatureHistory,
        preferredSessionId: nextHistoryItem?.id || '',
      });
      setStatusMessage(`${featureLabel} deleted.`);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to delete feature.');
    } finally {
      setDeletingFeatureCaptureId('');
    }
  };

  const executeRecaptureFeatureCapture = (item) => runAction('recapture', async () => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    const featureLabel = item?.feature_name || 'this feature';
    const startUrl = String(
      captureStartUrl
      || item?.feature_area_base_url
      || item?.feature_area?.base_url
      || item?.start_url
      || ''
    ).trim();
    if (!startUrl) {
      throw new Error('This captured feature does not have a target URL to recapture.');
    }
    setRecapturingFeatureCaptureId(sessionId);
    setErrorMessage('');
    try {
      await ensureFeatureCaptureExtensionReady();
      const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_RECAPTURE(sessionId), {
        method: 'POST',
        body: {},
      });
      applyFeatureSessionToWorkspace(payload, { fromHistory: true, stageOverride: 'capture' });
      const applicationId = flowApplicationId(payload || item);
      if (applicationId) {
        setSelectedDashboardApplicationId(applicationId);
        setSelectedApplicationId(applicationId);
      }
      setCaptureDashboardView('workflow');
      goToFeatureStage('capture', { sessionId: payload?.id || sessionId, replace: true });
      await refreshFeatureCaptureCollections({
        applicationId: applicationId || selectedDashboardApplicationId,
        refreshHistory: showFeatureHistory,
        preferredSessionId: sessionId,
      });

      const extensionResponse = await sendExtensionBridgeMessage({
        type: 'lsq-feature-capture-start',
        sessionId,
        endpoint: backendEndpoint,
        appUrl: `${window.location.origin}/feature-capture`,
        startUrl,
        featureName: payload.feature_name || featureLabel,
        environmentId: captureEnvironmentId,
        autoStart: true,
        skipEnvironment: Boolean(payload.recorded_login),
        recapture: true,
      });

      if (extensionResponse?.ok === false) {
        throw new Error(extensionResponse.error || 'Chrome extension could not start recapture.');
      }
      setPreparingRecapture(false);
      setShowFeatureHistory(false);
      setStatusMessage(`Recapturing ${payload.feature_name || featureLabel}. Complete the flow in the new capture window, then finish from the extension panel.`);
    } finally {
      setRecapturingFeatureCaptureId('');
    }
  });

  const openRecaptureSetup = async (item) => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    setErrorMessage('');
    setRecaptureConfirmation(null);
    setPreparingRecapture(true);

    try {
      let captureSession = session;
      if (String(session?.id || '').trim() !== sessionId) {
        captureSession = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId));
        applyFeatureSessionToWorkspace(captureSession, { fromHistory: true, stageOverride: 'capture' });
      } else {
        setFeatureName(String(session?.feature_name || item?.feature_name || '').trim());
        setWorkflowStageOverride('capture');
      }
      const applicationId = flowApplicationId(captureSession || item);
      if (applicationId) {
        setSelectedDashboardApplicationId(applicationId);
        setSelectedApplicationId(applicationId);
      }
      setCaptureDashboardView('workflow');
      goToFeatureStage('capture', { sessionId, replace: true });
      setShowFeatureHistory(false);
      setStatusMessage('Review the recording setup, make any changes, then open the target when you are ready to recapture.');
    } catch (error) {
      setPreparingRecapture(false);
      setErrorMessage(error?.message || 'The recapture setup could not be opened. Refresh the page and try again.');
    }
  };

  const handleRecaptureFeatureCapture = (item) => {
    const sessionId = String(item?.id || '').trim();
    if (!sessionId) return;
    setRecaptureConfirmation({ item, intent: 'prepare', saveEdits: false });
  };

  const handleStartRecaptureFeatureCapture = () => {
    const normalizedName = String(featureName || '').trim();
    const sessionId = String(session?.id || '').trim();
    const applicationId = flowApplicationId(session);
    setFeatureNameError(normalizedName ? '' : 'Feature name is required.');
    if (!normalizedName || !sessionId) return;
    if (featureNameExistsInApplication(normalizedName, applicationId, sessionId)) {
      setFeatureNameError('Feature name already exists in this feature area.');
      return;
    }

    const nameChanged = normalizedName !== String(session?.feature_name || '').trim();
    setRecaptureConfirmation({
      item: { ...session, feature_name: normalizedName, start_url: activeSessionTargetUrl },
      intent: 'start',
      saveEdits: nameChanged,
      featureName: normalizedName,
    });
  };

  const beginEditingRecaptureTarget = (field = 'feature') => {
    setRecaptureFeatureName(String(session?.feature_name || featureName || '').trim());
    setFeatureNameError('');
    setRecaptureEditFocus(field);
    setEditingRecaptureTarget(true);
  };

  const cancelEditingRecaptureTarget = () => {
    if (recaptureTargetSaving) return;
    setEditingRecaptureTarget(false);
    setFeatureNameError('');
  };

  const handleSaveTargetAndRecapture = () => {
    const normalizedName = String(recaptureFeatureName || '').trim();
    const sessionId = String(session?.id || '').trim();
    const applicationId = flowApplicationId(session);
    setFeatureNameError(normalizedName ? '' : 'Feature name is required.');
    if (!normalizedName || !sessionId) return;
    if (featureNameExistsInApplication(normalizedName, applicationId, sessionId)) {
      setFeatureNameError('Feature name already exists in this feature area.');
      return;
    }
    setRecaptureConfirmation({
      item: { ...session, feature_name: normalizedName },
      saveEdits: true,
      featureName: normalizedName,
    });
  };

  const handleSaveRecaptureTarget = async () => {
    const normalizedName = String(recaptureFeatureName || '').trim();
    const sessionId = String(session?.id || '').trim();
    const applicationId = flowApplicationId(session);
    setFeatureNameError(normalizedName ? '' : 'Feature name is required.');
    if (!normalizedName || !sessionId) return;
    if (featureNameExistsInApplication(normalizedName, applicationId, sessionId)) {
      setFeatureNameError('Feature name already exists in this feature area.');
      return;
    }

    const updates = {};
    if (normalizedName !== String(session?.feature_name || '').trim()) {
      updates.feature_name = normalizedName;
    }
    if (!Object.keys(updates).length) {
      setEditingRecaptureTarget(false);
      return;
    }

    setRecaptureTargetSaving(true);
    try {
      const updatedSession = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId), {
        method: 'PATCH',
        body: updates,
      });
      applyRenamedFeatureToState(updatedSession || { ...session, ...updates }, normalizedName);
      setSession((current) => ({ ...current, ...updatedSession, ...updates }));
      setEditingRecaptureTarget(false);
      await refreshFeatureCaptureCollections({
        applicationId,
        refreshHistory: showFeatureHistory,
        preferredSessionId: sessionId,
      });
    } catch (error) {
      const message = error?.message || 'Feature details could not be saved.';
      if (/name already exists|archived feature named/i.test(message)) {
        setFeatureNameError(message);
      } else {
        setFeatureNameError(message);
      }
    } finally {
      setRecaptureTargetSaving(false);
    }
  };

  const confirmRecaptureFeatureCapture = async () => {
    const confirmation = recaptureConfirmation;
    if (!confirmation?.item) return;
    if (confirmation.intent === 'prepare') {
      setRecaptureConfirmation(null);
      await openRecaptureSetup(confirmation.item);
      return;
    }
    if (!confirmation.saveEdits) {
      setRecaptureConfirmation(null);
      await executeRecaptureFeatureCapture(confirmation.item);
      return;
    }
    const normalizedName = String(confirmation.featureName || '').trim();
    const sessionId = String(confirmation.item?.id || '').trim();
    setRecaptureTargetSaving(true);
    try {
      const updatedSession = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId), {
        method: 'PATCH',
        body: { feature_name: normalizedName },
      });
      applyRenamedFeatureToState(updatedSession || { ...session, feature_name: normalizedName }, normalizedName);
      setSession((current) => ({ ...current, ...updatedSession }));
      setEditingRecaptureTarget(false);
      setRecaptureConfirmation(null);
      await executeRecaptureFeatureCapture(
        updatedSession || { ...session, feature_name: normalizedName }
      );
    } catch (error) {
      setRecaptureConfirmation(null);
      const message = error?.message || 'Feature details could not be saved.';
      if (/name already exists|archived feature named/i.test(message)) {
        setFeatureNameError(message);
      } else {
        setFeatureNameError(message);
      }
    } finally {
      setRecaptureTargetSaving(false);
    }
  };

  const restoreFeatureCaptureFromHistory = async (historyItem, targetStage = 'capture', targetTab = '') => {
    const sessionId = String(historyItem?.id || '').trim();
    if (!sessionId) return;
    const nextStage = normalizeFeatureCaptureStageId(targetStage, 'capture');
    const nextCapturedFlowTab = capturedFlowTabForRestore(nextStage, targetTab);
    pendingCapturedFlowTabRef.current = nextCapturedFlowTab;
    if (nextStage === 'captured-flow') {
      setCapturedFlowTab(nextCapturedFlowTab);
    }
    const featureLabel = historyItem?.feature_name || 'captured feature';
    const targetPath = buildFeatureCaptureRoute({
      rootApplicationId: isApplicationRouteMode ? lockedApplicationId : '',
      sessionId,
      stageId: nextStage,
    });
    const currentPath = `${location.pathname}${location.search || ''}`;
    if (!location.search && currentPath !== targetPath) {
      const signature = `workflow:${sessionId}:${nextStage}`;
      pendingRestoreStageRef.current = nextStage;
      featureRouteHandledRef.current = '';
      setFeatureHistoryRestoreId(sessionId);
      setErrorMessage('');
      setFeatureHistoryError('');
      setStatusMessage(`Opening ${featureLabel}...`);
      setWorkflowStageOverride(nextStage);
      setCaptureDashboardView('workflow');
      updateFeatureRouteHydration(signature, true);
      navigate(targetPath);
      return;
    }
    pendingRestoreStageRef.current = nextStage;
    setFeatureHistoryRestoreId(sessionId);
    setErrorMessage('');
    setFeatureHistoryError('');
    setStatusMessage(`Opening ${featureLabel}...`);
    try {
      const fastOpen = ['scripts', 'execution'].includes(nextStage);
      const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId, { fast: fastOpen }));
      setSelectedFeatureHistoryId(sessionId);
      applyFeatureSessionToWorkspace(payload, { fromHistory: true, stageOverride: nextStage });
      setSelectedDashboardApplicationId(flowApplicationId(payload || historyItem));
      setCaptureDashboardView('workflow');
      setStatusMessage(`Loaded ${payload.feature_name || 'captured feature'} from history. Viewing ${workflowStages.find((stage) => stage.id === nextStage)?.title || 'Capture'}.`);
      setShowFeatureHistory(false);
    } catch (error) {
      pendingRestoreStageRef.current = '';
      setFeatureHistoryError(error.message || 'Failed to load this captured feature.');
      setErrorMessage(error.message || 'Failed to load this captured feature.');
    } finally {
      setFeatureHistoryRestoreId('');
    }
  };

  const openFlowComposerFromHistory = (historyItem) => {
    return restoreFeatureCaptureFromHistory(historyItem, 'captured-flow', 'components');
  };

  const handleOpenCapture = () => runAction('open', async () => {
    if (!canPrepareCapture) return;
    const normalizedFeatureName = String(featureName || '').trim();
    const normalizedApplicationId = String(selectedApplicationId || '').trim();
    if (
      !isApplicationRouteMode
      && featureNameExistsInApplication(normalizedFeatureName, normalizedApplicationId, session?.id)
    ) {
      setFeatureNameError('Feature name already exists in this feature area.');
      return;
    }
    setFeatureNameError('');
    await ensureFeatureCaptureExtensionReady();

    const contextFilePayload = await buildContextFilePayload();
    const createdSession = await requestJson(API_ENDPOINTS.FEATURE_CAPTURES, {
      method: 'POST',
      body: {
        feature_name: featureName,
        start_url: captureStartUrl,
        recorded_login: recordSharedLogin,
        application_id: selectedWorkspaceId,
        feature_area_id: isApplicationRouteMode ? undefined : selectedApplicationId || undefined,
        brd_reference: selectedBrdSectionReference?.pdf_id || undefined,
        brd_section_reference: selectedBrdSectionReference,
        brd_context_text: brdContextText,
        brd_context_file: contextFilePayload,
        notes,
      },
    });

    applyFeatureSessionToWorkspace(createdSession);
    if (!isApplicationRouteMode && (createdSession?.application_id || selectedWorkspaceId)) {
      await loadApplications(createdSession.application_id || selectedWorkspaceId);
    }
    const createdApplicationId = String(createdSession?.feature_area_id || createdSession?.feature_area?.id || '').trim();
    if (!isApplicationRouteMode && createdApplicationId) {
      setSelectedDashboardApplicationId(createdApplicationId);
      await loadApplicationFeatures(createdApplicationId);
    }
    const refreshedDashboardApplications = isApplicationRouteMode
      ? []
      : await loadApplicationDashboard();
    if (
      createdApplicationId
      && refreshedDashboardApplications.some((application) => String(application?.id || '') === createdApplicationId)
    ) {
      setSelectedDashboardApplicationId(createdApplicationId);
    }
    setCaptureDashboardView('workflow');
    setStatusMessage(captureLaunchMessage);

    const extensionResponse = await sendExtensionBridgeMessage({
      type: 'lsq-feature-capture-start',
      sessionId: createdSession.id,
      endpoint: backendEndpoint,
      appUrl: `${window.location.origin}/feature-capture`,
      startUrl: captureStartUrl,
      featureName,
      environmentId: captureEnvironmentId,
      autoStart: !recordContinuation,
      skipEnvironment: recordSharedLogin,
    });

    if (extensionResponse?.ok === false) {
      throw new Error(extensionResponse.error || 'Chrome extension could not start capture.');
    }

    applyFeatureSessionToWorkspace(createdSession);
    setStatusMessage(recordContinuation
      ? 'Capture window opened. Complete the shared steps, click Start Capture at the branch point, then finish from the extension panel.'
      : 'Capture window opened. Complete the flow there, then finish from the extension panel.');
  });

  const handleRefreshFlow = () => runAction('refresh', async () => {
    const payload = await refreshSession();
    if (Array.isArray(payload?.normalized_steps) && payload.normalized_steps.length > 0) {
      goToFeatureStage('captured-flow', { sessionId: payload.id || session?.id });
    }
    setStatusMessage('Captured flow refreshed.');
  });

  handleRefreshFlowRef.current = handleRefreshFlow;

  useEffect(() => {
    const handleExtensionFinished = (event) => {
      if (event.source !== window) return;
      const data = event.data || {};
      if (data.source !== EXTENSION_SOURCE || data.type !== 'lsq-feature-capture-finished') return;

      const finishedSessionId = String(data.sessionId || '').trim();
      const currentSessionId = String(session?.id || '').trim();
      if (finishedSessionId && currentSessionId && finishedSessionId !== currentSessionId) return;
      if (!finishedSessionId && !currentSessionId) return;

      setStatusMessage('Capture finished. Loading captured flow...');
      handleRefreshFlowRef.current?.();
    };

    window.addEventListener('message', handleExtensionFinished);
    return () => window.removeEventListener('message', handleExtensionFinished);
  }, [session?.id]);

  const saveFlowContextIfChanged = async () => {
    if (!session?.id) return session;
    const normalizedNotes = String(notes || '').trim();
    if (normalizedNotes === String(session?.notes || '').trim()) return session;
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(session.id), {
      method: 'PATCH',
      body: { notes: normalizedNotes },
    });
    setSession(payload);
    return payload;
  };

  const handleSaveTestContext = async () => {
    if (!session?.id || contextSaving) return;
    setContextSaving(true);
    setContextError('');
    try {
      const payload = await saveFlowContextIfChanged();
      setNotes(String(payload?.notes || '').trim());
      setContextEditing(false);
    } catch (error) {
      setContextError(error.message || 'Test context could not be saved. Your text is still here.');
    } finally {
      setContextSaving(false);
    }
  };

  const handleCancelTestContext = () => {
    setNotes(String(session?.notes || '').trim());
    setContextEditing(false);
    setContextError('');
  };

  const handleGenerateCases = (generationConfig = {}, options = {}) => runAction('cases', async () => {
    if (!session?.id) return;
    if (actionableCaptureStepCount === 0) {
      throw new Error('No user interactions were captured. Recapture the flow and perform at least one action before generating test cases.');
    }
    setFeatureSaveResponse(null);
    setStatusMessage('Saving flow context...');
    const savedSession = await saveFlowContextIfChanged();
    const sessionId = savedSession?.id || session.id;
    const normalizedGenerationConfig = {
      ...generationConfig,
      positive_count: Math.max(1, Math.min(20, Number(generationConfig.positive_count || 1))),
      negative_count: Math.max(1, Math.min(20, Number(generationConfig.negative_count || 1))),
    };
    const initialStreamResult = {
      id: sessionId,
      source_type: 'feature_capture',
      section_title: savedSession?.feature_name || session.feature_name || featureName || 'Captured Feature',
      section_number: 1,
      status: 'running',
      stream_status: 'running',
      approved_counts: {
        positive_count: normalizedGenerationConfig.positive_count,
        negative_count: normalizedGenerationConfig.negative_count,
      },
      approved_focus: normalizedGenerationConfig.additional_focus || [],
      approved_global_constraints: normalizedGenerationConfig.global_constraints || [],
      test_case_field_config: normalizedGenerationConfig.test_case_field_config || null,
      input_set_count: normalizedGenerationConfig.input_set_count || 3,
      test_cases: buildInitialStreamTestCases(
        savedSession?.feature_name || session.feature_name || featureName || 'Captured Feature',
        normalizedGenerationConfig.test_case_field_config || null
      ),
      streamed_case_count: 0,
      streamed_input_sets_count: 0,
    };
    setStreamProcessingResult(initialStreamResult);
    if (options.openCases) {
      goToFeatureStage('cases', { sessionId });
      setSelectedStepIds([]);
      setStatusMessage('Generating 2 test cases from the captured actions...');
    }

    const queued = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_GENERATE_TEST_CASES_QUEUE(sessionId), {
      method: 'POST',
      body: normalizedGenerationConfig,
    });
    const jobId = queued?.job?.id;
    if (!jobId) throw new Error('Test case generation did not return a job ID.');
    window.sessionStorage.setItem(featureCaseJobStorageKey(sessionId), jobId);
    activeCaseMonitorRef.current = jobId;
    setActiveCaseJobId(jobId);
    try {
      const finalSession = await waitForFeatureCaseJob(jobId, sessionId,
        (row) => applyFeatureCaseProgressEvent(row, setStreamProcessingResult, initialStreamResult));
      setSession(finalSession);
      setStreamProcessingResult(null);
      window.sessionStorage.removeItem(featureCaseJobStorageKey(sessionId));
      setStatusMessage(`${finalSession.test_cases?.length || 0} test case(s) generated.`);
      return finalSession;
    } catch (error) {
      if (error.terminalJob) window.sessionStorage.removeItem(featureCaseJobStorageKey(sessionId));
      setStreamProcessingResult((current) => markStreamResultFailed(current || initialStreamResult, error.message));
      throw error;
    } finally {
      activeCaseMonitorRef.current = '';
      setActiveCaseJobId('');
    }
  });

  const handleQuickFlowCheck = (options = {}) => handleGenerateCases({
    positive_count: 1,
    negative_count: 1,
    additional_focus: [],
    global_constraints: Array.isArray(session?.approved_generation_config?.global_constraints)
      ? session.approved_generation_config.global_constraints
      : [],
    input_set_count: 1,
    test_case_field_config:
      session?.approved_generation_config?.test_case_field_config || buildDefaultTestCaseFieldConfig(),
  }, options);

  const handleCancelCaseGeneration = async () => {
    if (!activeCaseJobId) return;
    try {
      await requestJson(API_ENDPOINTS.JOB_CANCEL(activeCaseJobId), { method: 'POST' });
      setStatusMessage('Stopping test case generation…');
    } catch (error) {
      setErrorMessage(error.message || 'Could not cancel test case generation.');
    }
  };

  const handleSaveFeatureTestCases = async (testCasePayload, baselinePayload, options = {}) => {
    if (!session?.id) {
      throw new Error('Start or load a feature capture before saving test cases.');
    }
    const sourceId = featureAutomationSourceId(session);
    if (!sourceId) {
      throw new Error('This captured flow is missing its artifact source ID. Reload the flow and try again.');
    }
    setBusyAction('saveCases');
    setErrorMessage('');
    try {
      const autosave = Boolean(options?.autosave);
      const artifactPayload = {
        edited_test_cases: testCasePayload,
        section_title: session.feature_name || 'Captured Feature',
        section_number: 1,
        pdf_id: sourceId,
        save_mode: autosave ? 'autosave' : 'manual',
        ...(options?.manualImportMeta ? { manual_import: options.manualImportMeta } : {}),
        ...(autosave
          ? {}
          : {
              original_test_cases: baselinePayload,
              comparison: {
                original_count: countTestCases(baselinePayload),
                edited_count: countTestCases(testCasePayload),
                timestamp: new Date().toISOString(),
              },
            }),
      };
      const persistedArtifact = await requestJson(API_ENDPOINTS.SAVE_TEST_CASE_ARTIFACTS, {
        method: 'POST',
        body: artifactPayload,
      });

      if (autosave) {
        setStatusMessage('Draft test cases saved.');
        return persistedArtifact;
      }

      const reviewedSession = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_SAVE_TEST_CASES(session.id), {
        method: 'POST',
        body: {
          test_cases: testCasePayload,
          baseline_test_cases: baselinePayload,
          manual_import: options?.manualImportMeta || null,
        },
      });

      const exportPayload = await requestJson(`${API_ENDPOINTS.SAVE_TEST_CASES}/${sourceId}/save`, {
        method: 'POST',
        body: {
          ...artifactPayload,
          comparison: persistedArtifact?.metrics || artifactPayload.comparison,
          filename: `${session.feature_name || 'Captured Feature'} Test Cases.xlsx`,
        },
      });

      setFeatureSaveResponse(exportPayload);
      try {
        const pomPayload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_GENERATE_POM(reviewedSession.id || session.id), {
          method: 'POST',
          body: {},
        });
        setSession(pomPayload);
        goToFeatureStage('save', { sessionId: pomPayload?.id || reviewedSession?.id || session.id });
        setStatusMessage('Reviewed cases saved and page objects generated from the captured flow.');
      } catch (pomError) {
        setSession(reviewedSession);
        goToFeatureStage('save', { sessionId: reviewedSession?.id || session.id });
        setErrorMessage(pomError.message || 'Test cases were saved, but POM generation needs attention.');
        setStatusMessage('Reviewed test cases saved. Generate page objects when ready.');
      }
      return {
        ...persistedArtifact,
        export: exportPayload,
      };
    } catch (error) {
      setErrorMessage(error.message || String(error));
      throw error;
    } finally {
      setBusyAction('');
    }
  };

  const handleGeneratePom = () => runAction('pom', async () => {
    if (!session?.id) return;
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_GENERATE_POM(session.id), {
      method: 'POST',
      body: {},
    });
    setSession(payload);
    goToFeatureStage('pom', { sessionId: payload?.id || session.id });
    setStatusMessage('Page objects generated from the captured pages.');
    return payload;
  });

  const handleFeaturePomChange = useCallback(async () => {
    const sessionId = String(session?.id || '').trim();
    if (!sessionId) return null;
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_BY_ID(sessionId));
    setSession(payload);
    return payload;
  }, [session?.id]);

  const exportFeatureExecutionResults = async () => {
    if (!session?.id) {
      throw new Error('Load a feature flow before saving execution results.');
    }
    const addExecutionStatus = (cases, category) => (cases || []).map((testCase, index) => {
      const key = `${category}::${testCase.test_case_no || 'case'}::${index}`;
      const run = featureRunResults?.[key];
      return {
        ...testCase,
        execution_status: run ? (run.exit_code === 0 ? 'Passed' : 'Failed') : 'Not Run',
      };
    });
    const currentCases = activeFeatureProcessingResult.test_cases || {};
    const augmentedCases = {
      positive: addExecutionStatus(currentCases.positive, 'positive'),
      negative: addExecutionStatus(currentCases.negative, 'negative'),
    };
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_SAVE_TEST_CASES(session.id), {
      method: 'POST',
      body: {
        test_cases: augmentedCases,
        baseline_test_cases: activeFeatureProcessingResult.test_cases,
        manual_import: null,
      },
    });
    setSession(payload);
    setStatusMessage('Execution results saved to this feature flow.');
    return payload;
  };

  const markFeatureExecutionPrepared = () => runAction('execute', async () => {
    if (!session?.id) {
      setWorkflowStageOverride('execute');
      return null;
    }
    const payload = await requestJson(API_ENDPOINTS.FEATURE_CAPTURE_EXECUTE(session.id), {
      method: 'POST',
      body: {},
    });
    setSession(payload);
    goToFeatureStage('execute', { sessionId: payload?.id || session.id });
    setStatusMessage('Execution package prepared.');
    return payload;
  });

  const renderFeatureHistoryModal = () => {
    if (!showFeatureHistory) return null;
    const isRestoring = Boolean(featureHistoryRestoreId);
    const dashboardItem = selectedFeatureHistoryItem;
    const canDeleteSelectedHistoryApplication = Boolean(
      selectedHistoryApplication?.id
      && selectedHistoryApplication.id !== 'unassigned'
      && selectedHistoryApplication.flows?.length
      && selectedHistoryApplication.flows.every((flow) => Boolean(flow?.is_owner))
    );
    const selectedHistoryApplicationDeleting = Boolean(
      selectedHistoryApplication?.id
      && String(deletingApplicationId || '') === String(selectedHistoryApplication.id || '')
    );
    const selectedHistoryTitle = selectedHistoryApplication?.name || 'All captured flows';
    const selectedHistorySubtitle = selectedHistoryApplication
      ? selectedHistoryApplication.workspace_name || selectedHistoryApplication.base_url || ''
      : 'All applications';
    const canShowHistoryFlowShareButton = (item) => {
      const itemIsSelected = String(dashboardItem?.id || '') === String(item?.id || '');
      return Boolean(item?.id) && (
        Boolean(item?.is_owner)
        || (
          itemIsSelected
          && (featureCanShare || featureSharingLoading || Boolean(currentFeatureSharing))
        )
      );
    };
    const modalContent = (
      <>
      <HistoryDashboardShell
        ariaLabel="Feature capture history"
        kicker="Feature History"
        title="Previous Captured Feature Flows"
        copy="Reuse captured journeys and continue through cases, POM, scripts, and execution."
        gradientClassName="from-slate-900 via-teal-900 to-blue-700"
        accentClassName="bg-[radial-gradient(560px_260px_at_8%_0%,rgba(45,212,191,0.25),transparent_72%),radial-gradient(420px_220px_at_92%_0%,rgba(59,130,246,0.25),transparent_70%)]"
        onClose={() => setShowFeatureHistory(false)}
        closeAriaLabel="Close feature history"
        sidebar={(
          <>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Feature Areas
              </p>
              <span className="inline-flex min-w-[28px] items-center justify-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                {featureHistoryLoading ? (
                  <span className="h-3 w-4 animate-pulse rounded bg-slate-200" />
                ) : (
                  featureHistoryApplicationGroups.length
                )}
              </span>
            </div>

            {featureHistoryLoading ? (
              <div className="space-y-2">
                {[0, 1, 2].map((row) => (
                  <div key={`feature-history-app-loading-${row}`} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 w-3/4 rounded bg-slate-200" />
                      <div className="h-3 w-20 rounded bg-slate-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : featureHistoryApplicationGroups.length > 0 ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedHistoryApplicationId('');
                    setSelectedFeatureHistoryId(featureHistoryItems[0]?.id || '');
                    if (featureHistoryItems[0]?.id) loadFeatureSharingDetails(featureHistoryItems[0].id);
                  }}
                  disabled={isRestoring}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                    !selectedHistoryApplicationId
                      ? 'border-slate-400 bg-slate-100 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900">All feature areas</span>
                    <span className="mt-0.5 block text-xs text-slate-500">{formatCountLabel(featureHistoryItems.length, 'flow')}</span>
                  </span>
                  {!selectedHistoryApplicationId && <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-slate-600" />}
                </button>
                {featureHistoryApplicationGroups.map((group) => {
                  const isSelected = group.id === selectedHistoryApplicationId;
                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => {
                        setSelectedHistoryApplicationId(group.id);
                        setSelectedFeatureHistoryId(group.flows[0]?.id || '');
                        if (group.flows[0]?.id) loadFeatureSharingDetails(group.flows[0].id);
                      }}
                      disabled={isRestoring}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                        isSelected
                          ? 'border-teal-300 bg-teal-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-900">{group.name}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">{formatCountLabel(group.flows.length, 'flow')}</span>
                      </span>
                      {isSelected && <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-teal-600" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                No feature areas found.
              </div>
            )}
          </>
        )}
      >
        {featureHistoryLoading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3].map((row) => (
              <div key={`feature-history-row-loading-${row}`} className="rounded-xl border border-slate-200 bg-white px-4 py-4">
                <div className="animate-pulse space-y-3">
                  <div className="h-4 w-2/5 rounded bg-slate-200" />
                  <div className="h-3 w-1/3 rounded bg-slate-100" />
                  <div className="flex gap-2">
                    <div className="h-8 w-16 rounded-lg bg-slate-100" />
                    <div className="h-8 w-20 rounded-lg bg-slate-100" />
                    <div className="h-8 w-16 rounded-lg bg-slate-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : featureHistoryError ? (
          <div className="flex min-h-[280px] items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
            <div>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-rose-600">
                <AlertCircle className="h-6 w-6" />
              </div>
              <p className="mt-4 text-sm font-semibold text-rose-800">
                Could not load feature history.
              </p>
              <p className="mt-1 text-xs text-rose-700">
                {featureHistoryError}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Flows
                  </p>
                  <h3 className="mt-1 truncate text-lg font-bold text-slate-900">
                    {selectedHistoryTitle}
                  </h3>
                  {selectedHistorySubtitle && (
                    <p className="mt-1 truncate text-xs text-slate-500">
                      {selectedHistorySubtitle}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex h-8 items-center rounded-full border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700">
                    {formatCountLabel(filteredFeatureHistory.length, 'flow')}
                  </span>
                  {canDeleteSelectedHistoryApplication && (
                    <button
                      type="button"
                      onClick={() => handleDeleteApplication({
                        id: selectedHistoryApplication.id,
                        name: selectedHistoryApplication.name,
                        flows: selectedHistoryApplication.flows,
                        flows_count: selectedHistoryApplication.flows.length,
                      })}
                      disabled={Boolean(deletingApplicationId)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {selectedHistoryApplicationDeleting ? (
                        <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Delete Application
                    </button>
                  )}
                </div>
              </div>
              <label className="relative mt-4 block w-full">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={featureHistorySearch}
                  onChange={(event) => setFeatureHistorySearch(event.target.value)}
                  placeholder="Search flows"
                  className="text-input pl-9"
                />
              </label>
            </section>

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              {filteredFeatureHistory.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {filteredFeatureHistory.map((item) => {
                    const itemId = String(item?.id || '');
                    const itemIsSelected = String(dashboardItem?.id || '') === itemId;
                    const itemIsOwner = Boolean(item?.is_owner);
                    const itemIsRestoring = String(featureHistoryRestoreId || '') === itemId;
                    const showShareButton = canShowHistoryFlowShareButton(item);
                    return (
                      <div
                        key={item.id}
                        className={`flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                          itemIsSelected ? 'bg-blue-50/45' : 'bg-white'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {flowDisplayName(item, featureHistoryItems)}
                          </p>
                          <p className="mt-1 truncate text-xs text-slate-500">
                            {flowApplicationName(item)} · {formatHistoryDate(item.updated_at || item.created_at)}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => restoreFeatureCaptureFromHistory(item, 'captured-flow')}
                            disabled={isRestoring || isBusy}
                            className="primary-btn h-8 px-3 py-0 text-xs"
                          >
                            {itemIsRestoring ? (
                              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <ExternalLink className="h-3.5 w-3.5" />
                            )}
                            Open
                          </button>
                          {Number(item?.steps_count || 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => openFlowComposerFromHistory(item)}
                              disabled={isRestoring || isBusy}
                              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                              title="Create a reusable modular flow from this recording"
                            >
                              <GitBranch className="h-3.5 w-3.5" />
                              Create modular flow
                            </button>
                          )}
                          {showShareButton && (
                            <button
                              type="button"
                              onClick={() => openFeatureSharingPanel(item)}
                              disabled={isRestoring || featureSharingLoading}
                              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {featureSharingLoading && itemIsSelected ? (
                                <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Share2 className="h-3.5 w-3.5" />
                              )}
                              Share
                            </button>
                          )}
                          {itemIsOwner && (
                            <button
                              type="button"
                              onClick={() => openFeatureDeleteDialog(item)}
                              disabled={isRestoring}
                              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex min-h-[240px] items-center justify-center p-6 text-center">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      No captured feature flows found.
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Try another application or clear the search.
                    </p>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </HistoryDashboardShell>

      {featureHistoryDeleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">
                Delete Feature Flow
              </p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">
                Delete {featureHistoryDeleteTarget.feature_name || 'this captured flow'}?
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                This removes the captured flow and generated feature artifacts from history.
              </p>
            </div>

            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                Type <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-900">delete this</span> to confirm.
              </p>
              <input
                type="text"
                value={featureHistoryDeleteConfirmText}
                onChange={(event) => setFeatureHistoryDeleteConfirmText(event.target.value)}
                placeholder="delete this"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none ring-0 transition focus:border-rose-400"
                disabled={featureHistoryDeleteSaving}
              />
              {featureHistoryDeleteError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {featureHistoryDeleteError}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeFeatureDeleteDialog}
                className="secondary-btn justify-center px-4 py-2 text-sm"
                disabled={featureHistoryDeleteSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteFeatureCapture}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-700 bg-rose-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={featureHistoryDeleteSaving}
              >
                {featureHistoryDeleteSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {featureSharingPanelOpen && dashboardItem && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/35 p-4">
          <div className="flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  Feature Sharing
                </p>
                <h3 className="mt-1 text-lg font-bold text-slate-900">
                  Share {dashboardItem.feature_name || 'captured flow'}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Give application members access to this captured flow and its generated outputs.
                </p>
              </div>
              <button
                type="button"
                onClick={closeFeatureSharingPanel}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                disabled={featureSharingLoading}
                aria-label="Close feature sharing"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
              {featureSharingError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {featureSharingError}
                </div>
              )}

              {featureCanShare ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Add application member</p>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                    <select
                      value={featureShareUserId}
                      onChange={(event) => setFeatureShareUserId(event.target.value)}
                      className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-emerald-400"
                      disabled={featureSharingLoading || featureEligibleShareUsers.length === 0}
                    >
                      <option value="">
                        {featureEligibleShareUsers.length ? 'Select user' : 'No eligible users'}
                      </option>
                      {featureEligibleShareUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name || user.email || user.id}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={requestFeatureShare}
                      disabled={featureSharingLoading || !featureShareUserId}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-700 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {featureSharingLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                      {featureShareActionLabel}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  You can view this shared flow, but you do not have permission to share it with others.
                </div>
              )}

              {featureShareActionError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {featureShareActionError}
                </div>
              )}

              <section className="rounded-2xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                  <p className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Users className="h-4 w-4 text-slate-500" />
                    Shared users
                  </p>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                    {featureSharedUsers.length}
                  </span>
                </div>
                <div className="divide-y divide-slate-100">
                  {featureSharedUsers.length ? featureSharedUsers.map((user) => (
                    <div key={user.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{user.name || user.email || user.id}</p>
                        {user.email && <p className="truncate text-xs text-slate-500">{user.email}</p>}
                      </div>
                      {featureCanDirectShare && (
                        <button
                          type="button"
                          onClick={() => setFeatureRevokeTargetUser(user)}
                          disabled={featureSharingLoading}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <UserMinus className="h-3.5 w-3.5" />
                          Remove
                        </button>
                      )}
                    </div>
                  )) : (
                    <div className="px-4 py-4 text-sm text-slate-500">No direct shared users yet.</div>
                  )}
                </div>
              </section>

              {featurePendingShareRequests.length > 0 && (
                <section className="rounded-2xl border border-blue-200 bg-blue-50/60">
                  <div className="border-b border-blue-100 px-4 py-3">
                    <p className="text-sm font-semibold text-blue-900">Pending share requests</p>
                  </div>
                  <div className="divide-y divide-blue-100">
                    {featurePendingShareRequests.map((requestRow) => (
                      <div key={requestRow.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {requestRow.target_user_name || requestRow.target_user_email || requestRow.target_user_id}
                          </p>
                          <p className="truncate text-xs text-slate-600">
                            Requested by {requestRow.requested_by_name || requestRow.requested_by_email || 'unknown user'}
                          </p>
                        </div>
                        {featureCanDirectShare && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => reviewFeatureShareRequest(requestRow.id, 'approve')}
                              disabled={featureSharingLoading}
                              className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => reviewFeatureShareRequest(requestRow.id, 'reject')}
                              disabled={featureSharingLoading}
                              className="rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                onClick={closeFeatureSharingPanel}
                className="secondary-btn px-4 py-2 text-sm"
                disabled={featureSharingLoading}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {featureRevokeTargetUser && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/35 p-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">Remove Access</p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">
                Remove {featureRevokeTargetUser.name || featureRevokeTargetUser.email || 'this user'}?
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                They will lose access to this captured flow and its generated outputs.
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setFeatureRevokeTargetUser(null)}
                className="secondary-btn justify-center px-4 py-2 text-sm"
                disabled={featureSharingLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => revokeFeatureShareAccess(featureRevokeTargetUser.id)}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-700 bg-rose-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-rose-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={featureSharingLoading}
              >
                {featureSharingLoading ? 'Removing...' : 'Remove Access'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
    return typeof document === 'undefined'
      ? modalContent
      : createPortal(modalContent, document.body);
  };

  const renderBrdPickerModal = () => {
    if (!showBrdPicker) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/55 px-4 pb-8 pt-8 backdrop-blur-sm sm:pt-12"
        role="presentation"
        onClick={() => setShowBrdPicker(false)}
      >
        <div
          className="modal-shell flex max-h-[calc(100dvh-5rem)] max-w-5xl flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Choose BRD section"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Choose one BRD section</h3>
              <p className="mt-1 text-sm text-slate-600">
                Select a BRD only to view its sections. One section is used as the requirement context for this flow.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowBrdPicker(false)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100"
              aria-label="Close BRD picker"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 gap-0 overflow-hidden md:grid-cols-[0.9fr_1.1fr]">
            <section className="flex min-h-0 flex-col border-b border-slate-200 p-5 md:border-b-0 md:border-r">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Uploaded BRDs</p>
                  <p className="mt-1 text-sm text-slate-600">{workspaceProcessedBrds.length} available</p>
                </div>
                <button
                  type="button"
                  onClick={loadProcessedBrds}
                  disabled={brdListLoading}
                  className="secondary-btn px-3 py-2 text-sm"
                >
                  <RefreshCw className={`h-4 w-4 ${brdListLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              <div className="relative mt-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={brdSearchQuery}
                  onChange={(event) => setBrdSearchQuery(event.target.value)}
                  className="text-input pl-9"
                  placeholder="Search BRDs"
                  autoFocus
                />
              </div>

              <div className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {brdListLoading ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    Loading uploaded BRDs...
                  </div>
                ) : brdListError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                    {brdListError}
                  </div>
                ) : filteredBrds.length ? (
                  filteredBrds.map((brd) => {
                    const isActive = String(brd.pdf_id) === String(selectedBrdId);
                    return (
                      <button
                        key={brd.pdf_id}
                        type="button"
                        onClick={() => {
                          handleBrdScopeChange(brd.pdf_id);
                          setSectionSearchQuery('');
                        }}
                        disabled={!canManageCaptureBrd}
                        className={`w-full rounded-2xl border p-3 text-left transition-colors ${
                          isActive
                            ? 'border-teal-300 bg-teal-50 text-teal-950'
                            : 'border-slate-200 bg-white text-slate-800 hover:border-teal-200 hover:bg-teal-50/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ${
                            isActive ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
                          }`}
                          >
                            <FileText className="h-4 w-4" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold">
                              {brd.pdf_file || brd.pdf_id}
                            </span>
                            <span className="mt-1 block text-xs text-slate-500">
                              {brd.sections_count || 0} sections
                              {brd.brd_version ? ` · v${brd.brd_version}` : ''}
                            </span>
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                    <FileText className="mx-auto h-8 w-8 text-slate-400" />
                    <p className="mt-2 text-sm font-semibold text-slate-900">No matching BRDs</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Attach context below or adjust your search.
                    </p>
                  </div>
                )}
              </div>
            </section>

            <section className="flex min-h-0 flex-col p-5">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Sections</p>
                  <p className="mt-1 truncate text-sm text-slate-600">
                    {selectedBrd
                      ? selectedBrdSection
                        ? brdSectionLabel(selectedBrdSection)
                        : `${selectedBrd.pdf_file || selectedBrd.pdf_id} · choose one section`
                      : 'Select a BRD to view sections'}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  {selectedBrdSection && (
                    <button
                      type="button"
                      onClick={() => setSelectedBrdSectionNumber('')}
                      disabled={!canManageCaptureBrd}
                      className="secondary-btn px-3 py-2 text-sm"
                    >
                      Clear section
                    </button>
                  )}
                  {selectedBrd && (
                    <button
                      type="button"
                      onClick={() => confirmSelectedBrdSection()}
                      disabled={
                        !canManageCaptureBrd
                        || !selectedBrdSection
                        || brdSectionsLoading
                        || Boolean(brdSectionsError)
                      }
                      title={!selectedBrdSection ? 'Select one section before continuing.' : undefined}
                      className="primary-btn px-3 py-2 text-sm"
                    >
                      Done
                    </button>
                  )}
                </div>
              </div>

              <div className="relative mt-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={sectionSearchQuery}
                  onChange={(event) => setSectionSearchQuery(event.target.value)}
                  className="text-input pl-9"
                  placeholder="Search sections"
                  disabled={!selectedBrdId || !canManageCaptureBrd || brdSectionsLoading}
                />
              </div>

              <div className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {!selectedBrdId ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                    <Search className="mx-auto h-8 w-8 text-slate-400" />
                    <p className="mt-2 text-sm font-semibold text-slate-900">Start with a BRD</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Search and select a BRD from the left panel.
                    </p>
                  </div>
                ) : brdSectionsLoading ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                    Loading stored sections...
                  </div>
                ) : brdSectionsError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                    <p>{brdSectionsError}</p>
                    <button
                      type="button"
                      onClick={() => setBrdSectionsReloadToken((current) => current + 1)}
                      disabled={!canManageCaptureBrd}
                      className="mt-3 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Retry stored sections
                    </button>
                  </div>
                ) : filteredBrdSections.length ? (
                  filteredBrdSections.map((section) => {
                    const value = String(section?.number ?? section?.section_number ?? '');
                    const isActive = (
                      String(section?.number ?? section?.section_number ?? '') === String(selectedBrdSectionNumber)
                      || String(section?.section_number ?? section?.number ?? '') === String(selectedBrdSectionNumber)
                    );
                    return (
                      <button
                        key={`${value}-${section?.section_number || ''}`}
                        type="button"
                        onClick={() => {
                          setSelectedBrdSectionNumber(value);
                        }}
                        disabled={!canManageCaptureBrd}
                        className={`w-full rounded-2xl border p-3 text-left transition-colors ${
                          isActive
                            ? 'border-teal-300 bg-teal-50'
                            : 'border-slate-200 bg-white hover:border-teal-200 hover:bg-teal-50/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            isActive ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
                          }`}
                          >
                            {isActive ? <CheckCircle2 className="h-4 w-4" /> : section?.number || section?.section_number || '-'}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold leading-5 text-slate-900">
                              {brdSectionLabel(section)}
                            </span>
                            <span className="mt-1 block text-xs text-slate-500">
                              Pages {section?.indexed_pages || '-'} · Images {section?.image_count ?? 0}
                            </span>
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : brdSections.length ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                    <FileSearch className="mx-auto h-8 w-8 text-slate-400" />
                    <p className="mt-2 text-sm font-semibold text-slate-900">No matching sections</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Try another search term.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center">
                    <FileSearch className="mx-auto h-8 w-8 text-slate-400" />
                    <p className="mt-2 text-sm font-semibold text-slate-900">No stored sections available</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Retry the stored-section read, or upload and index the BRD again.
                    </p>
                    <button
                      type="button"
                      onClick={() => setBrdSectionsReloadToken((current) => current + 1)}
                      disabled={!canManageCaptureBrd}
                      className="secondary-btn mt-3 px-3 py-2 text-sm"
                    >
                      Retry stored sections
                    </button>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="border-t border-slate-200 bg-slate-50/80 px-5 py-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">No extracted BRD yet?</p>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  PDF and Word files are uploaded, sectioned, and indexed. Text and Markdown remain capture-only attachments.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <label className={`secondary-btn px-3 py-2 text-sm ${
                  brdIndexing
                    ? 'cursor-wait opacity-60'
                    : canManageCaptureBrd
                      ? 'cursor-pointer'
                      : 'cursor-not-allowed opacity-60'
                }`}>
                  {brdIndexing
                    ? <LoaderCircle className="h-4 w-4 animate-spin" />
                    : <Upload className="h-4 w-4" />}
                  {brdIndexing ? 'Indexing...' : 'Upload & index BRD'}
                  <input
                    type="file"
                    accept={CONTEXT_FILE_ACCEPT}
                    className="hidden"
                    disabled={brdIndexing || !canManageCaptureBrd}
                    onChange={(event) => {
                      const selectedFile = event.target.files?.[0] || null;
                      event.target.value = '';
                      handleFeatureBrdFileChange(selectedFile);
                    }}
                  />
                </label>
                {(brdContextFile || brdContextText.trim()) && (
                  <button
                    type="button"
                    className="secondary-btn px-3 py-2 text-sm"
                    disabled={!canManageCaptureBrd}
                    onClick={() => {
                      setBrdContextFile(null);
                      setBrdContextText('');
                      setBrdFileError('');
                    }}
                  >
                    <X className="h-4 w-4" />
                    Remove
                  </button>
                )}
              </div>
            </div>

            {(brdIndexing || brdIndexedFileName || brdIndexingError) && (
              <div className={`mt-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                brdIndexingError
                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                  : 'border-indigo-200 bg-white text-slate-700'
              }`}
              >
                {brdIndexing
                  ? <LoaderCircle className="h-4 w-4 flex-shrink-0 animate-spin" />
                  : brdIndexingError
                    ? <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    : <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-indigo-600" />}
                <span className="min-w-0 truncate">
                  {brdIndexingError
                    || (brdIndexing
                      ? `Uploading and indexing ${brdIndexedFileName || 'BRD'}...`
                      : `${brdIndexedFileName} uploaded and indexed`)}
                </span>
              </div>
            )}

            {(brdContextFile || brdFileError) && (
              <div className={`mt-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                brdFileError
                  ? 'border-rose-200 bg-rose-50 text-rose-700'
                  : 'border-teal-200 bg-white text-slate-700'
              }`}
              >
                <FileText className="h-4 w-4 flex-shrink-0" />
                <span className="min-w-0 truncate">
                  {brdFileError || `${brdContextFile.name} (${Math.ceil(brdContextFile.size / 1024)} KB)`}
                </span>
              </div>
            )}

            <textarea
              value={brdContextText}
              onChange={(event) => setBrdContextText(event.target.value)}
              className="text-input mt-3 min-h-[76px] resize-y bg-white"
              placeholder="Paste feature-specific acceptance criteria, field rules, validations, or edge cases."
              disabled={!canManageCaptureBrd}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderOpeningFeatureSkeleton = (featureLabel = 'captured feature') => (
    <section className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Opening feature</p>
          <h3 className="mt-1 truncate text-lg font-bold text-slate-900">{featureLabel}</h3>
          <p className="mt-1 text-sm text-slate-600">
            Loading the captured flow, steps, and generated outputs.
          </p>
        </div>
        <LoaderCircle className="mt-1 h-5 w-5 flex-shrink-0 animate-spin text-blue-600" />
      </div>
      <div className="mt-5 animate-pulse space-y-3">
        <div className="rounded-xl border border-white/80 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-100" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-1/3 rounded bg-slate-200" />
              <div className="h-3 w-2/3 rounded bg-slate-100" />
            </div>
          </div>
        </div>
        {[0, 1, 2].map((row) => (
          <div key={`opening-feature-row-${row}`} className="rounded-xl border border-white/80 bg-white px-4 py-3">
            <div className="flex items-center gap-4">
              <div className="h-3 w-8 rounded bg-slate-100" />
              <div className="h-4 w-36 rounded bg-slate-200" />
              <div className="h-3 flex-1 rounded bg-slate-100" />
              <div className="h-7 w-20 rounded-lg bg-blue-100" />
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  const renderApplicationsDashboard = () => (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <p className="workspace-accent-text text-xs font-semibold uppercase tracking-[0.18em]">
              Feature Areas
            </p>
            <h3 className="mt-1 text-xl font-bold text-slate-900">Select a feature area</h3>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Only feature areas available in your applications are shown here.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateApplicationPanel}
            disabled={!canOpenCreateApplicationPanel}
            className="primary-btn workspace-accent-button h-10 px-4 py-0 text-sm disabled:shadow-none"
            title={createApplicationButtonTitle}
          >
            <Plus className="h-4 w-4" />
            New Feature Area
          </button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_240px]">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={dashboardApplicationSearch}
              onChange={(event) => setDashboardApplicationSearch(event.target.value)}
              className="text-input pl-9"
              placeholder="Search feature areas"
              aria-label="Search feature areas"
            />
          </label>
          <select
            value={dashboardWorkspaceFilter}
            onChange={(event) => setDashboardWorkspaceFilter(event.target.value)}
            className="text-input bg-white"
            disabled={workspaceLoading}
            aria-label="Filter feature areas by application"
          >
            <option value="">All applications</option>
            {workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}
              </option>
            ))}
          </select>
        </div>

        {showCreateApplicationPanel && (
          <div className="mt-4 overflow-hidden rounded-2xl border border-teal-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white">
                    <Briefcase className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="workspace-accent-text text-xs font-semibold uppercase tracking-[0.18em]">
                      New Feature Area
                    </p>
                    <h4 className="mt-0.5 text-base font-bold text-slate-950">Create feature area</h4>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  <span className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-800">
                    {effectiveDashboardWorkspaceName || 'Select application above'}
                  </span>
                  <button
                    type="button"
                    onClick={closeCreateApplicationPanel}
                    disabled={creatingDashboardApplication}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Close create application"
                    title="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-3 p-4 sm:p-5">
              {!workspaces.length && (
                <div className="rounded-2xl border border-dashed border-teal-200 bg-white/80 p-4 shadow-sm">
                  {canCreateWorkspace ? (
                    <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_auto] lg:items-end">
                      <label className="block">
                        <span className="text-sm font-semibold text-slate-700">
                          Create an application first
                        </span>
                        <input
                          className="text-input mt-2 bg-white"
                          placeholder="Application name"
                          value={newWorkspaceName}
                          onChange={(event) => {
                            setNewWorkspaceName(event.target.value);
                            if (workspaceError) setWorkspaceError('');
                          }}
                          disabled={creatingWorkspace}
                        />
                      </label>
                      <button
                        type="button"
                        className="secondary-btn px-3 py-2 text-sm"
                        onClick={handleCreateWorkspace}
                        disabled={creatingWorkspace || !String(newWorkspaceName || '').trim()}
                      >
                        {creatingWorkspace ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        {creatingWorkspace ? 'Creating...' : 'Create application'}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-sm text-amber-700">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">No application access yet</p>
                        <p className="mt-1 text-amber-700/90">
                          Ask a Lead or Admin to add you to an application before creating a feature area.
                        </p>
                      </div>
                    </div>
                  )}
                  {workspaceError && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>{workspaceError}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                <label className="block min-w-0">
                    <span className="text-sm font-semibold text-slate-800">Feature area name</span>
                    <div className="relative mt-2">
                      <Briefcase className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-teal-500" />
                      <input
                        value={dashboardCreateApplication.name}
                        onChange={(event) => {
                          setDashboardCreateApplication((current) => ({ ...current, name: event.target.value }));
                          if (dashboardCreateApplicationError) setDashboardCreateApplicationError('');
                        }}
                        className="text-input h-11 bg-white pl-9 shadow-sm"
                        placeholder="Example: Loan Origination"
                        disabled={creatingDashboardApplication}
                      />
                    </div>
                </label>

                <button
                  type="button"
                  onClick={handleCreateDashboardApplication}
                  disabled={
                    creatingDashboardApplication
                    || !String(dashboardCreateApplication.name || '').trim()
                    || !String(dashboardCreateApplication.application_id || effectiveDashboardWorkspaceId || '').trim()
                  }
                  className="primary-btn workspace-accent-button h-11 px-5 py-0 text-sm disabled:shadow-none"
                >
                  {creatingDashboardApplication ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Create
                </button>
              </div>

              {dashboardCreateApplicationError && (
                <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm text-rose-700">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{dashboardCreateApplicationError}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {dashboardApplicationsError && (
        <div className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{dashboardApplicationsError}</span>
        </div>
      )}

      {dashboardApplicationsLoading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map((row) => (
            <div key={`application-dashboard-loading-${row}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="animate-pulse space-y-4">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-slate-200" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-4 w-2/3 rounded bg-slate-200" />
                    <div className="h-3 w-4/5 rounded bg-slate-100" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : visibleDashboardApplications.length ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="divide-y divide-slate-100">
          {visibleDashboardApplications.map((application) => {
            const appName = application?.name || 'Untitled feature area';
            const appBaseLabel = applicationBaseLabel(application);
            const appEnvironmentLabel = applicationEnvironmentLabel(application);
            const workspaceName = applicationWorkspaceName(application);
            const applicationOwnerId = String(application?.owner_user_id || '').trim();
            const currentUserId = String(currentUser?.id || currentUser?.user_id || '').trim();
            const canDeleteApplication = !applicationOwnerId || (currentUserId && applicationOwnerId === currentUserId);
            const isDeletingApplication = deletingApplicationId === String(application?.id || '');
            return (
              <div
                key={application.id}
                className="workspace-accent-row flex flex-col gap-3 px-4 py-4 transition-colors lg:flex-row lg:items-center lg:justify-between"
              >
                <button
                  type="button"
                  onClick={() => openApplicationDashboard(application)}
                  className="flex min-w-0 flex-1 items-start gap-3 text-left"
                >
                  <span className="workspace-context-icon inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-white">
                    <Briefcase className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-base font-bold text-slate-900" title={appName}>
                      {appName}
                    </span>
                    {appBaseLabel && (
                      <span className="mt-1 block truncate text-xs text-slate-500" title={appBaseLabel}>
                        {appBaseLabel}
                      </span>
                    )}
                    <span className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">
                        {workspaceName || 'Application not set'}
                      </span>
                      {appEnvironmentLabel && (
                        <span className="rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-teal-700">
                          {appEnvironmentLabel}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-600 lg:justify-end">
                  <span className="workspace-accent-chip rounded-md border px-2.5 py-1">
                    {formatCountLabel(application?.features_count || 0, 'feature')}
                  </span>
                  <span className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1">
                    {formatCountLabel(application?.flows_count || 0, 'flow')}
                  </span>
                  <span className="rounded-md border border-teal-100 bg-teal-50 px-2.5 py-1 text-teal-700">
                    {formatCountLabel(application?.test_cases_count || 0, 'case')}
                  </span>
                  <span className="rounded-md border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-indigo-700">
                    {formatCountLabel(application?.scripts_count || 0, 'script')}
                  </span>
                  {canDeleteApplication && (
                    <button
                      type="button"
                      onClick={() => handleDeleteApplication(application)}
                      disabled={Boolean(deletingApplicationId)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label={`Delete ${appName}`}
                      title={`Delete ${appName}`}
                    >
                      {isDeletingApplication ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      ) : (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <Briefcase className="mx-auto h-10 w-10 text-slate-400" />
          <h3 className="mt-3 text-lg font-bold text-slate-900">No feature areas found</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600">
            No feature areas are available for the selected application.
          </p>
          <button
            type="button"
            onClick={openCreateApplicationPanel}
            disabled={!canOpenCreateApplicationPanel}
            className="primary-btn workspace-accent-button mx-auto mt-4 px-4 py-2 text-sm disabled:shadow-none"
            title={createApplicationButtonTitle}
          >
            <Plus className="h-4 w-4" />
            New Feature Area
          </button>
        </section>
      )}
    </div>
  );

  const renderApplicationEnvironmentRow = () => {
    const statusLabel = applicationAuthLoading
      ? 'Checking'
      : applicationSessionAvailable && applicationLoginScriptAvailable
        ? 'Fast login ready'
        : applicationSessionAvailable
          ? 'Saved session ready'
          : applicationLoginScriptAvailable
            ? 'Login script ready'
        : 'Not captured';
    const authenticationReady = applicationSessionAvailable || applicationLoginScriptAvailable;
    const statusClass = authenticationReady
      ? 'border-cyan-200 bg-cyan-50 text-cyan-800'
      : 'border-slate-200 bg-slate-50 text-slate-600';
    const detail = applicationSessionAvailable && applicationLoginScriptAvailable
      ? `Captured ${applicationAuthCapturedLabel} · restores the session first and uses automatic login only if needed`
      : applicationSessionAvailable
        ? `Captured ${applicationAuthCapturedLabel} · restores the saved session before recording`
        : applicationLoginScriptAvailable
          ? `Captured ${applicationAuthCapturedLabel} · signs in automatically before recording`
      : 'No automated login. Recording starts immediately from the Application URL.';

    return (
      <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${
            authenticationReady ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {authenticationReady ? <ShieldCheck className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-bold text-slate-900">Automation environment</p>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${statusClass}`}>
                {statusLabel}
              </span>
            </div>
            <p className="mt-1 truncate text-sm text-slate-600">{detail}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowApplicationEnvironmentModal(true)}
          disabled={!effectiveFeatureAuthSourceId}
          className="secondary-btn h-10 w-full justify-center px-4 text-sm disabled:cursor-not-allowed disabled:opacity-60 lg:w-auto"
          title={!effectiveFeatureAuthSourceId ? 'Application source is not ready yet.' : undefined}
        >
          {applicationAuthLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
          Environment
        </button>
      </div>
    );
  };

  const renderSelectedApplicationDashboard = () => {
    const application = selectedDashboardApplication || selectedApplication;
    if (!application) {
      return (
        <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
          <Briefcase className="mx-auto h-10 w-10 text-slate-400" />
          <h3 className="mt-3 text-lg font-bold text-slate-900">Feature area not selected</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600">
            Go back to the feature area list and choose one to view its features.
          </p>
          <button
            type="button"
            onClick={returnToApplicationsDashboard}
            className="secondary-btn mx-auto mt-4 px-4 py-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Feature Areas
          </button>
        </section>
      );
    }

    const featureCount = Number(application?.features_count || applicationFeatureGroups.length || 0);
    const flowCount = Number(application?.flows_count || applicationFeatureItems.length || 0);
    const caseCount = Number(application?.test_cases_count || 0);
    const scriptCount = Number(application?.scripts_count || 0);
    const applicationOwnerId = String(application?.owner_user_id || '').trim();
    const currentUserId = String(currentUser?.id || currentUser?.user_id || '').trim();
    const canDeleteApplication = !applicationOwnerId || (currentUserId && applicationOwnerId === currentUserId);
    const canManageApplication = canDeleteApplication;
    const isDeletingApplication = deletingApplicationId === String(application?.id || '');
    const isRenamingApplication = renamingApplicationId === String(application?.id || '');
    const appBaseLabel = applicationBaseLabel(application);
    const appEnvironmentLabel = applicationEnvironmentLabel(application);
    const openingFeatureItem = featureHistoryRestoreId
      ? applicationFeatureItems.find((item) => String(item?.id || '') === String(featureHistoryRestoreId || ''))
      : null;
    const isOpeningApplicationFeature = Boolean(openingFeatureItem);

    return (
      <div className="min-w-0 space-y-5">
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {canDeleteApplication && (
            <div className="flex flex-wrap items-center justify-end gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3 md:px-5">
              <button
                type="button"
                onClick={() => handleDeleteApplication(application)}
                disabled={Boolean(deletingApplicationId)}
                className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isDeletingApplication ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Delete feature area
              </button>
            </div>
          )}

          <div className="p-4 md:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <span className="workspace-context-icon inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-white">
                  <Briefcase className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="workspace-accent-text text-[11px] font-bold uppercase tracking-[0.16em]">
                    Feature Area Profile
                  </p>
                  {isRenamingApplication ? (
                    <div className="mt-2 min-w-0">
                      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="text"
                          value={applicationRenameValue}
                          onChange={(event) => {
                            setApplicationRenameValue(event.target.value);
                            setApplicationRenameError('');
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') handleRenameApplication(application);
                            if (event.key === 'Escape') closeApplicationRename();
                          }}
                          className="text-input min-h-9 max-w-md text-sm"
                          placeholder="Application name"
                          autoFocus
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleRenameApplication(application)}
                            disabled={applicationRenameSaving}
                            className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {applicationRenameSaving ? (
                              <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Save className="h-3.5 w-3.5" />
                            )}
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={closeApplicationRename}
                            disabled={applicationRenameSaving}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Cancel application rename"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {applicationRenameError && (
                        <p className="mt-2 text-xs font-semibold text-rose-700">{applicationRenameError}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                        <h3 className="truncate text-lg font-bold text-slate-900" title={application?.name}>
                          {application?.name || 'Untitled feature area'}
                        </h3>
                        {canManageApplication && (
                          <button
                            type="button"
                            onClick={() => beginRenameApplication(application)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                            aria-label={`Rename ${application?.name || 'feature area'}`}
                            title="Rename feature area"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="mt-1 text-sm leading-5 text-slate-600">
                        Capture journeys and continue them into reusable automation outputs.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-slate-600 lg:justify-end">
                {appBaseLabel && (
                  <span className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
                    <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="max-w-[22rem] truncate">{appBaseLabel}</span>
                  </span>
                )}
                <span className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 font-semibold">
                  {applicationWorkspaceName(application) || 'Application not set'}
                </span>
                {appEnvironmentLabel && (
                  <span className="rounded-lg border border-teal-200 bg-teal-50 px-2.5 py-1.5 font-semibold text-teal-700">
                    {appEnvironmentLabel}
                  </span>
                )}
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
              {[
                ['Features', featureCount, Route],
                ['Flows', flowCount, GitBranch],
                ['Cases', caseCount, ListChecks],
                ['Scripts', scriptCount, FileCode2],
              ].map(([label, value, Icon]) => (
                <div
                  key={label}
                  className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5"
                >
                  <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-teal-700 shadow-sm ring-1 ring-inset ring-slate-200">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <dt className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</dt>
                    <dd className="text-2xl font-bold leading-tight text-slate-900">{value}</dd>
                  </div>
                </div>
              ))}
            </dl>
            {renderApplicationEnvironmentRow()}
          </div>
        </section>

        {isOpeningApplicationFeature ? (
          renderOpeningFeatureSkeleton(openingFeatureItem?.feature_name || 'captured feature')
        ) : (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Features</p>
              <h3 className="mt-1 text-lg font-bold text-slate-900">Captured features in this feature area</h3>
            </div>
            <div className="flex w-full items-center gap-3 lg:w-auto">
              <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 text-xs font-semibold text-slate-600">
                <input
                  type="checkbox"
                  checked={showArchivedFeatures}
                  onChange={(event) => setShowArchivedFeatures(event.target.checked)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                Show archived
              </label>
              <label className="relative block w-full lg:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={applicationFeatureSearch}
                  onChange={(event) => setApplicationFeatureSearch(event.target.value)}
                  className="text-input pl-9"
                  placeholder="Search features"
                />
              </label>
            </div>
          </div>

          {applicationFeatureError && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{applicationFeatureError}</span>
            </div>
          )}

          {applicationFeatureLoading ? (
            <div className="mt-4 space-y-3">
              {[0, 1, 2].map((row) => (
                <div key={`application-feature-loading-${row}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="animate-pulse space-y-3">
                    <div className="h-4 w-56 rounded bg-slate-200" />
                    <div className="h-3 w-4/5 rounded bg-slate-100" />
                    <div className="flex gap-2">
                      <div className="h-7 w-20 rounded-lg bg-slate-100" />
                      <div className="h-7 w-20 rounded-lg bg-slate-100" />
                      <div className="h-7 w-20 rounded-lg bg-slate-100" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredApplicationFeatureGroups.length ? (
            <div className="mt-4 grid gap-3">
              {filteredApplicationFeatureGroups.map((group) => {
                    const latest = group.latest || {};
                    const hasSteps = Number(latest.steps_count || 0) > 0;
                    const hasCases = Number(latest.test_cases_count || 0) > 0;
                    const hasPom = Number(latest.pom_pages_count || 0) > 0;
                    const hasScripts = Number(latest.scripts_count || 0) > 0;
                    const canDeleteFeature = Boolean(latest.is_owner);
                    const isDeletingFeature = deletingFeatureCaptureId === String(latest?.id || '');
                    const isArchivingFeature = archivingFeatureCaptureId === String(latest?.id || '');
                    const isArchivedFeature = Boolean(latest?.archived);
                    const isRecapturingFeature = recapturingFeatureCaptureId === String(latest?.id || '');
                    const isOpeningFeature = featureHistoryRestoreId === String(latest?.id || '');
                    const isRenamingFeature = renamingFeatureId === String(latest?.id || '');
                    const isSavingFeatureRename = featureRenameSavingId === String(latest?.id || '');
                    return (
                      <article
                        key={group.id}
                        className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:border-teal-200 hover:shadow-md ${
                          isArchivedFeature ? 'border-dashed border-slate-300' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex flex-col gap-4 p-4 sm:p-5 xl:flex-row xl:items-start xl:justify-between">
                          <div className="flex min-w-0 flex-1 items-start gap-3.5">
                            <span
                              className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
                                hasSteps ? 'bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-100' : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              <Route className="h-5 w-5" />
                            </span>
                            <div className="min-w-0 flex-1">
                              {isRenamingFeature ? (
                                <div className="max-w-xl">
                                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <input
                                      type="text"
                                      value={featureRenameValue}
                                      onChange={(event) => {
                                        setFeatureRenameValue(event.target.value);
                                        setFeatureRenameError('');
                                      }}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') handleRenameApplicationFeature(latest);
                                        if (event.key === 'Escape') closeFeatureRename();
                                      }}
                                      className="text-input min-h-9 text-sm"
                                      placeholder="Feature name"
                                      autoFocus
                                    />
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleRenameApplicationFeature(latest)}
                                        disabled={isSavingFeatureRename}
                                        className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-100 disabled:cursor-not-allowed disabled:opacity-60"
                                      >
                                        {isSavingFeatureRename ? (
                                          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                          <Save className="h-3.5 w-3.5" />
                                        )}
                                        Save
                                      </button>
                                      <button
                                        type="button"
                                        onClick={closeFeatureRename}
                                        disabled={isSavingFeatureRename}
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        aria-label="Cancel feature rename"
                                        title="Cancel"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                  {featureRenameError && (
                                    <p className="mt-2 text-xs font-semibold text-rose-700">{featureRenameError}</p>
                                  )}
                                </div>
                              ) : (
                                <h4 className="flex items-center gap-2 truncate text-base font-bold text-slate-900" title={group.feature_name}>
                                  <span className="truncate">{group.feature_name || 'Untitled feature'}</span>
                                  {isArchivedFeature && (
                                    <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                      Archived
                                    </span>
                                  )}
                                </h4>
                              )}
                              <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                                <span className="inline-flex items-center gap-1.5 font-semibold text-slate-700">
                                  <span className={`h-1.5 w-1.5 rounded-full ${hasSteps ? 'bg-teal-500' : 'bg-slate-300'}`} aria-hidden="true" />
                                  {statusLabel(latest.status)}
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                  <CalendarDays className="h-3.5 w-3.5" />
                                  Updated {formatHistoryDate(latest.updated_at || latest.created_at)}
                                </span>
                                <span className="min-w-0 max-w-full truncate" title={latest.start_url || latest.id}>
                                  {group.flows.length > 1
                                    ? `Latest: ${flowDisplayName(latest, applicationFeatureItems)}`
                                    : (latest.start_url || latest.id)}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-1.5 xl:flex-shrink-0 xl:justify-end">
                            {canDeleteFeature && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => beginRenameApplicationFeature(latest)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                                  disabled={isBusy || Boolean(featureHistoryRestoreId) || !latest.id || Boolean(featureRenameSavingId)}
                                  aria-label={`Rename ${group.feature_name || 'feature'}`}
                                  title="Rename feature"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleArchiveApplicationFeature(latest)}
                                  disabled={Boolean(archivingFeatureCaptureId) || Boolean(deletingFeatureCaptureId) || Boolean(featureHistoryRestoreId)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                                  aria-label={`${isArchivedFeature ? 'Restore' : 'Archive'} ${group.feature_name || 'feature'}`}
                                  title={isArchivedFeature
                                    ? `Restore ${group.feature_name || 'feature'}`
                                    : `Archive ${group.feature_name || 'feature'} (keeps the data and the name)`}
                                >
                                  {isArchivingFeature ? (
                                    <LoaderCircle className="h-4 w-4 animate-spin" />
                                  ) : isArchivedFeature ? (
                                    <ArchiveRestore className="h-4 w-4" />
                                  ) : (
                                    <Archive className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteApplicationFeature(latest)}
                                  disabled={Boolean(deletingFeatureCaptureId) || Boolean(archivingFeatureCaptureId) || Boolean(featureHistoryRestoreId)}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                                  aria-label={`Delete ${group.feature_name || 'feature'} permanently`}
                                  title={`Delete ${group.feature_name || 'feature'} permanently (frees the name)`}
                                >
                                  {isDeletingFeature ? (
                                    <LoaderCircle className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </button>
                                <span className="mx-1 hidden h-6 w-px bg-slate-200 sm:block" aria-hidden="true" />
                              </>
                            )}
                            {hasSteps && (
                              <button
                                type="button"
                                onClick={() => openFlowComposerFromHistory(latest)}
                                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isBusy || Boolean(featureHistoryRestoreId) || !latest.id}
                                title="Create a reusable modular flow from this recording"
                              >
                                <GitBranch className="h-3.5 w-3.5" />
                                Create modular flow
                              </button>
                            )}
                            {canDeleteFeature && (
                              <button
                                type="button"
                                onClick={() => handleRecaptureFeatureCapture(latest)}
                                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                disabled={isBusy || Boolean(featureHistoryRestoreId) || !latest.id || Boolean(recapturingFeatureCaptureId)}
                              >
                                {isRecapturingFeature ? (
                                  <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-3.5 w-3.5" />
                                )}
                                Recapture
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => restoreFeatureCaptureFromHistory(latest, hasSteps ? 'captured-flow' : 'capture')}
                              className="primary-btn group h-9 rounded-lg px-3.5 py-0 text-xs"
                              disabled={isBusy || Boolean(featureHistoryRestoreId) || !latest.id}
                            >
                              {isOpeningFeature ? (
                                <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                              ) : null}
                              {isOpeningFeature ? 'Opening' : 'Open'}
                              {!isOpeningFeature && (
                                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                          <ol className="flex flex-wrap items-center gap-1" aria-label={`${group.feature_name || 'Feature'} progress`}>
                            {[
                              { id: 'flow', label: 'Flow', value: formatCountLabel(latest.steps_count || 0, 'step'), done: hasSteps },
                              { id: 'cases', label: 'Cases', value: latest.test_cases_count || 0, done: hasCases, stageId: 'cases' },
                              { id: 'pom', label: 'POM', value: formatCountLabel(latest.pom_pages_count || 0, 'page'), done: hasPom, stageId: 'pom' },
                              { id: 'scripts', label: 'Scripts', value: latest.scripts_count || 0, done: hasScripts, stageId: 'scripts' },
                            ].map((stage, index) => {
                              const chipClass = stage.done
                                ? 'inline-flex h-7 items-center gap-1.5 rounded-full border border-teal-200 bg-white px-2.5 text-xs font-semibold text-teal-800'
                                : 'inline-flex h-7 items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-2.5 text-xs font-medium text-slate-400';
                              const chipContent = (
                                <>
                                  {stage.done
                                    ? <CheckCircle2 className="h-3.5 w-3.5" />
                                    : <Circle className="h-3.5 w-3.5" />}
                                  {stage.label}
                                  {stage.done && <span className="font-medium text-teal-700/80">{stage.value}</span>}
                                </>
                              );
                              return (
                                <li key={stage.id} className="flex items-center gap-1">
                                  {index > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" aria-hidden="true" />}
                                  {stage.done && stage.stageId ? (
                                    <button
                                      type="button"
                                      onClick={() => restoreFeatureCaptureFromHistory(latest, stage.stageId)}
                                      className={`${chipClass} transition-colors hover:border-teal-300 hover:bg-teal-50 disabled:cursor-not-allowed disabled:opacity-60`}
                                      disabled={isBusy || Boolean(featureHistoryRestoreId)}
                                      title={`Open ${stage.label}`}
                                    >
                                      {chipContent}
                                    </button>
                                  ) : (
                                    <span className={chipClass}>{chipContent}</span>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                          <p className="text-xs font-medium text-slate-500">{formatCountLabel(group.flows.length, 'flow')}</p>
                        </div>
                      </article>
                    );
                  })}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-7 text-center">
              <FileSearch className="mx-auto h-9 w-9 text-slate-400" />
              <h4 className="mt-3 text-base font-bold text-slate-900">No features found</h4>
              <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-slate-600">
                Capture the first feature journey for this feature area to populate the dashboard.
              </p>
              <button
                type="button"
                onClick={() => startFeatureCaptureForApplication(application)}
                className="primary-btn mx-auto mt-4 px-4 py-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                New Feature Capture
              </button>
            </div>
          )}
        </section>
        )}
      </div>
    );
  };

  const renderApplicationDashboardContent = () => (
    captureDashboardView === 'application'
      ? renderSelectedApplicationDashboard()
      : renderApplicationsDashboard()
  );

  const renderFlowContextPanel = () => {
    const savedNotes = String(session?.notes || '').trim();
    const hasSavedContent = savedNotes.length > 0;
    const isEditing = contextEditing || !hasSavedContent;
    const isDirty = String(notes || '').trim() !== savedNotes;
    return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Test context</h3>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Rules added here are applied every time test cases and scripts are generated for this flow.
          </p>
        </div>
        {!isEditing ? (
          <button
            type="button"
            onClick={() => { setContextError(''); setContextEditing(true); }}
            className="secondary-btn h-8 flex-shrink-0 px-3 py-0 text-xs"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
        ) : hasSavedContent && (
          <span className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
            isDirty ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {isDirty ? 'Unsaved changes' : 'Editing'}
          </span>
        )}
      </div>

      {isEditing ? (
        <>
          <label className="mt-3 block">
            <span className="sr-only">Flow rules and additional context</span>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={contextSaving}
              className="text-input min-h-[168px] resize-y leading-6 disabled:opacity-60"
              placeholder="Add required validations, success end state, roles, data constraints, or assertions to include in test cases and scripts"
            />
          </label>
          {contextError && (
            <p className="mt-2 text-xs text-rose-600">{contextError}</p>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            {hasSavedContent && (
              <button
                type="button"
                onClick={handleCancelTestContext}
                disabled={contextSaving}
                className="secondary-btn h-9 px-3 py-0 text-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSaveTestContext}
              disabled={contextSaving || !isDirty || !session?.id}
              className="primary-btn h-9 px-3 py-0 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            >
              {contextSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {contextSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      ) : (
        <div className="mt-3 whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-sm leading-6 text-slate-700">
          {savedNotes}
        </div>
      )}
    </section>
    );
  };

  const signInMode = applicationSessionAvailable
    ? {
      label: 'Fast session',
      icon: ShieldCheck,
      copy: `The saved session is restored first${applicationLoginScriptAvailable ? '; automatic login runs only if it has expired' : ''}. Recording then begins on the current page.`,
    }
    : applicationLoginScriptAvailable
      ? {
        label: 'Auto login',
        icon: KeyRound,
        copy: 'The saved login runs here first. Recording begins on the page where the application lands.',
      }
      : {
        label: 'Direct start',
        icon: Link2,
        copy: 'No login script is configured, so recording begins as soon as this page loads.',
      };
  const SignInModeIcon = signInMode.icon;
  const captureWindowCopy = recordContinuation
    ? 'Recording waits for you to click Start Capture in the extension after reaching the new part of the flow. Later, put the shared recording before this one in Create flow from recordings.'
    : recordSharedLogin
      ? 'The saved environment login is skipped so you can record the login actions as part of this shared flow.'
      : applicationAuthAvailable
        ? `The saved login for ${captureEnvironmentName || 'this environment'} is applied to that window before recording starts, so the sign-in is not captured as a step.`
        : 'No saved login is configured for this application, so recording starts on the target page. Add one under Automation environment to skip signing in.';
  const capturedFeatureName = featureName || session?.feature_name || 'Untitled feature';
  const capturedAtValue = session?.updated_at || session?.created_at || '';
  const capturedAtLabel = capturedAtValue ? formatHistoryDate(capturedAtValue) : '';
  const activeRecordingMode = recordContinuation
    ? 'continuation'
    : recordSharedLogin
      ? 'shared-login'
      : 'full';
  // The two recording options are mutually exclusive, so they are chosen as one mode.
  const selectRecordingMode = (modeId) => {
    setRecordContinuation(modeId === 'continuation');
    setRecordSharedLogin(modeId === 'shared-login');
  };

  const renderSignInModeBadge = () => (
    <span
      className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[11px] font-semibold ${
        applicationAuthAvailable ? 'border-cyan-200 text-cyan-800' : 'border-slate-200 text-slate-600'
      }`}
    >
      <SignInModeIcon className="h-3 w-3" />
      {signInMode.label}
    </span>
  );

  const renderBrdContextControl = () => {
    const brdHasError = Boolean(brdListError || brdSectionsError);
    const brdIsLinked = hasSelectedBrdSectionContext || hasAttachedBrdContext;
    const brdCanClear = Boolean(selectedBrd || hasSelectedBrdSectionContext || hasAttachedBrdContext);
    const brdIsEmpty = !brdHasError && !brdCanClear;
    const brdStatus = brdListError
      || brdSectionsError
      || (hasSelectedBrdSectionContext
        ? `${selectedBrd?.pdf_file || selectedBrd?.pdf_id} / ${brdSectionLabel(selectedBrdSection)}`
        : selectedBrd
          ? `BRD loaded: ${selectedBrd.pdf_file || selectedBrd.pdf_id} · choose one section to use as context.`
          : brdContextFile
            ? `Attached: ${brdContextFile.name}`
            : 'Manual BRD context added');
    const toneClass = brdHasError
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : brdIsLinked
        ? 'border-teal-200 bg-teal-50/70 text-teal-800'
        : 'border-amber-200 bg-amber-50 text-amber-800';
    const StatusIcon = brdHasError ? AlertCircle : brdIsLinked ? CheckCircle2 : FileText;

    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {brdIsEmpty ? (
          <p className="text-sm text-slate-500">No BRD section linked.</p>
        ) : (
          <div className={`flex min-w-0 items-start gap-2 rounded-lg border px-3 py-2 text-xs font-medium leading-5 ${toneClass}`}>
            <StatusIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <span className="min-w-0 break-words">{brdStatus}</span>
          </div>
        )}
        <div className="flex flex-shrink-0 items-center gap-1.5">
          {brdCanClear && (
            <button
              type="button"
              onClick={clearBrdContext}
              disabled={!canManageCaptureBrd}
              className="inline-flex h-9 items-center rounded-xl px-3 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={handleOpenBrdPicker}
            disabled={!canManageCaptureBrd}
            className="secondary-btn h-9 px-3 py-0 text-sm"
          >
            <Search className="h-4 w-4" />
            {hasSelectedBrdSectionContext || hasAttachedBrdContext ? 'Change' : 'Choose'}
          </button>
        </div>
      </div>
    );
  };

  const renderSetupRow = ({ index, label, hint, optional = false, complete = false, htmlFor, children }) => {
    const LabelTag = htmlFor ? 'label' : 'p';
    return (
      <div className="grid gap-3 px-5 py-5 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8">
        <div className="flex items-start gap-3">
          <span
            className={`mt-px inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-colors ${
              complete ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500'
            }`}
            aria-hidden="true"
          >
            {index}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <LabelTag htmlFor={htmlFor} className="text-sm font-semibold text-slate-900">{label}</LabelTag>
              {optional && (
                <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Optional
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs leading-5 text-slate-500">{hint}</p>
          </div>
        </div>
        <div className="min-w-0">{children}</div>
      </div>
    );
  };

  const renderCaptureStage = () => (
    <div className="space-y-5">
      {!isApplicationRouteMode && <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white">
              <Briefcase className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Application
              </p>
              <h3 className="truncate text-base font-semibold text-slate-900">
                {selectedWorkspace?.name || 'Select an application'}
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                {selectedWorkspace
                  ? `${formatWorkspaceRole(selectedWorkspace)} access`
                  : 'Required before starting capture'}
              </p>
            </div>
          </div>

          {!isApplicationRouteMode && <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              className="text-input min-w-[240px] bg-white"
              value={selectedWorkspaceId}
              onChange={(event) => handleWorkspaceSelectionChange(event.target.value)}
              disabled={workspaceLoading || brdIndexing || Boolean(session?.id)}
              aria-label="Select application"
              title={
                session?.id
                  ? 'Application is fixed after a capture session is created.'
                  : brdIndexing
                    ? 'Wait for BRD indexing to finish before changing application.'
                    : undefined
              }
            >
              <option value="">Choose an application...</option>
              {workspaces.map((workspace) => (
                <option key={workspace.id} value={workspace.id}>
                  {workspace.name} ({formatWorkspaceRole(workspace)})
                </option>
              ))}
            </select>
            {canUseWorkspaceTools && (
              <button
                type="button"
                className="secondary-btn px-3 py-2 text-sm"
                onClick={() => {
                  if (opensMembersDirectly) {
                    openWorkspaceMembersManager();
                    return;
                  }
                  setShowWorkspaceTools((previous) => !previous);
                }}
                aria-expanded={opensMembersDirectly ? undefined : showWorkspaceTools}
              >
                <Settings2 className="h-4 w-4" />
                {opensMembersDirectly ? 'Members' : 'Manage'}
                {!opensMembersDirectly && (
                  showWorkspaceTools ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )
                )}
              </button>
            )}
          </div>}
        </div>
        {workspaceError && (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {workspaceError}
          </div>
        )}
        {!isApplicationRouteMode && showWorkspaceTools && canUseWorkspaceTools && !opensMembersDirectly && (
          <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 md:grid-cols-[1fr_auto]">
            {canCreateWorkspace && (
              <div className="min-w-0">
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Create Application
                </label>
                <input
                  className="text-input"
                  placeholder="New application name"
                  value={newWorkspaceName}
                  onChange={(event) => setNewWorkspaceName(event.target.value)}
                />
              </div>
            )}
            <div className="flex items-end gap-2">
              {canCreateWorkspace && (
                <button
                  type="button"
                  className="secondary-btn px-3 py-2 text-sm"
                  onClick={handleCreateWorkspace}
                  disabled={creatingWorkspace || !String(newWorkspaceName || '').trim()}
                >
                  <Plus className="h-4 w-4" />
                  {creatingWorkspace ? 'Creating...' : 'Create'}
                </button>
              )}
              {canViewWorkspaceMembers && selectedWorkspaceId && (
                <button
                  type="button"
                  className="secondary-btn px-3 py-2 text-sm"
                  onClick={openWorkspaceMembersManager}
                >
                  <Settings2 className="h-4 w-4" />
                  Members
                </button>
              )}
            </div>
          </div>
        )}
      </div>}

      {!isApplicationRouteMode && <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
              <BadgeCheck className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Feature area selected
              </p>
              <h3 className="truncate text-base font-semibold text-slate-900">
                {selectedApplication?.name || session?.feature_area?.name || 'Choose a feature area'}
              </h3>
              <p className="mt-1 truncate text-xs text-slate-500">
                {selectedApplication?.base_url || session?.feature_area?.base_url || applicationUrl || 'Open from the feature area dashboard before capture'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
            <span className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1">
              {selectedWorkspaceName || session?.application?.name || session?.workspace?.name || 'Application not set'}
            </span>
            {(() => {
              const environmentLabel = applicationEnvironmentLabel(selectedApplication)
                || applicationEnvironmentLabel(session?.feature_area);
              return environmentLabel ? (
                <span className="rounded-md border border-teal-200 bg-teal-50 px-2.5 py-1 text-teal-700">
                  {environmentLabel}
                </span>
              ) : null;
            })()}
          </div>
        </div>

        {applicationsError && (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {applicationsError}
          </div>
        )}

        {!selectedApplicationId && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Select an application from the dashboard to start a feature capture.
          </div>
        )}

        {selectedApplicationId && renderApplicationEnvironmentRow()}
      </div>}

      {captureIsComplete ? (
        <>
          <section className="relative overflow-hidden rounded-2xl border border-teal-200/80 bg-gradient-to-br from-teal-50 via-white to-white shadow-sm">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-teal-200/30 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-col gap-5 p-5 sm:p-6 xl:flex-row xl:items-start xl:justify-between">
              <div className="flex min-w-0 flex-1 items-start gap-4">
                <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-[0_10px_24px_rgba(13,148,136,0.28)]">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-white/80 px-2.5 py-1 font-semibold text-teal-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-teal-500" aria-hidden="true" />
                      Capture complete
                    </span>
                    {capturedAtLabel && (
                      <span className="inline-flex items-center gap-1.5 text-slate-500">
                        <Clock className="h-3.5 w-3.5" />
                        Updated {capturedAtLabel}
                      </span>
                    )}
                  </div>

                  {editingRecaptureTarget ? (
                    <div className="mt-3 max-w-xl">
                      <label htmlFor="capture-feature-name" className="text-xs font-semibold text-slate-600">Feature name</label>
                      <input
                        id="capture-feature-name"
                        value={recaptureFeatureName}
                        onChange={(event) => {
                          setRecaptureFeatureName(event.target.value);
                          if (featureNameError) setFeatureNameError('');
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !recaptureTargetSaving) handleSaveRecaptureTarget();
                          if (event.key === 'Escape') cancelEditingRecaptureTarget();
                        }}
                        className={`text-input mt-1.5 text-base font-semibold ${featureNameError ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                        autoFocus={recaptureEditFocus === 'feature'}
                        aria-invalid={Boolean(featureNameError)}
                      />
                      {featureNameError && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-rose-700">
                          <AlertCircle className="h-3.5 w-3.5" />
                          {featureNameError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 flex min-w-0 items-center gap-1.5">
                      <h3 className="truncate text-2xl font-bold tracking-tight text-slate-900" title={capturedFeatureName}>
                        {capturedFeatureName}
                      </h3>
                      {canManageCaptureBrd && (
                        <button
                          type="button"
                          onClick={() => beginEditingRecaptureTarget('feature')}
                          className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white hover:text-teal-700 hover:shadow-sm"
                          aria-label="Edit feature name"
                          title="Edit feature name"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}

                  <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-2">
                    {captureStartUrl && /^https?:\/\//i.test(captureStartUrl) ? (
                      <a
                        href={captureStartUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-sm text-slate-600 transition-colors hover:text-teal-700"
                        title={captureStartUrl}
                      >
                        <Globe className="h-4 w-4 flex-shrink-0 text-slate-400" />
                        <span className="truncate">{captureStartUrl}</span>
                      </a>
                    ) : (
                      <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-sm text-slate-500">
                        <Globe className="h-4 w-4 flex-shrink-0 text-slate-400" />
                        <span className="truncate">{captureStartUrl || 'Configure an Application URL'}</span>
                      </span>
                    )}
                    {renderSignInModeBadge()}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 xl:flex-shrink-0 xl:justify-end">
                {editingRecaptureTarget ? (
                  <>
                    <button
                      type="button"
                      onClick={cancelEditingRecaptureTarget}
                      disabled={recaptureTargetSaving}
                      className="secondary-btn h-10 px-4 py-0 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveTargetAndRecapture}
                      disabled={recaptureTargetSaving}
                      className="secondary-btn h-10 shrink-0 px-4 py-0 text-sm"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Save &amp; recapture
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveRecaptureTarget}
                      disabled={recaptureTargetSaving}
                      className="primary-btn h-10 shrink-0 px-5 py-0 text-sm"
                    >
                      {recaptureTargetSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {recaptureTargetSaving ? 'Saving...' : 'Save changes'}
                    </button>
                  </>
                ) : (
                  <>
                    {canManageCaptureBrd && (
                      <button
                        type="button"
                        onClick={() => handleRecaptureFeatureCapture({ ...session, start_url: activeSessionTargetUrl })}
                        disabled={isBusy || Boolean(recapturingFeatureCaptureId) || !activeSessionTargetUrl}
                        className="secondary-btn h-10 px-4 py-0 text-sm"
                        title={!activeSessionTargetUrl ? 'Add a target URL before recapturing.' : undefined}
                      >
                        {activeSessionIsRecapturing
                          ? <LoaderCircle className="h-4 w-4 animate-spin" />
                          : <MonitorPlay className="h-4 w-4" />}
                        {activeSessionIsRecapturing ? 'Opening target…' : 'Re-record flow'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => goToFeatureStage('captured-flow')}
                      className="primary-btn group h-10 shrink-0 px-5 py-0 text-sm"
                    >
                      Review captured actions
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {steps.length > 0 && (
              <dl className="relative grid grid-cols-3 divide-x divide-teal-100 border-t border-teal-100 bg-white/70">
                {[
                  ['Actions', actionableCaptureStepCount, ListChecks],
                  ['Pages', capturedFlowPageGroups.length, Layers],
                  ['Runtime data', journeyVariables.length, Database],
                ].map(([label, value, Icon]) => (
                  <div key={label} className="flex min-w-0 items-center gap-3 px-4 py-3.5 sm:px-6">
                    <span className="hidden h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 sm:inline-flex">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <dt className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500" title={label}>{label}</dt>
                      <dd className="text-xl font-bold leading-tight text-slate-900">{value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            )}
          </section>

          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-slate-100 px-5 py-3.5">
              <h3 className="text-sm font-bold text-slate-900">Recording setup</h3>
              <p className="text-xs text-slate-500">Applied again when you recapture this feature.</p>
            </div>
            <dl className="divide-y divide-slate-100">
              <div className="grid gap-2 px-5 py-4 md:grid-cols-[180px_minmax(0,1fr)] md:gap-6">
                <dt className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <SignInModeIcon className="h-4 w-4 text-slate-400" />
                  Sign-in
                </dt>
                <dd className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{signInMode.label}</p>
                  <p className="mt-0.5 text-xs leading-5 text-slate-500">{signInMode.copy}</p>
                </dd>
              </div>
              <div className="grid gap-2 px-5 py-4 md:grid-cols-[180px_minmax(0,1fr)] md:gap-6">
                <dt className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <FileText className="h-4 w-4 text-slate-400" />
                  BRD context
                </dt>
                <dd className="min-w-0">{renderBrdContextControl()}</dd>
              </div>
            </dl>
          </section>
        </>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
            <span className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-100">
              <MonitorPlay className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-900">Set up recording</h3>
              <p className="mt-0.5 text-sm text-slate-500">
                Name the feature, confirm where it starts, and open the target. Flow rules can be added after steps are captured.
              </p>
            </div>
          </div>

          <div className="divide-y divide-slate-100">
            {renderSetupRow({
              index: 1,
              label: 'Feature name',
              hint: 'What this journey covers.',
              complete: Boolean(featureName.trim()),
              htmlFor: !preparingRecapture || editingRecaptureTarget ? 'capture-feature-name' : undefined,
              children: (
                preparingRecapture ? (
                  editingRecaptureTarget ? (
                    <div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          id="capture-feature-name"
                          value={recaptureFeatureName}
                          onChange={(event) => {
                            setRecaptureFeatureName(event.target.value);
                            if (featureNameError) setFeatureNameError('');
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !recaptureTargetSaving) handleSaveRecaptureTarget();
                            if (event.key === 'Escape') cancelEditingRecaptureTarget();
                          }}
                          className={`text-input min-w-0 flex-1 ${featureNameError ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                          autoComplete="off"
                          aria-invalid={Boolean(featureNameError)}
                          autoFocus
                        />
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <button
                            type="button"
                            onClick={cancelEditingRecaptureTarget}
                            disabled={recaptureTargetSaving}
                            className="secondary-btn h-10 px-3 py-0 text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveRecaptureTarget}
                            disabled={recaptureTargetSaving}
                            className="primary-btn h-10 px-4 py-0 text-sm"
                          >
                            {recaptureTargetSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {recaptureTargetSaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                      {featureNameError && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-rose-700" role="alert">
                          <AlertCircle className="h-3.5 w-3.5" />
                          {featureNameError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5">
                      <p className="min-w-0 truncate text-sm font-semibold text-slate-900" title={featureName}>
                        {featureName || 'Untitled feature'}
                      </p>
                      <button
                        type="button"
                        onClick={() => beginEditingRecaptureTarget('feature')}
                        className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-500/20"
                        aria-label="Edit feature name"
                        title="Edit feature name"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  )
                ) : (
                  <>
                    <input
                      id="capture-feature-name"
                      value={featureName}
                      onChange={(event) => {
                        setFeatureName(event.target.value);
                        if (featureNameError) setFeatureNameError('');
                      }}
                      className={`text-input ${featureNameError ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                      placeholder="Lead Capture"
                      autoComplete="off"
                      aria-invalid={Boolean(featureNameError)}
                    />
                    {featureNameError && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-rose-700">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {featureNameError}
                      </p>
                    )}
                  </>
                )
              ),
            })}

            {renderSetupRow({
              index: 2,
              label: 'Start page',
              hint: 'Recording opens here.',
              complete: Boolean(captureStartUrl),
              children: (
                <div className="space-y-3">
                  <div className={`flex items-start gap-3 rounded-xl border p-3.5 ${applicationAuthAvailable ? 'border-cyan-200 bg-cyan-50/60' : 'border-slate-200 bg-slate-50/70'}`}>
                    <span className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${applicationAuthAvailable ? 'bg-white text-cyan-700 shadow-sm' : 'bg-white text-slate-500 shadow-sm'}`}>
                      <Globe className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <p className="min-w-0 truncate text-sm font-semibold text-slate-900" title={captureStartUrl || 'Not configured'}>
                          {captureStartUrl || 'Configure an Application URL'}
                        </p>
                        {renderSignInModeBadge()}
                      </div>
                      <p className="mt-1 text-xs leading-5 text-slate-600">{signInMode.copy}</p>
                    </div>
                  </div>
                </div>
              ),
            })}

            {renderSetupRow({
              index: 3,
              label: 'Recording mode',
              hint: 'Choose how much of the journey this capture records.',
              complete: true,
              children: (
                <fieldset>
                  <legend className="sr-only">Recording mode</legend>
                  <div className="grid gap-2.5 md:grid-cols-3">
                    {CAPTURE_RECORDING_MODES.map((mode) => {
                      const isActive = activeRecordingMode === mode.id;
                      const ModeIcon = mode.icon;
                      return (
                        <label
                          key={mode.id}
                          className={`relative flex cursor-pointer flex-col rounded-xl border p-3.5 transition-all has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-teal-500/20 ${
                            isActive
                              ? 'border-teal-500 bg-teal-50/60 shadow-sm ring-1 ring-teal-500'
                              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/80'
                          }`}
                        >
                          <input
                            type="radio"
                            name="capture-recording-mode"
                            value={mode.id}
                            checked={isActive}
                            onChange={() => selectRecordingMode(mode.id)}
                            className="sr-only"
                          />
                          <span className="flex items-center justify-between gap-2">
                            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${isActive ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                              <ModeIcon className="h-4 w-4" />
                            </span>
                            <span
                              className={`inline-flex h-4 w-4 items-center justify-center rounded-full border-2 ${isActive ? 'border-teal-600' : 'border-slate-300'}`}
                              aria-hidden="true"
                            >
                              {isActive && <span className="h-1.5 w-1.5 rounded-full bg-teal-600" />}
                            </span>
                          </span>
                          <span className="mt-3 text-sm font-semibold text-slate-900">{mode.label}</span>
                          <span className="mt-1 text-xs leading-5 text-slate-600">{mode.copy}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ),
            })}

            {renderSetupRow({
              index: 4,
              label: 'BRD context',
              optional: true,
              hint: 'Ground generated cases in an extracted BRD section.',
              complete: Boolean(hasSelectedBrdSectionContext || hasAttachedBrdContext),
              children: renderBrdContextControl(),
            })}
          </div>

          <div className="flex flex-col gap-4 border-t border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-slate-600 shadow-sm ring-1 ring-inset ring-slate-200">
                {session?.id
                  ? <RefreshCw className="h-4 w-4 text-teal-700" />
                  : applicationAuthAvailable
                    ? <ShieldCheck className="h-4 w-4 text-teal-700" />
                    : <MousePointerClick className="h-4 w-4" />}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">
                  {session?.id ? 'Review setup before recapturing' : 'Opens in a new Incognito window'}
                </p>
                <p className="mt-0.5 text-xs leading-5 text-slate-600">
                  {session?.id
                    ? `Make any needed changes above. The target opens only after you choose “Open target to recapture.” ${captureWindowCopy}`
                    : captureWindowCopy}
                </p>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2 sm:justify-end">
              {session?.id ? (
                canManageCaptureBrd && (
                  <button
                    type="button"
                    onClick={handleStartRecaptureFeatureCapture}
                    disabled={isBusy || editingRecaptureTarget || Boolean(recapturingFeatureCaptureId) || !activeSessionTargetUrl}
                    className="primary-btn h-10 px-5 py-0 text-sm"
                    title={editingRecaptureTarget
                      ? 'Save or cancel the feature-name edit before starting recapture.'
                      : !activeSessionTargetUrl
                        ? 'This feature needs a target URL before recapture.'
                        : undefined}
                  >
                    {activeSessionIsRecapturing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    {activeSessionIsRecapturing ? 'Opening target…' : 'Open target to recapture'}
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={handleOpenCapture}
                  disabled={!canPrepareCapture || isBusy}
                  className="primary-btn h-10 shrink-0 px-5 py-0 text-sm"
                >
                  {busyAction === 'open' ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  {busyAction === 'open' ? 'Opening target…' : 'Open target to record'}
                </button>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );

  const capturedFlowPageGroups = useMemo(() => buildPageGroups(steps), [steps]);

  const activeStepEntry = useMemo(() => {
    if (!activeStepId) return null;
    const index = steps.findIndex((step, position) => stepSelectionId(step, position) === activeStepId);
    if (index < 0) return null;
    return { step: steps[index], index, id: activeStepId };
  }, [activeStepId, steps]);

  const activeStepPageLabel = useMemo(() => {
    if (!activeStepEntry) return '';
    const group = capturedFlowPageGroups.find(
      (row) => row.allEntries.some((entry) => entry.id === activeStepEntry.id)
    );
    return group ? group.label : '';
  }, [activeStepEntry, capturedFlowPageGroups]);

  const moveActiveStep = (offset) => {
    if (!activeStepEntry) return;
    const nextIndex = activeStepEntry.index + offset;
    if (nextIndex < 0 || nextIndex >= steps.length) return;
    setActiveStepId(stepSelectionId(steps[nextIndex], nextIndex));
  };

  // The drawer edits the same binding the row does, so it needs the values a step is
  // allowed to read: only the ones captured before it.
  const activeStepVariables = useMemo(() => {
    if (!activeStepEntry) return [];
    const orderByStepId = new Map(steps.map((step, index) => [stepSelectionId(step, index), index]));
    return journeyVariables.filter((variable) => {
      const producerIndex = orderByStepId.get(String(variable?.producer?.step_id || ''));
      return producerIndex === undefined || producerIndex < activeStepEntry.index;
    });
  }, [activeStepEntry, journeyVariables, steps]);

  const renderStepDetailDrawer = ({ canDelete }) => (
    <StepDetailDrawer
      entry={activeStepEntry}
      totalSteps={steps.length}
      pageLabel={activeStepPageLabel}
      sessionId={session?.id || ''}
      onClose={() => setActiveStepId('')}
      onPrevious={() => moveActiveStep(-1)}
      onNext={() => moveActiveStep(1)}
      hasPrevious={Boolean(activeStepEntry && activeStepEntry.index > 0)}
      hasNext={Boolean(activeStepEntry && activeStepEntry.index < steps.length - 1)}
      canEdit={Boolean(activeStepEntry && !isBusy && session?.composition_mode !== 'modules')}
      isSaving={busyAction === 'updateStep'}
      onSave={handleUpdateCapturedStep}
      canDelete={canDelete}
      isDeleting={busyAction === 'removeSteps'}
      onDelete={(stepId) => {
        setActiveStepId('');
        handleRemoveSelectedSteps([stepId]);
      }}
      binding={
        activeStepEntry
          ? stepValueBinding(activeStepEntry.step, journeyVariableNamesById(journeyVariables))
          : null
      }
      bindingEditable={Boolean(
        activeStepEntry
        && !isBusy
        && session?.composition_mode !== 'modules'
        && CONSUMABLE_STEP_ACTIONS.has(String(activeStepEntry.step.action || '').toLowerCase())
      )}
      isSavingBinding={busyAction === 'valueSource'}
      bindingVariables={activeStepVariables}
      onUseJourneyVariable={session?.composition_mode === 'modules' ? undefined : handleUseJourneyVariable}
    />
  );

  const renderCapturedFlowStage = () => {
    const flowUpdatedAt = session?.updated_at || session?.created_at || '';
    const hasCases = testCases.length > 0;
    const hasPom = hasObjectContent(session?.pom);
    const hasScripts = hasObjectContent(session?.scripts);

    const flowTabs = [
      { id: 'steps', label: 'Steps', count: capturedCount },
      { id: 'components', label: 'Components' },
      {
        id: 'data',
        label: 'Runtime Data',
        count: journeyVariables.length,
        tone: journeyVariableIssueCount > 0 ? 'danger' : undefined,
      },
      { id: 'context', label: 'Test Context' },
    ];
    const activeTabId = flowTabs.some((tab) => tab.id === capturedFlowTab) ? capturedFlowTab : 'steps';

    return (
      <div className="space-y-4">
        <CapturedFlowSummary
          featureName={session?.feature_name || featureName || 'Captured feature'}
          targetUrl={session?.start_url || applicationUrl || 'No target URL'}
          updatedLabel={flowUpdatedAt ? formatHistoryDate(flowUpdatedAt) : ''}
          applicationName={
            selectedWorkspaceName || session?.application?.name || session?.workspace?.name || ''
          }
          featureAreaName={selectedApplicationName || ''}
          actions={(
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              {hasCases && (
                <button
                  type="button"
                  onClick={() => goToFeatureStage('cases')}
                  className="secondary-btn px-3 py-2 text-sm"
                >
                  <FileText className="h-4 w-4" />
                  Cases
                </button>
              )}
              {hasPom && (
                <button
                  type="button"
                  onClick={() => goToFeatureStage('pom')}
                  className="secondary-btn px-3 py-2 text-sm"
                >
                  <Settings2 className="h-4 w-4" />
                  POM
                </button>
              )}
              {hasScripts && (
                <button
                  type="button"
                  onClick={() => goToFeatureStage('scripts')}
                  disabled={!featureLocatorReadiness.ready}
                  title={!featureLocatorReadiness.ready
                    ? 'Review or recapture unmatched manual steps before opening scripts.'
                    : undefined}
                  className="primary-btn px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Play className="h-4 w-4" />
                  Scripts
                </button>
              )}
            </div>
          )}
        />

        {actionableCaptureStepCount === 0 && (
          <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-amber-950">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
            <div className="min-w-0">
              <p className="text-sm font-bold">No user interactions were captured</p>
              <p className="mt-1 text-sm leading-6 text-amber-900">
                This flow only contains page navigation. Recapture it and perform at least one click, input, selection, or assertion before generating test cases.
              </p>
            </div>
          </div>
        )}

        <CapturedFlowTabs tabs={flowTabs} activeId={activeTabId} onChange={setCapturedFlowTab} />

        <div
          role="tabpanel"
          id={`captured-flow-panel-${activeTabId}`}
          aria-labelledby={`captured-flow-tab-${activeTabId}`}
        >
          {activeTabId === 'steps' && (
            <div className="space-y-4">
              {session?.composition_mode === 'modules' && (
                <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                  This flow is assembled from versioned components. Replace or upgrade a component in the Components tab instead of editing its materialized steps directly.
                </div>
              )}
              <CapturedStepsPanel
                steps={steps}
                capturedCount={capturedCount}
                selectedStepIds={selectedStepIds}
                onToggleStep={handleToggleStepSelection}
                onToggleAllSteps={handleToggleAllStepSelection}
                onRemoveSelectedSteps={handleRemoveSelectedSteps}
                isRemovingSelectedSteps={busyAction === 'removeSteps'}
                canRemoveSteps={!isBusy && session?.composition_mode !== 'modules'}
                journeyVariables={journeyVariables}
                onUseJourneyVariable={isBusy || session?.composition_mode === 'modules' ? undefined : handleUseJourneyVariable}
                onApplySuggestedMappings={session?.composition_mode === 'modules' ? undefined : handleApplySuggestedMappings}
                isSavingBinding={busyAction === 'valueSource'}
                captureTabs={captureTabs}
                activeStepId={activeStepId}
                onOpenStep={setActiveStepId}
              />
              <CaptureTabsPanel tabs={captureTabs} />
            </div>
          )}
          {activeTabId === 'data' && (
            <JourneyVariablesPanel
              variables={journeyVariables}
              validation={journeyVariablesValidation}
              steps={steps}
              captureTabs={captureTabs}
              canEdit={!isBusy && session?.composition_mode !== 'modules'}
              onRename={handleRenameJourneyVariable}
              onDelete={handleDeleteJourneyVariable}
              onChangeRowRule={handleChangeJourneyVariableRowRule}
            />
          )}
          {activeTabId === 'components' && (
            <FlowModuleComposer
              session={session}
              selectedStepIds={selectedStepIds}
              onFlowCreated={(payload) => {
                applyFeatureSessionToWorkspace(payload);
                goToFeatureStage('captured-flow', { sessionId: payload?.id });
                setStatusMessage('Composed flow created. Review it before generating test cases and scripts.');
              }}
            />
          )}
          {activeTabId === 'context' && renderFlowContextPanel()}
        </div>

        {renderStepDetailDrawer({ canDelete: !isBusy && session?.composition_mode !== 'modules' })}
      </div>
    );
  };

  const renderCasesStage = () => {
    const showEntryChoice = !testCases.length && !hasLiveCaseGeneration;
    const sourceId = featureAutomationSourceId(session);
    return (
      <div className="space-y-5">
        {showEntryChoice && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-slate-900">No test cases generated yet</h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Review the captured actions first, then generate one positive and one negative case.
                </p>
              </div>
              <button
                type="button"
                onClick={() => goToFeatureStage('captured-flow')}
                className="secondary-btn h-10 flex-shrink-0 px-4 py-0 text-sm"
              >
                <ArrowLeft className="h-4 w-4" />
                Review captured actions
              </button>
            </div>
          </section>
        )}
        {!showEntryChoice && (
          <>
            <EditTestCasesStep
              processingResult={activeFeatureProcessingResult}
              pdfId={sourceId}
              selectedSection={1}
              currentUser={currentUser}
              initialEditedTestCases={activeFeatureProcessingResult.test_cases}
              initialBaselineTestCases={featureInitialTestCases}
              status={busyAction === 'cases' ? 'processing' : (busyAction === 'saveCases' ? 'saving' : 'completed')}
              onSaveTestCases={handleSaveFeatureTestCases}
              sourceLabel="Captured feature"
              continueTargetLabel="save and export step"
              enableManualImport={false}
              allowEmptyManualImport={false}
              showSourceBreakdown={false}
              normalizationContext={{
                context_type: 'feature_capture',
                source_type: 'feature_capture',
                source_label: 'Captured feature',
                feature_session_id: session?.id || '',
                feature_area_id: session?.feature_area_id || activeApplication?.id || '',
                pdf_id: sourceId,
                section_number: 1,
                section_title: session?.feature_name || activeFeatureProcessingResult?.section_title || 'Captured Feature',
              }}
            />
          </>
        )}
      </div>
    );
  };

  const renderSaveStage = () => {
    const pom = hasObjectContent(session?.pom) ? session.pom : {};
    const pomReady = Boolean(Number(pom.page_count || 0) || (Array.isArray(pom.pages) && pom.pages.length));
    return (
      <SaveTestsStep
        testCases={activeFeatureProcessingResult.test_cases}
        baselineTestCases={featureInitialTestCases}
        pdfId={featureAutomationSourceId(session)}
        currentUserEmail={currentUser?.email || ''}
        uploadedPdfName={`${session?.feature_name || 'Captured Feature'}.feature.json`}
        apiResponse={featureSaveResponse}
        processingResult={activeFeatureProcessingResult}
        canProceedToPOM
        showApprovalWorkflow={false}
        sourceLabel="captured feature"
        pomActionLabel={pomReady ? 'Review POM' : 'Generate POM'}
        showBackButton={false}
        onBack={() => {
          goToFeatureStage('cases');
        }}
        onGeneratePOM={() => (pomReady ? goToFeatureStage('pom') : handleGeneratePom())}
        testingTypeMode="web-only"
      />
    );
  };

  const renderPomStage = () => (
    <div className="space-y-4">
      <LocatorReadinessSummary
        readiness={featureLocatorReadiness}
        onRecapture={canManageCaptureBrd && activeSessionTargetUrl
          ? () => handleRecaptureFeatureCapture(session)
          : null}
        recaptureDisabled={isBusy || Boolean(recapturingFeatureCaptureId)}
      />
      <GeneratePOMStep
        pdfId={featureAutomationSourceId(session)}
        automationMode="feature"
        featurePom={session?.pom || null}
        onFeatureGeneratePom={handleGeneratePom}
        featurePomLoading={busyAction === 'pom'}
        onPOMChange={handleFeaturePomChange}
        showBackButton={false}
        onBack={() => goToFeatureStage('save')}
        backLabel="Back to Save"
        onNext={() => goToFeatureStage('scripts')}
        continueDisabled={!featureLocatorReadiness.ready}
        continueDisabledReason="Review or recapture unmatched manual steps before generating scripts."
      />
    </div>
  );

  const renderScriptsStage = () => {
    if (!featureLocatorReadiness.ready) {
      return (
        <div className="space-y-4">
          <LocatorReadinessSummary
            readiness={featureLocatorReadiness}
            onRecapture={canManageCaptureBrd && activeSessionTargetUrl
              ? () => handleRecaptureFeatureCapture(session)
              : null}
            recaptureDisabled={isBusy || Boolean(recapturingFeatureCaptureId)}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => goToFeatureStage('pom')}
              className="secondary-btn px-3 py-2 text-sm"
            >
              Review POM
            </button>
          </div>
        </div>
      );
    }

    return (
      <GenerateScriptsStep
        editedTestCases={activeFeatureProcessingResult.test_cases}
        pdfId={featureAutomationSourceId(session)}
        automationAuthPdfId={effectiveFeatureAuthSourceId}
        processingResult={activeFeatureProcessingResult}
        automationMode="feature"
        theme="capture"
        featureSessionId={session?.id || ''}
        applicationId={session?.application_id || session?.application?.id || ''}
        featurePom={session?.pom || null}
        featureScripts={session?.scripts || null}
        featureScriptNotes={scriptNotes}
        onFeatureScriptNotesChange={setScriptNotes}
        showBackButton={false}
        onFeatureSessionChange={(payload, options = {}) => {
          setSession(payload);
          goToFeatureStage('scripts', { sessionId: payload?.id || session?.id });
          setStatusMessage(options?.message || 'Scripts updated from the captured feature flow.');
        }}
        onBack={() => goToFeatureStage('pom')}
        onComplete={async () => {
          await markFeatureExecutionPrepared();
        }}
        onRunResultsChange={setFeatureRunResults}
      />
    );
  };

  const renderExecuteStage = () => (
    <ExecutionSummaryStep
      editedTestCases={activeFeatureProcessingResult.test_cases}
      runResults={featureRunResults}
      pdfId={featureAutomationSourceId(session)}
      processingResult={activeFeatureProcessingResult}
      automationMode="feature"
      downloadActionLabel="Save Execution Results"
      showBackButton={false}
      onBack={() => goToFeatureStage('scripts')}
      onExport={exportFeatureExecutionResults}
    />
  );

  const renderStageContent = () => {
    if (currentStage.id === 'captured-flow') return renderCapturedFlowStage();
    if (currentStage.id === 'cases') return renderCasesStage();
    if (currentStage.id === 'save') return renderSaveStage();
    if (currentStage.id === 'pom') return renderPomStage();
    if (currentStage.id === 'scripts') return renderScriptsStage();
    if (currentStage.id === 'execute') return renderExecuteStage();
    return renderCaptureStage();
  };

  const isApplicationDashboardView = captureDashboardView !== 'workflow';
  const dashboardHasAttention = Boolean(
    isApplicationDashboardView
    && (dashboardApplicationsError || applicationFeatureError)
  );
  const dashboardAttentionMessage = String(
    dashboardApplicationsError || applicationFeatureError || ''
  ).trim();
  const dashboardIsLoading = Boolean(
    isApplicationDashboardView
    && (
      captureDashboardView === 'application'
        ? applicationFeatureLoading
        : dashboardApplicationsLoading
    )
  );
  const activeDashboardApplication = selectedDashboardApplication || selectedApplication;
  const stageKicker = isApplicationDashboardView ? 'Application Dashboard' : 'Current Stage';
  const stageTitle = isApplicationDashboardView
    ? captureDashboardView === 'application'
      ? activeDashboardApplication?.name || 'Application'
      : 'Feature Areas'
    : currentStage.title;
  const stageCopy = isApplicationDashboardView
    ? captureDashboardView === 'application'
      ? 'Review captured features for this feature area and continue the right automation output.'
      : 'Choose an application first, then drill into its feature areas.'
    : currentStage.copy;
  const stagePillLabel = isApplicationDashboardView
    ? dashboardIsLoading
      ? 'Loading'
      : captureDashboardView === 'application'
        ? formatCountLabel(applicationFeatureGroups.length, 'feature')
        : formatCountLabel(dashboardApplications.length, 'feature area')
    : currentStage.id === 'capture' && !captureIsComplete
      ? canPrepareCapture ? 'Ready to record' : 'Setup incomplete'
      : currentStage.id === 'cases' && String(streamProcessingResult?.stream_status || '').toLowerCase() === 'running'
        ? 'Generating test cases'
        : statusLabel(session?.status);
  const captureSetupIncomplete = Boolean(
    !isApplicationDashboardView
    && currentStage.id === 'capture'
    && !captureIsComplete
    && !canPrepareCapture
  );
  const canShowApplicationDashboardActions = Boolean(
    isApplicationDashboardView
    && captureDashboardView === 'application'
    && activeDashboardApplication?.id
  );
  const canShowApplicationDashboardBack = Boolean(
    isApplicationDashboardView
    && captureDashboardView === 'application'
  );
  const canShowWorkflowBack = Boolean(
    !isApplicationDashboardView
    && (
      isApplicationRouteMode
      || session?.id
      || selectedDashboardApplicationId
      || selectedApplicationId
    )
  );
  const canShowWorkflowActions = Boolean(!isApplicationDashboardView && session?.id);
  const canShowFeatureBack = canShowApplicationDashboardBack || canShowWorkflowBack;
  const canShowWorkflowRecapture = canShowWorkflowActions
    && currentStage.id === 'captured-flow';
  const activeSessionIsRecapturing = Boolean(
    session?.id
    && String(recapturingFeatureCaptureId || '') === String(session.id || '')
  );
  const activeSessionTargetUrl = String(captureStartUrl || session?.start_url || applicationUrl || '').trim();
  const sidebarMetricRows = isApplicationDashboardView
    ? captureDashboardView === 'application'
      ? [
        ['Feature Area', activeDashboardApplication?.name || 'Not selected'],
        ['Application', applicationWorkspaceName(activeDashboardApplication) || 'Not selected'],
        ['Features', applicationFeatureLoading ? 'Loading…' : applicationFeatureGroups.length],
        ['Flows', applicationFeatureLoading ? 'Loading…' : applicationFeatureItems.length],
        ['Current View', 'Features'],
      ]
      : [
        ['Feature Areas', dashboardApplicationsLoading ? 'Loading…' : dashboardApplications.length],
        ['Application', dashboardWorkspaceName],
        ['Current View', 'Feature Areas'],
      ]
    : isApplicationRouteMode
      ? [
        ['Feature', featureName || session?.feature_name || 'Not selected'],
        ['Application', selectedWorkspaceName || session?.application?.name || 'Not selected'],
      ]
      : [
        ['Feature', featureName || session?.feature_name || 'Not selected'],
        ['Application', selectedWorkspaceName || session?.application?.name || session?.workspace?.name || 'Not selected'],
        ['Feature Area', selectedApplicationName || session?.feature_area?.name || 'Not selected'],
      ];
  const activeWorkspaceChecklist = isApplicationDashboardView
    ? (
      captureDashboardView === 'application'
        ? CAPTURE_DASHBOARD_CHECKLISTS.application
        : CAPTURE_DASHBOARD_CHECKLISTS.applications
    )
    : showStageChecklist
      ? {
        title: currentStage.checklistTitle,
        items: currentStage.checklist,
      }
      : null;
  const routeApplicationId = parsedFeatureRoute.routeKind === 'application'
    ? String(parsedFeatureRoute.applicationId || '').trim()
    : '';
  const routeWorkflowSessionId = parsedFeatureRoute.routeKind === 'workflow'
    ? String(parsedFeatureRoute.sessionId || '').trim()
    : '';
  const routeWorkflowStageId = routeWorkflowSessionId
    ? normalizeFeatureCaptureStageId(parsedFeatureRoute.stageId, 'capture')
    : '';
  const activeFeatureRouteSignature = routeApplicationId
    ? `application:${routeApplicationId}`
    : routeWorkflowSessionId
      ? `workflow:${routeWorkflowSessionId}:${routeWorkflowStageId}`
      : '';
  const applicationRouteLoaded = Boolean(
    routeApplicationId
    && captureDashboardView === 'application'
    && String(selectedDashboardApplicationId || '').trim() === routeApplicationId
    && activeDashboardApplication
  );
  const workflowRouteLoaded = Boolean(
    routeWorkflowSessionId
    && captureDashboardView === 'workflow'
    && String(session?.id || '').trim() === routeWorkflowSessionId
    && currentStage.id === routeWorkflowStageId
  );
  const routeStillFetchingApplication = Boolean(
    routeApplicationId
    && !activeDashboardApplication
    && (dashboardApplicationsLoading || applicationFeatureLoading)
  );
  const activeFeatureRouteNeedsHydration = Boolean(
    routeApplicationId ? !applicationRouteLoaded : routeWorkflowSessionId && !workflowRouteLoaded
  );
  const shouldShowFeatureRouteSkeleton = Boolean(
    activeFeatureRouteSignature
    && !location.search
    && activeFeatureRouteNeedsHydration
    && (
      featureRouteHydration.loading
      || featureRouteHydration.signature !== activeFeatureRouteSignature
      || routeStillFetchingApplication
    )
  );

  if (shouldShowFeatureRouteSkeleton) {
    return <WorkspaceRouteSkeleton theme={CAPTURE_WORKSPACE_VARIANT.theme} />;
  }

  const handleFeatureWorkspaceBack = () => {
    if (isBusy) return;

    if (preparingRecapture && captureDashboardView === 'workflow' && currentStage.id === 'capture') {
      setPreparingRecapture(false);
      goToFeatureStage('captured-flow', { sessionId: session?.id, replace: true });
      return;
    }

    if (isApplicationRouteMode) {
      if (captureDashboardView === 'workflow') {
        const previousStageId = FEATURE_CAPTURE_BACK_STAGE_BY_CURRENT[currentStage.id];
        if (previousStageId) {
          goToFeatureStage(previousStageId);
          return;
        }
        returnToActiveApplicationDashboard();
        return;
      }
      navigate(buildFeatureCaptureRoute({ rootApplicationId: lockedApplicationId }));
      return;
    }

    // Workflow Back is stage-derived so it behaves the same after refresh and when
    // users arrived through a deep link. Capture/recapture is an action, not a
    // previous stage, so Captured Flow returns to the feature dashboard.
    if (captureDashboardView === 'workflow') {
      const previousStageId = FEATURE_CAPTURE_BACK_STAGE_BY_CURRENT[currentStage.id];
      if (previousStageId) {
        goToFeatureStage(previousStageId);
        return;
      }
      returnToActiveApplicationDashboard();
      return;
    }

    const previousRoute = consumeWorkspaceBackRoute(
      `${location.pathname}${location.search || ''}${location.hash || ''}`,
      {
        allowedRoutePrefixes: [FEATURE_CAPTURE_ROUTE_ROOT],
        ignoreStoredStack: wasCurrentDocumentReloaded(),
      }
    );
    if (previousRoute) {
      if (previousRoute === FEATURE_CAPTURE_ROUTE_ROOT) {
        returnToApplicationsDashboard();
        return;
      }
      navigate(previousRoute);
      return;
    }

    if (isApplicationDashboardView && captureDashboardView === 'application') {
      returnToApplicationsDashboard();
      return;
    }

  };

  return (
    <>
      <WorkspacePage
        theme={CAPTURE_WORKSPACE_VARIANT.theme}
        sidebar={(
          <WorkspaceSidebar
            theme={CAPTURE_WORKSPACE_VARIANT.theme}
            kicker={CAPTURE_WORKSPACE_VARIANT.sidebarKicker}
            title={CAPTURE_WORKSPACE_VARIANT.sidebarTitle}
            description={CAPTURE_WORKSPACE_VARIANT.sidebarDescription}
            historyLoading={featureHistoryLoading}
            historyDisabled={isBusy}
            onHistory={loadFeatureCaptureHistory}
            action={isApplicationRouteMode ? {
              label: 'Dashboard',
              icon: LayoutDashboard,
              onClick: () => navigate(
                buildFeatureCaptureRoute({ rootApplicationId: lockedApplicationId })
              ),
            } : null}
            metrics={sidebarMetricRows.map(([label, value]) => ({
              label,
              value: typeof value === 'number' ? value : compactValue(value),
              title: String(value ?? ''),
            }))}
          />
        )}
      >
        <WorkspaceStageCard
          kicker={stageKicker}
          title={stageTitle}
          description={stageCopy}
          actions={(
            <>
              {canShowFeatureBack && (
                <WorkspaceBackButton
                  onClick={handleFeatureWorkspaceBack}
                  disabled={isBusy}
                  title="Go back"
                />
              )}
              {canShowApplicationDashboardActions && (
                <button
                  type="button"
                  onClick={() => startFeatureCaptureForApplication(activeDashboardApplication)}
                  className="primary-btn workspace-accent-button h-9 px-3 py-0 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  New Feature Capture
                </button>
              )}
              {canShowWorkflowActions && (
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  {currentStage.id === 'cases' && activeCaseJobId && busyAction === 'cases' && (
                    <button
                      type="button"
                      onClick={handleCancelCaseGeneration}
                      className="workspace-accent-secondary inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm font-semibold shadow-sm transition-colors"
                    >
                      Stop generation
                    </button>
                  )}
                  {canShowWorkflowRecapture && canManageCaptureBrd && (
                    <button
                      type="button"
                      onClick={() => handleRecaptureFeatureCapture({
                        ...session,
                        start_url: activeSessionTargetUrl,
                      })}
                      disabled={isBusy || Boolean(recapturingFeatureCaptureId) || !activeSessionTargetUrl}
                      className="workspace-accent-secondary inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm font-semibold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      title={!activeSessionTargetUrl
                        ? 'This feature needs a target URL before the browser flow can be recorded again.'
                        : 'Replace the captured browser journey and all outputs generated from it'}
                    >
                      {activeSessionIsRecapturing ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <MonitorPlay className="h-4 w-4" />
                      )}
                      {activeSessionIsRecapturing ? 'Opening target…' : 'Re-record flow'}
                    </button>
                  )}
                  {currentStage.id === 'captured-flow' && testCases.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setRegenerateCasesConfirmation(true)}
                      disabled={actionableCaptureStepCount === 0 || isBusy}
                      className="workspace-accent-secondary inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm font-semibold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                      title="Replace the existing test cases with a newly generated set"
                    >
                      <Sparkles className="h-4 w-4" />
                      Regenerate cases
                    </button>
                  )}
                  {currentStage.id === 'captured-flow' && (
                    testCases.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => goToFeatureStage('cases', { sessionId: session?.id })}
                        disabled={isBusy}
                        className="primary-btn h-9 px-3 py-0 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FileText className="h-4 w-4" />
                        View {formatCountLabel(testCases.length, 'test case')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleQuickFlowCheck({ openCases: true })}
                        disabled={actionableCaptureStepCount === 0 || isBusy}
                        title={actionableCaptureStepCount === 0
                          ? 'Recapture the flow and perform at least one user action before generating cases.'
                          : 'Generate one positive and one negative test case'}
                        className="primary-btn h-9 px-3 py-0 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {busyAction === 'cases' ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                        {busyAction === 'cases' ? 'Starting generation…' : 'Generate 2 test cases'}
                      </button>
                    )
                  )}
                </div>
              )}
            </>
          )}
          status={currentStage.id === 'capture' && captureIsComplete ? null : {
            icon: errorMessage || dashboardHasAttention
              ? AlertCircle
              : dashboardIsLoading
                ? LoaderCircle
                : captureSetupIncomplete
                  ? Circle
                  : BadgeCheck,
            label: errorMessage || dashboardHasAttention ? 'Needs attention' : stagePillLabel,
            tone: errorMessage || dashboardHasAttention ? 'danger' : undefined,
            loading: dashboardIsLoading,
            iconOnly: Boolean(errorMessage || dashboardHasAttention),
            ariaLabel: errorMessage || dashboardHasAttention
              ? 'There is an issue. View details.'
              : stagePillLabel,
            details: errorMessage || dashboardHasAttention
              ? {
                  title: dashboardHasAttention
                    ? 'Application information could not be loaded'
                    : 'The requested action could not be completed',
                  description: dashboardHasAttention
                    ? (dashboardAttentionMessage || 'Application information is unavailable. Refresh the page to try loading it again.')
                    : (String(errorMessage || '').trim() || 'Try the action again. If it still fails, contact your administrator and describe what you were trying to do.'),
                }
              : null,
          }}
          checklist={activeWorkspaceChecklist && (
            <WorkspaceChecklist
              title={activeWorkspaceChecklist.title}
              items={activeWorkspaceChecklist.items}
            />
          )}
          contentClassName="space-y-5"
        >
          {isApplicationDashboardView ? renderApplicationDashboardContent() : renderStageContent()}
        </WorkspaceStageCard>
      </WorkspacePage>

      {!isApplicationRouteMode && renderFeatureHistoryModal()}
      {renderBrdPickerModal()}
      {recaptureConfirmation && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[6px]"
          role="presentation"
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !recaptureTargetSaving) {
              setRecaptureConfirmation(null);
            }
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !recaptureTargetSaving) {
              setRecaptureConfirmation(null);
            }
          }}
        >
          <div
            className="relative w-full max-w-xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.32)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recapture-dialog-title"
          >
            <div className="h-1 bg-teal-500" />
            <button
              type="button"
              onClick={() => setRecaptureConfirmation(null)}
              disabled={recaptureTargetSaving}
              className="absolute right-7 top-8 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
              aria-label="Close recapture confirmation"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="px-8 pb-5 pt-7">
              <div className="flex items-center gap-4 pr-12">
                <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 text-teal-700">
                  <RefreshCw className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-teal-700">
                    {recaptureConfirmation.intent === 'prepare' ? 'Before you recapture' : 'Confirm recapture'}
                  </p>
                  <h3 id="recapture-dialog-title" className="mt-1 text-[22px] font-bold tracking-tight text-slate-900">
                    {recaptureConfirmation.intent === 'prepare'
                      ? `Review recapture setup for ${recaptureConfirmation.item?.feature_name || 'this feature'}?`
                      : `Start recapturing ${recaptureConfirmation.item?.feature_name || 'this feature'}?`}
                  </h3>
                </div>
              </div>
              <p className="mt-5 text-sm leading-6 text-slate-600">
                {recaptureConfirmation.intent === 'prepare'
                  ? 'Recapturing will replace this feature’s existing recording and its generated outputs. Continue to the setup screen to review or change the recording options first. Nothing is removed until you explicitly start recapture.'
                  : recaptureConfirmation.saveEdits
                    ? 'Your updated feature name will be saved first. Starting recapture then replaces the existing recorded journey.'
                    : 'The setup shown on the previous screen will be used. Starting recapture replaces the existing recorded journey.'}
              </p>
            </div>
            <div className="mx-8 grid gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3.5 sm:grid-cols-[0.8fr_1.2fr]">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Feature</p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-900">
                  {recaptureConfirmation.item?.feature_name || 'Untitled feature'}
                </p>
              </div>
              <div className="min-w-0 sm:border-l sm:border-slate-200 sm:pl-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Target URL</p>
                <p className="mt-1 truncate text-sm font-medium text-slate-700" title={recaptureConfirmation.item?.start_url || ''}>
                  {recaptureConfirmation.item?.start_url || 'No target URL'}
                </p>
              </div>
            </div>
            <div className="mx-8 mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3.5 text-sm text-amber-950">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
              <div>
                <p className="font-semibold">
                  {recaptureConfirmation.intent === 'prepare'
                    ? 'Starting recapture will replace existing work'
                    : 'This replaces the current recording'}
                </p>
                <p className="mt-0.5 leading-5 text-amber-900">
                  {recaptureConfirmation.intent === 'prepare'
                    ? 'Captured steps, screenshots, runtime data, analysis, test cases, page objects, scripts, and execution results for this capture will be cleared when recapture starts.'
                    : 'Captured steps and generated outputs for this feature will be cleared.'}
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/80 px-8 py-5 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => setRecaptureConfirmation(null)}
                disabled={recaptureTargetSaving}
                className="secondary-btn h-10 min-w-[104px] justify-center rounded-lg px-4 py-0 text-sm"
                autoFocus
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRecaptureFeatureCapture}
                disabled={recaptureTargetSaving || isBusy}
                className="inline-flex h-10 min-w-[132px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(37,99,235,0.24)] transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {recaptureTargetSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {recaptureTargetSaving
                  ? 'Saving...'
                  : recaptureConfirmation.intent === 'prepare'
                    ? 'Continue to setup'
                    : recaptureConfirmation.saveEdits ? 'Save & start recapture' : 'Start recapture'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {regenerateCasesConfirmation && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[6px]"
          role="presentation"
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !isBusy) setRegenerateCasesConfirmation(false);
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isBusy) setRegenerateCasesConfirmation(false);
          }}
        >
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.32)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="regenerate-cases-dialog-title"
          >
            <div className="h-1 bg-amber-500" />
            <button
              type="button"
              onClick={() => setRegenerateCasesConfirmation(false)}
              disabled={isBusy}
              className="absolute right-7 top-8 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
              aria-label="Close regeneration warning"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="px-8 pb-5 pt-7">
              <div className="flex items-center gap-4 pr-12">
                <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700">
                  <RefreshCw className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-amber-700">Confirm regeneration</p>
                  <h3 id="regenerate-cases-dialog-title" className="mt-1 text-[22px] font-bold tracking-tight text-slate-900">
                    Replace {formatCountLabel(testCases.length, 'existing test case')}?
                  </h3>
                </div>
              </div>
              <p className="mt-5 text-sm leading-6 text-slate-600">
                A new positive and negative test case will be generated from the current captured flow. You can cancel and view the existing cases without changing them.
              </p>
            </div>
            <div className="mx-8 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3.5 text-sm text-amber-950">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
              <div>
                <p className="font-semibold">Existing automation outputs will be cleared</p>
                <p className="mt-0.5 leading-5 text-amber-900">
                  The current test cases, locator readiness, page objects, scripts, and execution results will be replaced or removed.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/80 px-8 py-5 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => setRegenerateCasesConfirmation(false)}
                disabled={isBusy}
                className="secondary-btn h-10 min-w-[104px] justify-center rounded-lg px-4 py-0 text-sm"
                autoFocus
              >
                Keep existing
              </button>
              <button
                type="button"
                onClick={() => {
                  setRegenerateCasesConfirmation(false);
                  handleQuickFlowCheck({ openCases: true });
                }}
                disabled={isBusy}
                className="inline-flex h-10 min-w-[148px] items-center justify-center gap-2 rounded-lg bg-amber-600 px-5 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(217,119,6,0.24)] transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className="h-4 w-4" />
                Regenerate cases
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {showApplicationEnvironmentModal && effectiveFeatureAuthSourceId && createPortal(
        <AutomationEnvironmentModal
          pdfId={effectiveFeatureAuthSourceId}
          defaultLoginUrl={captureStartUrl}
          onClose={() => setShowApplicationEnvironmentModal(false)}
          onAuthStateChange={(payload) => {
            setApplicationAuthState(payload);
          }}
        />,
        document.body
      )}

      {!isApplicationRouteMode && <WorkspaceMembersModal
        isOpen={showWorkspaceMembersModal}
        workspace={selectedWorkspace}
        members={workspaceMembers}
        loading={workspaceMembersLoading}
        error={workspaceMembersError}
        activeUsers={activeUsers}
        activeUsersLoading={activeUsersLoading}
        activeUsersError={activeUsersError}
        canManageMembers={canManageWorkspaceMembers}
        onClose={() => setShowWorkspaceMembersModal(false)}
        onRefresh={() => loadWorkspaceMembers()}
        onRefreshActiveUsers={() => loadActiveUsers()}
        onAddMember={addWorkspaceMember}
        onRemoveMember={removeWorkspaceMember}
      />}
    </>
  );
};
